import { useMemo, useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { View, Pressable, Alert, Platform, ActionSheetIOS, Modal, Share, Image, ScrollView, ActivityIndicator, InteractionManager, AppState, type AppStateStatus } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { Screen } from '@/components/layout/Screen';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { FloatingActionButton } from '@/components/ui/FloatingActionButton';
import { SelectField, type SelectOption } from '@/components/ui/SelectField';
import { InputField } from '@/components/ui/InputField';
import { SubHeading, Body, Caption, ThemedText } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { useToast } from '@/components/ui/ToastProvider';
import { TripErrorBoundary } from '@/utils/errorBoundary';
import { LoadingState } from '@/components/ui/LoadingState';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import { useUserStore, selectFirebaseUser, selectUserProfile } from '@/state/userStore';
import {
  subscribeToUserTrips,
  updateTrip,
  getTripById,
  notifyLiveTripUpdated,
  type Trip,
  DEFAULT_BUDGET_ALERT_THRESHOLDS,
  getLastKnownTrips,
} from '@/services/trips';
import { formatTripDates } from '@/data/trips';
import { canUserEditItem } from '@/services/collaboration';
import type { CollaboratorPermission } from '@/services/trips';
import {
  syncInboxBookings,
  checkInboxStatus,
  initiateInboxAuth,
  saveImapSettings,
  disconnectEmailProvider,
  type CheckInboxStatusOptions,
  type InboxProviderId,
} from '@/services/inboxSync';
import { useInboxConnectionsStore } from '@/state/inboxConnectionsStore';
import { subscribeToTripBookings, type Booking } from '@/services/bookings';
import { subscribeToTripExpenses, type Expense } from '@/services/expenses';
import { buildTripSelectOptions } from '@/utils/tripSelectOptions';
import { getDefaultTripId, normalizeTripRouteParam } from '@/utils/tripSelection';
import {
  consumeWidgetRouteParams,
  isWidgetRouteFocusParam,
  resolveRouteFocusedDateKey,
} from '@/navigation/planRouteParams';
import { completeOAuthWebSession } from '@/utils/completeOAuthWebSession';
import { CollaborationModal } from '@/components/trips/CollaborationModal';
import { PlanningTab, TripOverviewCard } from '@/components/wallet/PlanningTab';
import { ItineraryOfflineCard } from '@/components/planning/ItineraryOfflineCard';
import { WhereImStayingCard } from '@/components/trips/WhereImStayingCard';
import {
  requestItinerarySuggestions,
  applyItinerarySuggestions,
  requestRebookingSupport,
  requestDisruptionInsights,
  requestBudgetCopilot,
  type ChatPlannerResult,
  type RebookingCopilotResponse,
  type DisruptionCopilotResponse,
  type BudgetCopilotResponse,
} from '@/services/aiPlanner';
import { EnhancedSuggestionCard } from '@/components/suggestions/EnhancedSuggestionCard';
import { mapToEnhancedSuggestion } from '@/utils/suggestionHelpers';
import * as Linking from 'expo-linking';
import * as WebBrowser from 'expo-web-browser';
import * as Updates from 'expo-updates';
import {
  subscribeToTripItineraryItems,
  fetchTripItineraryItems,
  createItineraryItem,
  updateItineraryItem,
  deleteItineraryItem,
  type ItineraryItem,
} from '@/services/itinerary';
import { ItineraryItemForm } from '@/components/itinerary/ItineraryItemForm';
import { Calendar } from '@/components/ui/Calendar';
import { TimePickerInput } from '@/components/ui/TimePickerInput';
import { createBooking, updateBooking } from '@/services/bookings';
import { TripPlanningActionsBar } from '@/components/planning/TripPlanningActionsBar';
import type { CopilotPendingType } from '@/components/planning/CopilotResultModal';
import { MultiProviderSelector } from '@/components/planning/MultiProviderSelector';
import { VisaChecklist } from '@/components/planning/VisaChecklist';
import { getLastUsedInboxProvider, setLastUsedInboxProvider } from '@/utils/lastInboxProvider';
import { getTripDestinationLabel } from '@/utils/tripDestination';
import { isInternalDebugBuild, isUiDebugLoggingEnabled } from '@/services/logger';
import {
  buildPlannerFocusCacheKey,
  getCachedChatPlannerResult,
  setCachedChatPlannerResult,
} from '@/utils/aiSuggestionCache';
import * as Location from 'expo-location';
import { generateTripAlerts, hasCollaborators, type TripAlert } from '@/utils/tripAlerts';
import type { MaybeItem } from '@/services/maybeItems';
import { useSmartCueNotifications } from '@/hooks/useSmartCueNotifications';
import { NotificationBell } from '@/notifications/components/NotificationBell';
import { NotificationModal } from '@/notifications/components/NotificationModal';
import {
  clearNotificationsForTrip,
  hydrateTripAlertNotificationsWithPersistence,
  useNotificationsForTrip,
} from '@/notifications/service';
import { tripAlertsToNotifications } from '@/notifications/adapters';
import { useNotificationActionHandler } from '@/notifications/hooks';
import type { Notification } from '@/notifications/types';
import { useNotificationStore } from '@/notifications/store';
import { useTripSelectionStore } from '@/state/tripSelectionStore';
import { shallow } from 'zustand/vanilla/shallow';
import { getClearedAlerts } from '@/utils/clearedAlerts';
import {
  getCachedTripData,
  getCachedTrips,
  cacheTripPackForOffline,
  itineraryItemsFromOfflineCache,
  bookingsFromOfflineCache,
  expensesFromOfflineCache,
  mergeOfflineReceiptUrisIntoExpenses,
  getLastSyncTime,
} from '@/services/offlineTrips';
import { networkMonitor } from '@/services/networkMonitor';
import { getTravelRequirements } from '@/services/travelRequirements';
import type { VisaRequirement } from '@/services/visaChecker';
import { checkVisaRequirement, getVisaStatusMessage } from '@/utils/visaCheck';
import { COUNTRY_ISO2_OPTIONS, fromIso2CountryCode, toIso2CountryCode } from '@/utils/countryIso2';
import { coerceDate, toLocalDateKey } from '@/utils/dateKeys';
import { deriveTripDateRange } from '@/utils/tripDateRange';
import { clearInboxOauthMarkers, setInboxOauthPending } from '@/utils/inboxOauth';
import Constants from 'expo-constants';
import { useInboxOauthResume } from '@/hooks/useInboxOauthResume';
import { useInboxOauthPostMessage } from '@/hooks/useInboxOauthPostMessage';
import { useAiSuggestionsUiState } from '@/hooks/useAiSuggestionsUiState';
import { useActivePlanTripBinding } from '@/hooks/useActivePlanTripBinding';
import { useAppleTripSurfacePlanSync } from '@/hooks/useAppleTripSurfacePlanSync';
import { effectiveTopInset } from '@/utils/effectiveTopInset';

const PROVIDER_LABELS: Record<InboxProviderId, string> = {
  gmail: 'Gmail',
  outlook: 'Outlook',
  icloud: 'iCloud',
};

type CopilotResultState =
  | { type: 'rebooking'; data: RebookingCopilotResponse }
  | { type: 'disruption'; data: DisruptionCopilotResponse }
  | { type: 'budget'; data: BudgetCopilotResponse };
import { isTripCompleted } from '@/utils/tripStatus';
import { useTripBudgetSummaries } from '@/hooks/useTripBudgetSummaries';
import { ExportMenu } from '@/components/trips/ExportMenu';
import { syncTripMembership } from '@/services/tripMembership';
import { useOfflinePlanTierDisplay } from '@/hooks/useOfflinePlanTierDisplay';
import { usePremiumGateStore } from '@/state/premiumGateStore';
import {
  subscribeAiPlanIdeaCardsQuota,
  type AiPlanIdeaCardsQuotaView,
} from '@/services/usageCounters';
import { FREE_PLAN_LIMITS, PLAN_AI_IDEA_BATCH } from '@/types/entitlements';
import { traceModuleEvaluation } from '@/utils/moduleTrace';
import { getDocumentsTabTarget } from '@/navigation/tabTargets';

traceModuleEvaluation('app/(tabs)/plan');

/** Must match Maps — tolerate transient empty snapshots while Firestore reconnects after going online. */
const PLAN_ITINERARY_EMPTY_SNAPSHOT_GRACE_MS = 3500;

const DocumentViewer = lazy(() =>
  import('@/components/itinerary/DocumentViewer').then((module) => ({ default: module.DocumentViewer }))
);
const TripCommentsLazy = lazy(() =>
  import('@/components/trips/TripComments').then((module) => ({ default: module.TripComments }))
);
const ImapSettingsModalLazy = lazy(() =>
  import('@/components/planning/ImapSettingsModal').then((module) => ({ default: module.ImapSettingsModal }))
);
const CopilotResultModalLazy = lazy(() =>
  import('@/components/planning/CopilotResultModal').then((module) => ({ default: module.CopilotResultModal }))
);
const SmartUploadModalLazy = lazy(() =>
  import('@/components/smartUpload/SmartUploadModal').then((module) => ({ default: module.SmartUploadModal }))
);
const EmailSyncStatusLazy = lazy(() =>
  import('@/components/planning/EmailSyncStatus').then((module) => ({ default: module.EmailSyncStatus }))
);
const FlightSearchModalLazy = lazy(() =>
  import('@/components/bookings/FlightSearchModal').then((module) => ({ default: module.FlightSearchModal }))
);
const HotelSearchModalLazy = lazy(() =>
  import('@/components/bookings/HotelSearchModal').then((module) => ({ default: module.HotelSearchModal }))
);
const TravelCompanionLazy = lazy(() =>
  import('@/components/travel/TravelCompanion').then((module) => ({ default: module.TravelCompanion }))
);

function projectedPlanAiIdeaBatchSize(daysDiff: number): number {
  return Math.min(Math.max(PLAN_AI_IDEA_BATCH.min, daysDiff), PLAN_AI_IDEA_BATCH.max);
}

const buildNotificationSeedSignature = (items: Notification[]) =>
  items
    .map((item) => {
      const readAt = item.readAt != null ? String(item.readAt) : '';
      return `${item.id}:${item.title}:${item.body}:${item.kind}:${item.severity}:${readAt}`;
    })
    .sort()
    .join('|');

/** Pure helper so visa / modal logic does not need `selectedTrip` object identity in hook deps (Firestore churn). */
function computeTripNeedsDestinationCountryConfirm(trip: Trip | null | undefined): boolean {
  if (!trip) return false;
  const structured =
    (trip.destinations?.[0] as { countryCode?: unknown } | undefined)?.countryCode ||
    toIso2CountryCode(((trip.destinations?.[0] as { country?: unknown } | undefined)?.country as string) || '');
  if (structured) return false;
  const raw = (trip.destination || trip.destinations?.[0]?.city || '').toString();
  if (!raw) return true;
  const countryPart = raw.split(',').map((p) => p.trim()).pop() || raw;
  return !toIso2CountryCode(countryPart);
}

function PlanScreenInner() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const params = useLocalSearchParams();
  const paramTripId = normalizeTripRouteParam(params.tripId);
  const paramItemId = typeof params.itemId === 'string' ? params.itemId : undefined;
  const routeFocusedDateKey = useMemo(
    () => resolveRouteFocusedDateKey(params),
    [params.day, params.focus],
  );
  const router = useRouter();
  const routerRef = useRef(router);
  routerRef.current = router;
  const firebaseUser = useUserStore(selectFirebaseUser);
  const { confirm: confirmAction } = useConfirmDialog();
  const { showSuccess, showError, showInfo, showToastWithOptions } = useToast();
  const openedItemIdRef = useRef<string | null>(null);
  const handledWidgetRouteFocusRef = useRef<string | null>(null);
  /** Prevents route quick-add from re-firing on every trips snapshot (was max update depth with fresh Date refs). */
  const quickAddRouteHandledRef = useRef<string | null>(null);
  const planScrollViewRef = useRef<ScrollView>(null);
  const commentsSectionYRef = useRef(0);
  // Prefer Tripvio storage keys; fall back to legacy TripFlow keys for older installs.
  const tripPlanDefsKey = '@tripvio_trip_plan_defs_seen';
  const legacyTripPlanDefsKey = '@tripflow_trip_plan_defs_seen';
  const showStagingDebug = useMemo(() => isInternalDebugBuild(), []);

  // Visa debug helpers (must be hoisted above any callbacks that reference them to avoid TDZ crashes on web)
  const formatVisaProviderDebugHint = useCallback(
    (result: Awaited<ReturnType<typeof getTravelRequirements>> | null) => {
      if (!showStagingDebug || !result) return '';
      const failure = result.debug?.failureReason;
      if (!failure) return '';
      const status = result.debug?.providerHttpStatus;
      const providerMsg = result.debug?.providerMessage;
      const statusPart = typeof status === 'number' ? ` (${status})` : '';
      const msgPart = providerMsg ? ` - ${providerMsg}` : '';
      return `\n\n(Staging hint: ${failure}${statusPart}${msgPart})`;
    },
    [showStagingDebug],
  );

  const formatVisaErrorHint = useCallback(
    (error?: unknown) => {
      if (!showStagingDebug) return '';
      const code = (error as any)?.code;
      const message = (error as any)?.message;
      const detail = [code, message].filter(Boolean).join(' - ');
      return detail ? `\n\n(Staging hint: ${detail})` : '\n\n(Staging hint: no response)';
    },
    [showStagingDebug],
  );

  const [trips, setTrips] = useState<Trip[]>([]);
  const [tripsLoading, setTripsLoading] = useState(true);
  const rememberedTripId = useTripSelectionStore((state) => state.lastViewedTripId);
  const rememberTripId = useTripSelectionStore((state) => state.rememberTripId);

  // Trip selection must be declared early because many hooks below depend on `selectedTrip`.
  // If any hook references `selectedTrip` before it's initialized, web will crash with a TDZ error.
  const [selectedTripId, setSelectedTripId] = useState<string | null>(paramTripId ?? rememberedTripId ?? null);
  const selectedTrip = useMemo(() => {
    return trips.find((trip) => trip.id === selectedTripId) || null;
  }, [selectedTripId, trips]);

  useActivePlanTripBinding(selectedTripId);

  const isPlanScreenFocused = useIsFocused();

  const selectedTripIdRef = useRef(selectedTripId);
  selectedTripIdRef.current = selectedTripId;
  const tripsRef = useRef(trips);
  tripsRef.current = trips;
  const selectedTripRef = useRef(selectedTrip);
  selectedTripRef.current = selectedTrip;
  const itineraryItemsRef = useRef<ItineraryItem[]>([]);
  const planItineraryHasSeenNonEmptyRef = useRef(false);
  const planItineraryEmptySinceRef = useRef<number | null>(null);
  const planItineraryOnlineVerifyInFlightRef = useRef(false);
  /** IDs optimistically removed — filtered out of poll/cache until server confirms deletion. */
  const pendingDeletedItineraryIdsRef = useRef<Set<string>>(new Set());

  const filterPendingDeletedItineraryItems = useCallback((items: ItineraryItem[]): ItineraryItem[] => {
    const pending = pendingDeletedItineraryIdsRef.current;
    if (pending.size === 0) return items;
    return items.filter((item) => !pending.has(item.id));
  }, []);

  const reconcilePendingDeletedItineraryItems = useCallback((serverItems: ItineraryItem[]) => {
    const pending = pendingDeletedItineraryIdsRef.current;
    if (pending.size === 0) return;
    const serverIds = new Set(serverItems.map((i) => i.id));
    for (const id of pending) {
      if (!serverIds.has(id)) {
        pending.delete(id);
      }
    }
  }, []);
  const bookingsRef = useRef<Booking[]>([]);
  const expensesRef = useRef<Expense[]>([]);
  const lastBootstrapParamWriteRef = useRef<string | undefined>(paramTripId);
  const lastReadinessSeedSignatureRef = useRef('');
  const lastChecklistSeedSignatureRef = useRef('');

  useEffect(() => {
    if (selectedTripId) {
      rememberTripId(selectedTripId);
    }
  }, [rememberTripId, selectedTripId]);

  // Warm the lazy DocumentViewer chunk so first open rarely suspends. A Suspense fallback `Modal` races
  // DocumentViewer's own `Modal` and can freeze iOS ("presentation is in progress").
  useEffect(() => {
    void import('@/components/itinerary/DocumentViewer');
  }, []);
  /** Stable shallow output for Plan store slice (useShallow's wrapper is a new fn each render → getSnapshot churn / max depth). */
  const planStoreItemsShallowRef = useRef<Record<string, Notification>>({});
  const oauthSyncIntentRef = useRef(false);

  // Stable across Firestore snapshots that replace the trips array with the same ids (avoids effect churn).
  const tripIdsKey = useMemo(() => [...trips].map((t) => t.id).sort().join(','), [trips]);

  // Numeric keys so Firestore snapshot churn (new Timestamp/Date object identities) does not invalidate
  // downstream memos/effects that depend on trip dates (avoids setState loops).
  const tripStartMs: number | null =
    selectedTrip != null
      ? (() => {
          const t = coerceDate(selectedTrip.startDate)?.getTime();
          return t != null && Number.isFinite(t) ? t : null;
        })()
      : null;

  const tripEndMs: number | null =
    selectedTrip != null
      ? (() => {
          const t = coerceDate(selectedTrip.endDate)?.getTime();
          return t != null && Number.isFinite(t) ? t : null;
        })()
      : null;

  const tripDestRangeKey =
    selectedTrip != null && Array.isArray(selectedTrip.destinations)
      ? selectedTrip.destinations
          .map((d) => {
            const s = coerceDate((d as { startDate?: unknown }).startDate)?.getTime() ?? '';
            const e = coerceDate((d as { endDate?: unknown }).endDate)?.getTime() ?? '';
            return `${s},${e}`;
          })
          .join('|')
      : '';

  const safeTripStartDate = useMemo(() => {
    if (tripStartMs == null) return null;
    return new Date(tripStartMs);
  }, [tripStartMs]);

  const safeTripEndDate = useMemo(() => {
    if (tripEndMs != null && Number.isFinite(tripEndMs)) {
      return new Date(tripEndMs);
    }
    if (tripStartMs != null) return new Date(tripStartMs);
    return null;
  }, [tripEndMs, tripStartMs]);

  useEffect(() => {
    const trip = selectedTripRef.current;
    if (!trip) return;
    const start = coerceDate(trip.startDate);
    const end = coerceDate(trip.endDate);
    if (!start || !end) {
      console.warn('[Plan] Invalid trip dates detected', {
        tripId: trip.id,
        startDate: trip.startDate,
        endDate: trip.endDate,
      });
    }
  }, [selectedTripId]);

  // Proactive trip membership sync so Maybe/vote/subcollection access has members/memberIds set
  const lastSyncedTripIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedTrip?.id || !firebaseUser?.uid) return;
    if (lastSyncedTripIdRef.current === selectedTrip.id) return;
    lastSyncedTripIdRef.current = selectedTrip.id;
    void syncTripMembership(selectedTrip.id).catch(() => {
      // Non-blocking; permission error handlers in maybeItems/voting will retry
    });
  }, [selectedTrip?.id, firebaseUser?.uid]);

  const [inboxProvider, setInboxProvider] = useState<InboxProviderId>('gmail');
  const [inboxSyncing, setInboxSyncing] = useState(false);
  const [showEmailSyncStatus, setShowEmailSyncStatus] = useState(false);
  const [inboxConnections, setInboxConnections] = useState<Record<InboxProviderId, boolean | null>>({
    gmail: null,
    outlook: null,
    icloud: null,
  });
  const [imapModalVisible, setImapModalVisible] = useState(false);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [copilotLoading, setCopilotLoading] = useState(false);
  const [copilotResult, setCopilotResult] = useState<CopilotResultState | null>(null);
  const [copilotPendingType, setCopilotPendingType] = useState<CopilotPendingType | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [showCollaborationModal, setShowCollaborationModal] = useState(false);

  const [aiSuggestions, setAiSuggestions] = useState<ChatPlannerResult | null>(null);
  const {
    aiSuggestionsLoading,
    aiSuggestionsLoadingExtended,
    aiSuggestionsError,
    aiSuggestionsErrorMode,
    aiSuggestionsRequestMode,
    setAiSuggestionsLoading,
    setAiSuggestionsError,
    setAiSuggestionsErrorMode,
    setAiSuggestionsLoadingExtended,
    setAiSuggestionsRequestMode,
  } = useAiSuggestionsUiState();
  const aiSuggestionsRequestNonceRef = useRef(0);
  const aiPlannerIntentAppendixRef = useRef<string | null>(null);
  const aiSuggestionsLongTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const aiSuggestionsHardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [ideasModalVisibleForFab, setIdeasModalVisibleForFab] = useState(false);
  const [ideasModalReopenToken, setIdeasModalReopenToken] = useState(0);
  const [applyingSuggestions, setApplyingSuggestions] = useState(false);
  const [addingSuggestionIds, setAddingSuggestionIds] = useState<Set<string>>(new Set());
  const [planIdeaCardsQuota, setPlanIdeaCardsQuota] = useState<AiPlanIdeaCardsQuotaView | null>(null);

  const cancelAiSuggestions = useCallback(() => {
    // Best-effort cancel: we can't abort an in-flight callable, so we invalidate it and
    // ignore late results by bumping the request nonce.
    aiSuggestionsRequestNonceRef.current += 1;
    setAiSuggestionsLoading(false);
    setAiSuggestionsLoadingExtended(false);
    setAiSuggestionsRequestMode(null);
    if (aiSuggestionsLongTimerRef.current) {
      clearTimeout(aiSuggestionsLongTimerRef.current);
      aiSuggestionsLongTimerRef.current = null;
    }
    if (aiSuggestionsHardTimerRef.current) {
      clearTimeout(aiSuggestionsHardTimerRef.current);
      aiSuggestionsHardTimerRef.current = null;
    }
  }, []);
  const profile = useUserStore(selectUserProfile);
  const offlinePlan = useOfflinePlanTierDisplay(firebaseUser?.uid, {
    isPremium: profile?.isPremium,
    planTier: profile?.planTier,
    entitlement: profile?.entitlement,
  });
  const planAiPremium = offlinePlan.effectivePremium;
  const openPremiumGate = usePremiumGateStore((s) => s.open);

  useEffect(() => {
    const uid = firebaseUser?.uid;
    if (!uid || planAiPremium) {
      setPlanIdeaCardsQuota(null);
      return () => {};
    }
    return subscribeAiPlanIdeaCardsQuota(uid, true, setPlanIdeaCardsQuota);
  }, [firebaseUser?.uid, planAiPremium]);

  // Visa requirements (in-app; powered by TravelBuddy API via Functions)
  const passportCountryForVisaCall =
    profile?.citizenshipIso2 ||
    (profile?.citizenship ? toIso2CountryCode(profile.citizenship) : undefined) ||
    profile?.homeCountryIso2 ||
    (profile?.homeCountry ? toIso2CountryCode(profile.homeCountry) : undefined) ||
    undefined;
  const passportCountryForVisaLabel =
    profile?.citizenship ||
    profile?.homeCountry ||
    passportCountryForVisaCall;
  const [visaModalVisible, setVisaModalVisible] = useState(false);
  const [visaModalLoading, setVisaModalLoading] = useState(false);
  const [visaModalError, setVisaModalError] = useState<string | null>(null);
  const [visaRequirement, setVisaRequirement] = useState<VisaRequirement | null>(null);
  const visaRequirementRef = useRef<VisaRequirement | null>(null);
  visaRequirementRef.current = visaRequirement;
  const lastVisaFetchKeyRef = useRef<string | null>(null);
  const [destinationCountryIso2, setDestinationCountryIso2] = useState<string>('');

  const needsDestinationCountryConfirm = useMemo(
    () => computeTripNeedsDestinationCountryConfirm(selectedTrip),
    [selectedTrip],
  );

  // Visa requirements are fetched on-demand when the user opens the visa modal.
  // Background prefetch was removed to avoid extra API calls (e.g. on every Plan tab visit or trips snapshot).

  const countryOptions: SelectOption[] = useMemo(
    () => COUNTRY_ISO2_OPTIONS.map((o) => ({ value: o.value, label: o.label, meta: o.meta })),
    [],
  );

  const visaModalDest0 = selectedTrip?.destinations?.[0] as
    | { countryCode?: unknown; country?: unknown }
    | undefined;
  const visaModalDestCountryCodeStr =
    typeof visaModalDest0?.countryCode === 'string' ? visaModalDest0.countryCode : '';
  const visaModalDestCountryStr =
    typeof visaModalDest0?.country === 'string' ? visaModalDest0.country : '';

  const visaModalDestinationIsoHint = useMemo(() => {
    if (!visaModalDestCountryCodeStr && !visaModalDestCountryStr) return undefined;
    return (
      (visaModalDestCountryCodeStr ? toIso2CountryCode(visaModalDestCountryCodeStr) : undefined) ||
      (visaModalDestCountryStr ? toIso2CountryCode(visaModalDestCountryStr) : undefined) ||
      undefined
    );
  }, [visaModalDestCountryCodeStr, visaModalDestCountryStr]);

  useEffect(() => {
    if (!visaModalVisible || !visaModalDestinationIsoHint) return;
    if (!destinationCountryIso2) {
      setDestinationCountryIso2(visaModalDestinationIsoHint);
    }
  }, [visaModalVisible, visaModalDestinationIsoHint, destinationCountryIso2]);

  const persistDestinationCountryAndRetry = useCallback(async () => {
    if (!selectedTrip) return;
    if (!firebaseUser?.uid) {
      showInfo('Sign in to update trip details.');
      return;
    }
    if (!destinationCountryIso2) {
      Alert.alert('Destination country required', 'Please select the destination country to continue.');
      return;
    }
    if (!passportCountryForVisaCall) {
      Alert.alert(
        'Add your citizenship',
        'To check visa requirements, please add your citizenship in your profile.',
      );
      return;
    }

    const countryName = fromIso2CountryCode(destinationCountryIso2) || destinationCountryIso2;
    const baseCityLabel = (selectedTrip.destination || selectedTrip.destinations?.[0]?.city || selectedTrip.name || '').toString();

    const nextDestinations: any[] =
      selectedTrip.destinations && selectedTrip.destinations.length > 0
        ? [
            {
              ...selectedTrip.destinations[0],
              country: countryName,
              countryCode: destinationCountryIso2,
            },
            ...selectedTrip.destinations.slice(1),
          ]
        : [
            {
              city: baseCityLabel || `${selectedTrip.name}, ${countryName}`,
              country: countryName,
              countryCode: destinationCountryIso2,
              startDate: safeTripStartDate ?? new Date(),
              endDate: safeTripEndDate ?? safeTripStartDate ?? new Date(),
            },
          ];

    const legacyDestination =
      selectedTrip.destination && !selectedTrip.destination.includes(',')
        ? `${selectedTrip.destination}, ${countryName}`
        : selectedTrip.destination;

    setVisaModalLoading(true);
    setVisaModalError(null);
    try {
      await updateTrip(selectedTrip.id, firebaseUser.uid, {
        destinations: nextDestinations,
        destination: legacyDestination,
      });

      const patchedTrip = { ...selectedTrip, destinations: nextDestinations, destination: legacyDestination } as any;
      const result = await getTravelRequirements(patchedTrip, passportCountryForVisaCall);
      if (!result) {
        setVisaRequirement(null);
        setVisaModalError(`Unable to fetch visa requirements right now.${formatVisaErrorHint()}`);
        return;
      }

      const visaReq: VisaRequirement = {
        status: (result.visaStatus || 'unknown') as VisaRequirement['status'],
        visaRequired: !!result.visaRequired,
        stayDuration: result.stayDuration,
        notes: result.visaNotes,
        eVisaLink: result.eVisaLink,
        embassyLink: result.embassyLink,
        lastUpdated: result.lastUpdated,
        destination: countryName,
        passportCountry: passportCountryForVisaLabel || passportCountryForVisaCall,
      };
      setVisaRequirement(visaReq);
      lastVisaFetchKeyRef.current = `${selectedTrip.id}:${baseCityLabel}:${passportCountryForVisaCall}`;
    } catch (error) {
      console.error('[Plan] Error saving destination country for visa check', error);
      setVisaModalError('Unable to save destination country. Please try again.');
    } finally {
      setVisaModalLoading(false);
    }
  }, [
    destinationCountryIso2,
    firebaseUser?.uid,
    formatVisaErrorHint,
    passportCountryForVisaCall,
    passportCountryForVisaLabel,
    selectedTrip,
    safeTripEndDate,
    safeTripStartDate,
    showInfo,
  ]);
  const [hasAutoSelectedProvider, setHasAutoSelectedProvider] = useState(false);
  const [lastUsedProvider, setLastUsedProviderState] = useState<InboxProviderId | null>(null);

  // Itinerary state
  const [itineraryItems, setItineraryItems] = useState<ItineraryItem[]>([]);
  itineraryItemsRef.current = itineraryItems;
  bookingsRef.current = bookings;
  expensesRef.current = expenses;
  const [itineraryLoading, setItineraryLoading] = useState(false);

  useAppleTripSurfacePlanSync({
    trips,
    selectedTrip,
    itineraryItems,
    userId: firebaseUser?.uid,
    itineraryReady: !itineraryLoading,
    enabled: isPlanScreenFocused,
  });

  const [showItineraryForm, setShowItineraryForm] = useState(false);
  const [editingItineraryItem, setEditingItineraryItem] = useState<ItineraryItem | null>(null);
  const [selectedDateForForm, setSelectedDateForForm] = useState<Date | null>(null);
  const [pendingQuickTemplate, setPendingQuickTemplate] = useState<string | null>(null);
  const [addedItemSuccess, setAddedItemSuccess] = useState<{
    message: string;
    details?: string;
    itemId: string;
  } | null>(null);
  const [pendingOpenItemId, setPendingOpenItemId] = useState<string | null>(null);
  const addedItemSuccessTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Maybe → Schedule (prompt for date/time)
  const [scheduleMaybeVisible, setScheduleMaybeVisible] = useState(false);
  const [scheduleMaybeItem, setScheduleMaybeItem] = useState<MaybeItem | null>(null);
  const [scheduleMaybeDate, setScheduleMaybeDate] = useState<Date | null>(null);
  const [scheduleMaybeTime, setScheduleMaybeTime] = useState<string>(''); // HH:mm
  const [scheduleMaybeEndTime, setScheduleMaybeEndTime] = useState<string>(''); // HH:mm (optional)
  const [scheduleMaybeFlexible, setScheduleMaybeFlexible] = useState<boolean>(true);
  const [scheduleMaybeError, setScheduleMaybeError] = useState<string | null>(null);
  const [scheduleMaybeAdding, setScheduleMaybeAdding] = useState(false);
  const [scheduleMaybeDatePickerVisible, setScheduleMaybeDatePickerVisible] = useState(false);
  const scheduleMaybeResolveRef = useRef<((ok: boolean) => void) | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    readiness: true, // collapsed by default
    planning: false,
    inspiration: false,
    checklist: false,
    copilot: false,
    travelServices: false,
    comments: false,
  });
  const COLLAPSE_KEY = '@tripvio_plan_collapsed_sections';
  const LEGACY_COLLAPSE_KEY = '@tripflow_plan_collapsed_sections';

  useEffect(() => {
    // One-time migration for persisted UI state keys
    (async () => {
      try {
        const legacyDefs = await AsyncStorage.getItem(legacyTripPlanDefsKey);
        if (legacyDefs !== null) {
          await AsyncStorage.setItem(tripPlanDefsKey, legacyDefs);
          await AsyncStorage.removeItem(legacyTripPlanDefsKey);
        }
      } catch {
        // ignore
      }
      try {
        const legacyCollapsed = await AsyncStorage.getItem(LEGACY_COLLAPSE_KEY);
        if (legacyCollapsed !== null) {
          await AsyncStorage.setItem(COLLAPSE_KEY, legacyCollapsed);
          await AsyncStorage.removeItem(LEGACY_COLLAPSE_KEY);
        }
      } catch {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    const items = itineraryItemsRef.current;
    if (!items || items.length === 0) return;
    const invalidItems = items.filter((item) => !coerceDate(item.startDate));
    if (invalidItems.length > 0) {
      console.warn('[Plan] Invalid itinerary item dates detected', {
        count: invalidItems.length,
        sampleIds: invalidItems.slice(0, 3).map((item) => item.id),
      });
    }
  }, [selectedTripId]);

  useEffect(() => {
    AsyncStorage.getItem(COLLAPSE_KEY)
      .then((stored) => {
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            setExpandedSections((prev) => ({ ...prev, ...parsed }));
          } catch {
            // ignore parse errors
          }
        }
      })
      .catch(() => {
        // ignore
      });
  }, []);

  const updateSectionCollapse = useCallback((key: string, collapsed: boolean) => {
    setExpandedSections((prev) => {
      const next = { ...prev, [key]: collapsed };
      AsyncStorage.setItem(COLLAPSE_KEY, JSON.stringify(next)).catch(() => {});
      return next;
    });
  }, []);
  const [viewingDocument, setViewingDocument] = useState<{
    url: string;
    offlinePath?: string | null;
    fileName?: string;
    contentType?: string | null;
  } | null>(null);
  const [showFlightSearch, setShowFlightSearch] = useState(false);
  const [showHotelSearch, setShowHotelSearch] = useState(false);
  // Removed expense form state - now in wallet.tsx
  // Removed activeTab state - only planning tab remains
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [smartUploadLoading, setSmartUploadLoading] = useState(false);
  const [smartUploadVisible, setSmartUploadVisible] = useState(false);
  const [showTripAlertsModal, setShowTripAlertsModal] = useState(false);
  const [collabSuggestToken, setCollabSuggestToken] = useState(0);
  const [commentsExpanded, setCommentsExpanded] = useState(false);

  const scrollPlanToCommentsSection = useCallback(() => {
    setCommentsExpanded(true);
    const pad = theme.spacing(2);
    const runScroll = () => {
      const y = commentsSectionYRef.current;
      planScrollViewRef.current?.scrollTo({
        y: Math.max(0, y - pad),
        animated: true,
      });
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        InteractionManager.runAfterInteractions(runScroll);
      });
    });
  }, [theme]);

  // Trip Companion (during-trip FAB)
  const [companionMenuVisible, setCompanionMenuVisible] = useState(false);
  const [tripToolsVisible, setTripToolsVisible] = useState(false);
  const [whatsNextVisible, setWhatsNextVisible] = useState(false);
  const [travelCompanionVisible, setTravelCompanionVisible] = useState(false);

  // Show add-item form (hoisted above callers to avoid TDZ issues)
  const openAddItem = useCallback((date?: Date) => {
    setEditingItineraryItem(null);
    setSelectedDateForForm(date instanceof Date ? date : null);
    setShowItineraryForm(true);
  }, []);

  // Cleanup AI timers on unmount
  useEffect(() => {
    return () => {
      if (aiSuggestionsLongTimerRef.current) {
        clearTimeout(aiSuggestionsLongTimerRef.current);
        aiSuggestionsLongTimerRef.current = null;
      }
      if (aiSuggestionsHardTimerRef.current) {
        clearTimeout(aiSuggestionsHardTimerRef.current);
        aiSuggestionsHardTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setCommentsExpanded(false);
  }, [selectedTrip?.id]);

  // For scheduling (Maybe → Itinerary), use the trip's configured date range (and destinations),
  // not the currently-created itinerary items. Otherwise, the calendar can incorrectly shrink
  // to a single day when the itinerary is still empty/partial.
  const scheduleTripRange = useMemo(() => {
    if (!selectedTrip) {
      return { start: null, end: null, source: 'none' as const };
    }
    return deriveTripDateRange(selectedTrip, null);
  }, [selectedTripId, tripStartMs, tripEndMs, tripDestRangeKey]);
  const scheduleTripStart = useMemo(() => {
    const base = scheduleTripRange.start ?? safeTripStartDate;
    if (!base) return null;
    const d = new Date(base);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [safeTripStartDate, scheduleTripRange.start]);
  const scheduleTripEnd = useMemo(() => {
    const base = scheduleTripRange.end ?? safeTripEndDate;
    let endDate: Date | null = null;
    
    if (base) {
      const d = new Date(base);
      d.setHours(0, 0, 0, 0);
      endDate = d;
    }
    
    // Ensure we have at least a reasonable date range for scheduling
    // If end date equals start date (single day trip) or is null, extend to start + 14 days
    if (scheduleTripStart) {
      const startTime = scheduleTripStart.getTime();
      const endTime = endDate?.getTime() ?? 0;
      
      // If same day or no end date, provide 14-day range
      if (!endDate || endTime <= startTime) {
        const fallbackEnd = new Date(scheduleTripStart);
        fallbackEnd.setDate(fallbackEnd.getDate() + 14);
        fallbackEnd.setHours(0, 0, 0, 0);
        return fallbackEnd;
      }
    }
    
    return endDate ?? scheduleTripStart;
  }, [safeTripEndDate, scheduleTripRange.end, scheduleTripStart]);
  const isScheduleSingleDayTrip = useMemo(() => {
    if (!scheduleTripStart || !scheduleTripEnd) return true;
    return scheduleTripStart.getTime() === scheduleTripEnd.getTime();
  }, [scheduleTripEnd, scheduleTripStart]);

  const getDefaultMaybeScheduleDate = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (!scheduleTripStart) return today;
    const tripStart = new Date(scheduleTripStart);
    const tripEnd = scheduleTripEnd ? new Date(scheduleTripEnd) : new Date(tripStart);
    if (today < tripStart) return tripStart;
    if (today > tripEnd) return tripStart;
    return today;
  }, [scheduleTripEnd, scheduleTripStart]);

  const nextFiveMinutes = useCallback((): string => {
    const now = new Date();
    const minutes = now.getMinutes();
    const rounded = Math.ceil(minutes / 5) * 5;
    now.setSeconds(0, 0);
    if (rounded >= 60) {
      now.setHours(now.getHours() + 1);
      now.setMinutes(0);
    } else {
      now.setMinutes(rounded);
    }
    return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
  }, []);

  const scheduleMaybeSuggestedTimes = useMemo((): string[] => {
    const base = coerceDate(scheduleMaybeDate);
    if (!base) return [];
    const day = new Date(base);
    day.setHours(0, 0, 0, 0);
    const dayKey = day.getTime();

    const parse = (t?: string | null): number | null => {
      if (!t || t === 'flexible') return null;
      const m = /^(\d{1,2}):(\d{2})$/.exec(t.trim());
      if (!m) return null;
      const hh = Number(m[1]);
      const mm = Number(m[2]);
      if (!Number.isFinite(hh) || !Number.isFinite(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
      return hh * 60 + mm;
    };
    const fmt = (mins: number): string => {
      const hh = Math.floor(mins / 60);
      const mm = mins % 60;
      return `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
    };

    // Consider items with explicit times as "busy". If endTime is missing, assume 60 minutes.
    const busy: Array<{ start: number; end: number }> = [];
    for (const it of itineraryItems) {
      const itDay = coerceDate((it as any)?.startDate ?? null);
      if (!itDay) continue;
      const normalized = new Date(itDay);
      normalized.setHours(0, 0, 0, 0);
      if (normalized.getTime() !== dayKey) continue;

      const start = parse(it.startTime ?? null);
      if (start == null) continue;
      const end = parse(it.endTime ?? null) ?? Math.min(start + 60, 24 * 60);
      if (end <= start) continue;
      busy.push({ start, end });
    }

    const windowStart = 8 * 60;  // 08:00
    const windowEnd = 22 * 60;   // 22:00

    if (busy.length === 0) {
      // Common defaults when the day has no scheduled items.
      return ['09:00', '12:00', '15:00', '18:00'];
    }

    // Merge overlapping busy intervals
    busy.sort((a, b) => a.start - b.start);
    const merged: Array<{ start: number; end: number }> = [];
    for (const b of busy) {
      if (!merged.length) {
        merged.push({ ...b });
        continue;
      }
      const last = merged[merged.length - 1];
      if (b.start <= last.end) {
        last.end = Math.max(last.end, b.end);
      } else {
        merged.push({ ...b });
      }
    }

    // Compute free intervals in [windowStart, windowEnd]
    const free: Array<{ start: number; end: number }> = [];
    let cursor = windowStart;
    for (const b of merged) {
      const bs = Math.max(b.start, windowStart);
      const be = Math.min(b.end, windowEnd);
      if (be <= windowStart || bs >= windowEnd) continue;
      if (bs > cursor) free.push({ start: cursor, end: bs });
      cursor = Math.max(cursor, be);
    }
    if (cursor < windowEnd) free.push({ start: cursor, end: windowEnd });

    // Suggest 1–2 start times per free interval (start, plus +60min if space).
    const suggested = new Set<string>();
    for (const f of free) {
      if (f.end - f.start < 30) continue; // too small
      suggested.add(fmt(f.start));
      if (f.end - f.start >= 120) {
        const t = f.start + 60;
        if (t + 30 <= f.end) suggested.add(fmt(t));
      }
    }

    return Array.from(suggested).sort();
  }, [itineraryItems, scheduleMaybeDate]);

  const scheduleMaybeSuggestedBlocks = useMemo((): Array<{ start: string; end: string; label: string }> => {
    const base = coerceDate(scheduleMaybeDate);
    if (!base) return [];
    const day = new Date(base);
    day.setHours(0, 0, 0, 0);
    const dayKey = day.getTime();

    const parse = (t?: string | null): number | null => {
      if (!t || t === 'flexible') return null;
      const m = /^(\d{1,2}):(\d{2})$/.exec(t.trim());
      if (!m) return null;
      const hh = Number(m[1]);
      const mm = Number(m[2]);
      if (!Number.isFinite(hh) || !Number.isFinite(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
      return hh * 60 + mm;
    };
    const fmt = (mins: number): string => {
      const hh = Math.floor(mins / 60);
      const mm = mins % 60;
      return `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
    };
    const fmtDur = (mins: number): string => {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      if (h <= 0) return `${m}m`;
      if (m === 0) return `${h}h`;
      return `${h}h ${m}m`;
    };

    // Consider items with explicit times as "busy". If endTime is missing, assume 60 minutes.
    const busy: Array<{ start: number; end: number }> = [];
    for (const it of itineraryItems) {
      const itDay = coerceDate((it as any)?.startDate ?? null);
      if (!itDay) continue;
      const normalized = new Date(itDay);
      normalized.setHours(0, 0, 0, 0);
      if (normalized.getTime() !== dayKey) continue;

      const start = parse(it.startTime ?? null);
      if (start == null) continue;
      const end = parse(it.endTime ?? null) ?? Math.min(start + 60, 24 * 60);
      if (end <= start) continue;
      busy.push({ start, end });
    }

    const windowStart = 8 * 60; // 08:00
    const windowEnd = 22 * 60; // 22:00

    // If nothing scheduled, propose a few common “blocks”.
    if (busy.length === 0) {
      return [
        { start: '09:00', end: '11:00', label: '09:00–11:00 (2h)' },
        { start: '12:00', end: '14:00', label: '12:00–14:00 (2h)' },
        { start: '15:00', end: '17:00', label: '15:00–17:00 (2h)' },
      ];
    }

    // Merge overlapping busy intervals
    busy.sort((a, b) => a.start - b.start);
    const merged: Array<{ start: number; end: number }> = [];
    for (const b of busy) {
      if (!merged.length) {
        merged.push({ ...b });
        continue;
      }
      const last = merged[merged.length - 1];
      if (b.start <= last.end) {
        last.end = Math.max(last.end, b.end);
      } else {
        merged.push({ ...b });
      }
    }

    // Compute free intervals in [windowStart, windowEnd]
    const free: Array<{ start: number; end: number }> = [];
    let cursor = windowStart;
    for (const b of merged) {
      const bs = Math.max(b.start, windowStart);
      const be = Math.min(b.end, windowEnd);
      if (be <= windowStart || bs >= windowEnd) continue;
      if (bs > cursor) free.push({ start: cursor, end: bs });
      cursor = Math.max(cursor, be);
    }
    if (cursor < windowEnd) free.push({ start: cursor, end: windowEnd });

    const blocks: Array<{ start: string; end: string; label: string }> = [];
    for (const f of free) {
      const dur = f.end - f.start;
      if (dur < 60) continue;

      // If the gap is very large, suggest 2h chunks instead of an overly long block.
      if (dur > 240) {
        const aStart = f.start;
        const aEnd = Math.min(f.start + 120, f.end);
        blocks.push({ start: fmt(aStart), end: fmt(aEnd), label: `${fmt(aStart)}–${fmt(aEnd)} (${fmtDur(aEnd - aStart)})` });
        const bStart = Math.min(f.start + 150, f.end - 60);
        const bEnd = Math.min(bStart + 120, f.end);
        if (bEnd - bStart >= 60) {
          blocks.push({ start: fmt(bStart), end: fmt(bEnd), label: `${fmt(bStart)}–${fmt(bEnd)} (${fmtDur(bEnd - bStart)})` });
        }
        continue;
      }

      blocks.push({ start: fmt(f.start), end: fmt(f.end), label: `${fmt(f.start)}–${fmt(f.end)} (${fmtDur(dur)})` });
    }

    return blocks.slice(0, 6);
  }, [itineraryItems, scheduleMaybeDate]);

  const openMaybeSchedulePrompt = useCallback(
    async (maybe: MaybeItem): Promise<boolean> => {
      if (__DEV__) {
        console.log('[Plan] openMaybeSchedulePrompt called');
      }
      
      if (!selectedTripId || !tripsRef.current.some((t) => t.id === selectedTripId)) {
        if (__DEV__) {
          console.log('[Plan] No selected trip, cannot open schedule prompt');
        }
        return false;
      }
      if (!firebaseUser?.uid) {
        Alert.alert('Sign in required', 'Please sign in to schedule this idea.');
        return false;
      }

      // If a prompt is already open, treat it as cancelled to avoid dangling resolvers.
      if (scheduleMaybeResolveRef.current) {
        if (__DEV__) {
          console.log('[Plan] Cancelling previous schedule prompt');
        }
        scheduleMaybeResolveRef.current(false);
        scheduleMaybeResolveRef.current = null;
      }

      // If the schedule modal is already visible, reset it before reopening.
      if (scheduleMaybeVisible) {
        if (__DEV__) {
          console.log('[Plan] Schedule modal already visible, resetting before reopen');
        }
        setScheduleMaybeVisible(false);
        await new Promise((resolve) => setTimeout(resolve, 120));
      }

      // Ensure any nested modals (like date picker) are closed first
      if (scheduleMaybeDatePickerVisible) {
        setScheduleMaybeDatePickerVisible(false);
      }

      // Set up the schedule modal state
      setScheduleMaybeItem(maybe);
      setScheduleMaybeDate(getDefaultMaybeScheduleDate());
      setScheduleMaybeFlexible(true);
      setScheduleMaybeTime(nextFiveMinutes());
      setScheduleMaybeEndTime('');
      setScheduleMaybeError(null);
      setScheduleMaybeAdding(false);
      setScheduleMaybeDatePickerVisible(false);

      const resolveRef = scheduleMaybeResolveRef;
      const promise = new Promise<boolean>((resolve) => {
        resolveRef.current = resolve;
      });

      // Caller (PlanningTab) already closed Ideas modal when needed. Show schedule modal promptly.
      // Avoid waiting on InteractionManager so the modal doesn't stall during long interactions.
      setScheduleMaybeVisible(true);
      requestAnimationFrame(() => {
        setScheduleMaybeVisible(true);
        if (__DEV__) {
          console.log('[Plan] Schedule modal now visible');
        }
      });
      // Fallback repaint in case the first frame is swallowed.
      setTimeout(() => setScheduleMaybeVisible(true), 80);

      return await promise;
    },
    [firebaseUser?.uid, getDefaultMaybeScheduleDate, nextFiveMinutes, selectedTripId, scheduleMaybeVisible, scheduleMaybeDatePickerVisible],
  );

  const handleShareMyLocation = useCallback(async () => {
    try {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Location permission needed', 'Enable location permissions to share your coordinates.');
        return;
      }
      const loc = await Location.getCurrentPositionAsync({});
      const url = `https://maps.google.com/?q=${loc.coords.latitude},${loc.coords.longitude}`;
      await Share.share({
        message: `My current location: ${url}`,
      });
    } catch (error) {
      Alert.alert('Could not share location', error instanceof Error ? error.message : 'Please try again.');
    }
  }, []);

  const refreshInboxStatus = useCallback(
    async (provider: InboxProviderId, opts?: CheckInboxStatusOptions) => {
      if (!firebaseUser?.uid) {
        setInboxConnections({
          gmail: null,
          outlook: null,
          icloud: null,
        });
        return null;
      }

      try {
        const status = await checkInboxStatus(provider, opts);
        setInboxConnections((prev) => {
          const nextConnected = status.connected ?? false;
          if (prev[provider] === nextConnected) {
            return prev;
          }
          return {
            ...prev,
            [provider]: nextConnected,
          };
        });
        return status;
      } catch (error) {
        console.error(`[Wallet] Error checking ${provider} inbox`, error);
        setInboxConnections((prev) =>
          prev[provider] === false
            ? prev
            : {
                ...prev,
                [provider]: false,
              },
        );
        return null;
      }
    },
    [firebaseUser?.uid],
  );

  // Defer inbox status checks until a trip is selected to avoid a deep cascade of
  // state changes during the initial mount bootstrap (which causes "max update depth").
  useEffect(() => {
    if (!selectedTripId) return;
    refreshInboxStatus(inboxProvider);
  }, [selectedTripId, refreshInboxStatus, inboxProvider]);

  // Load last used provider (per user) – deferred until trip selected.
  useEffect(() => {
    if (!firebaseUser?.uid || !selectedTripId) {
      setLastUsedProviderState(null);
      return;
    }
    getLastUsedInboxProvider(firebaseUser.uid)
      .then((provider) => setLastUsedProviderState(provider))
      .catch(() => {});
  }, [firebaseUser?.uid, selectedTripId]);

  // Auto-select a connected provider (preferring last used, then Gmail -> Outlook -> iCloud) when available.
  // Deferred until trip selected.
  useEffect(() => {
    if (!firebaseUser?.uid || !selectedTripId) return;
    const baseOrder: InboxProviderId[] = ['gmail', 'outlook', 'icloud'];
    const preferredOrder: InboxProviderId[] = lastUsedProvider
      ? [lastUsedProvider, ...baseOrder.filter((p) => p !== lastUsedProvider)]
      : baseOrder;
    const connected = preferredOrder.find((p) => inboxConnections[p]);
    const currentConnected = inboxConnections[inboxProvider];

    // Initial auto-pick or when current provider is not connected but another is.
    if (!hasAutoSelectedProvider && connected && connected !== inboxProvider) {
      setInboxProvider(connected);
      setHasAutoSelectedProvider(true);
      return;
    }

    if (hasAutoSelectedProvider && currentConnected === false && connected && connected !== inboxProvider) {
      setInboxProvider(connected);
    }
  }, [firebaseUser?.uid, selectedTripId, inboxConnections, inboxProvider, hasAutoSelectedProvider, lastUsedProvider]);

  // Subscribe to trips updates
  useEffect(() => {
    if (!firebaseUser?.uid) {
      setTrips([]);
      setTripsLoading(false);
      return;
    }

    setTripsLoading(true);

    // Seed from cached trips (best-effort) so the screen doesn't sit on a spinner
    // while Firestore warms up on staging.
    let cancelled = false;
    let hasSeenLiveSnapshot = false;
    const lastKnown = getLastKnownTrips(firebaseUser.uid);
    if (lastKnown?.trips?.length) {
      setTrips(lastKnown.trips);
      setTripsLoading(false);
    }

    getCachedTrips(firebaseUser.uid)
      .then((cached) => {
        if (cancelled) return;
        // If Firestore already responded, never let cache overwrite live trips.
        if (hasSeenLiveSnapshot) return;
        if (Array.isArray(cached) && cached.length > 0) {
          // Only seed if we don't already have trips (avoid overwriting newer data).
          setTrips((prev) => (prev.length > 0 ? prev : cached));
          setTripsLoading(false);
        }
      })
      .catch(() => {});

    const unsubscribe = subscribeToUserTrips(firebaseUser.uid, (userTrips) => {
      hasSeenLiveSnapshot = true;
      // Avoid heavy logging in staging/production builds.
      if (__DEV__) {
        console.log('[Wallet] Trips loaded', { count: userTrips.length });
        if (userTrips.length === 0) {
          console.warn('[Wallet] No trips found for current user');
        }
      }
      setTrips((prev) => {
        if (
          networkMonitor.shouldUseOfflineDataFallback() &&
          prev.length > 0 &&
          userTrips.length === 0
        ) {
          return prev;
        }
        return userTrips;
      });
      setTripsLoading(false);
    });

    return () => {
      cancelled = true;
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (error) {
          // Silently ignore cleanup errors - they're often due to Firestore internal state
        }
      }
    };
  }, [firebaseUser?.uid]);

  // Ensure selection defaults to the nearest upcoming trip.
  // Do not list `selectedTripId` in deps: updating selection + setParams must not immediately re-enter
  // this effect (tab opens without tripId param → avoids setState/router churn loops).
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const currentTrips = tripsRef.current;
      const currentSelectedId = selectedTripIdRef.current;
      const tripExists = currentSelectedId
        ? currentTrips.some((trip) => trip.id === currentSelectedId)
        : false;
      const paramTripExists = paramTripId ? currentTrips.some((trip) => trip.id === paramTripId) : false;
      const rememberedTripExists = rememberedTripId
        ? currentTrips.some((trip) => trip.id === rememberedTripId)
        : false;
      const fallbackTripId = rememberedTripExists
        ? rememberedTripId ?? null
        : getDefaultTripId(currentTrips, firebaseUser?.uid);

      if (paramTripId) {
        lastBootstrapParamWriteRef.current = paramTripId;
        if (paramTripExists && currentSelectedId !== paramTripId) {
          if (__DEV__) {
            console.debug('[Plan][bootstrap] sync selection from route param', {
              paramTripId,
              currentSelectedId,
            });
          }
          setSelectedTripId(paramTripId);
        }
        if (!paramTripExists && !tripExists && fallbackTripId && currentSelectedId !== fallbackTripId) {
          setSelectedTripId(fallbackTripId);
        }
        return;
      }

      if (!tripExists) {
        if (fallbackTripId) {
          if (currentSelectedId !== fallbackTripId) {
            if (__DEV__) {
              console.debug('[Plan][bootstrap] selecting fallback trip', {
                fallbackTripId,
                currentSelectedId,
                tripCount: currentTrips.length,
              });
            }
            setSelectedTripId(fallbackTripId);
          }
          // Important: avoid writing route params during no-param tab bootstrap.
          // This route mutation can create navigation/state feedback loops on mount.
          lastBootstrapParamWriteRef.current = fallbackTripId;
        } else {
          if (currentTrips.length === 0) {
            return;
          }
          if (currentSelectedId !== null) {
            if (__DEV__) {
              console.debug('[Plan][bootstrap] clearing selected trip (no fallback available)', {
                currentSelectedId,
                tripCount: currentTrips.length,
              });
            }
            setSelectedTripId(null);
          }
          // Keep URL stable in no-param tab flow; only clear local selection.
          lastBootstrapParamWriteRef.current = undefined;
        }
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [paramTripId, tripIdsKey, firebaseUser?.uid, rememberedTripId]);

  useEffect(() => {
    setIdeasModalReopenToken(0);
    setIdeasModalVisibleForFab(false);
    setCollabSuggestToken(0);
  }, [selectedTripId]);

  const quickAddInitialItem = useMemo(() => {
    if (!pendingQuickTemplate || !selectedTripId) return undefined;
    const template = pendingQuickTemplate.toLowerCase();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tripStart = safeTripStartDate ? new Date(safeTripStartDate) : today;
    tripStart.setHours(0, 0, 0, 0);
    const tripEnd = safeTripEndDate ? new Date(safeTripEndDate) : new Date(tripStart);
    tripEnd.setHours(23, 59, 59, 999);
    const startDate = today < tripStart ? tripStart : today > tripEnd ? tripStart : today;

    const templates: Record<string, Partial<ItineraryItem>> = {
      meal: { title: 'Meal break', category: 'activity', startTime: '12:00' },
      nap: { title: 'Nap / quiet time', category: 'activity', startTime: '14:00' },
      buffer: { title: 'Buffer time', category: 'activity' },
      restroom: { title: 'Restroom stop', category: 'activity' },
      play: { title: 'Playground break', category: 'activity' },
    };

    const preset = templates[template] ?? { title: 'Family activity', category: 'activity' };
    return {
      title: preset.title,
      category: preset.category ?? 'activity',
      startDate,
      status: 'planned',
      documents: [],
      source: 'manual',
      ...preset,
    } as unknown as ItineraryItem;
  }, [pendingQuickTemplate, safeTripEndDate, safeTripStartDate, selectedTripId]);

  const destinationLabel = useMemo(
    () => (selectedTrip ? getTripDestinationLabel(selectedTrip) : undefined),
    [selectedTrip],
  );

  const isSelectedTripCompleted = selectedTrip ? isTripCompleted(selectedTrip) : false;
  const isSelectedTripReadOnly = selectedTrip ? (selectedTrip.isArchived || isSelectedTripCompleted) : false;

  useEffect(() => {
    if (!selectedTrip?.id || !isSelectedTripReadOnly) return;
    void clearNotificationsForTrip(selectedTrip.id).catch((error) => {
      console.error('[Plan] Failed to clear read-only trip notifications', error);
    });
  }, [selectedTrip?.id, isSelectedTripReadOnly]);

  // Trip Companion (during-trip FAB) - depends on selectedTrip, so it must be declared after it.
  const selectedTripArchived = selectedTrip?.isArchived ?? false;
  const isTripActiveNow = useMemo(() => {
    if (!selectedTripId || selectedTripArchived) return false;
    const now = new Date();
    const start = safeTripStartDate ? new Date(safeTripStartDate) : null;
    const end = safeTripEndDate ? new Date(safeTripEndDate) : null;
    if (!start || !end) return false;
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return now >= start && now <= end;
  }, [selectedTripId, selectedTripArchived, safeTripEndDate, safeTripStartDate]);

  const nextItineraryItem = useMemo(() => {
    if (!isTripActiveNow || !Array.isArray(itineraryItems) || itineraryItems.length === 0) return null;
    const now = new Date();
    let best: { item: ItineraryItem; at: number } | null = null;
    for (const it of itineraryItems) {
      if (!it?.startDate) continue;
      const dt = coerceDate(it.startDate);
      if (!dt) continue;
      if (it.startTime && it.startTime !== 'flexible') {
        const [h, m] = it.startTime.split(':').map((v) => parseInt(v, 10));
        if (Number.isFinite(h)) dt.setHours(h, Number.isFinite(m) ? m : 0, 0, 0);
      }
      const t = dt.getTime();
      if (Number.isNaN(t) || t < now.getTime()) continue;
      if (!best || t < best.at) best = { item: it, at: t };
    }
    return best?.item || null;
  }, [isTripActiveNow, itineraryItems]);

  // Get user's permission for selected trip
  const userPermission = useMemo((): CollaboratorPermission | 'owner' | null => {
    if (!selectedTrip || !firebaseUser?.uid) return null;
    if (selectedTrip.owner === firebaseUser.uid) return 'owner';
    const collaborator = selectedTrip.collaborators?.find(c => c.userId === firebaseUser.uid);
    return collaborator?.permission || null;
  }, [selectedTrip, firebaseUser?.uid]);

  const isOwner = userPermission === 'owner';

  const selectedTripForBudget = useMemo(
    () => (selectedTrip ? [selectedTrip] : []),
    [selectedTripId, tripIdsKey],
  );
  const budgetSnapshots = useTripBudgetSummaries(
    selectedTripForBudget,
    Boolean(firebaseUser?.uid && selectedTrip && !tripsLoading),
  );
  const budgetSnapshot = selectedTrip ? budgetSnapshots[selectedTrip.id] : undefined;

  // Generate local alerts for this trip (includes booking/itinerary context for readiness cues).
  // Use stable primitives (tripId, tripIdsKey) instead of the `selectedTrip` object reference
  // so Firestore snapshot churn (new Trip instances with same data) doesn't recalculate alerts.
  const tripAlertsForPlan = useMemo<TripAlert[]>(() => {
    const trip = selectedTripRef.current;
    if (!trip) return [];
    const uid = firebaseUser?.uid || trip.owner || '';
    return generateTripAlerts(
      trip,
      uid,
      undefined,
      {
        bookings: bookingsRef.current,
        itineraryItems: itineraryItemsRef.current,
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTripId, tripIdsKey, firebaseUser?.uid, bookings, itineraryItems]);

  const [clearedAlertIds, setClearedAlertIds] = useState<Set<string>>(new Set());
  const [isOfflineDownloaded, setIsOfflineDownloaded] = useState(false);
  const [offlineCacheBump, setOfflineCacheBump] = useState(0);
  const [offlineFallbackActive, setOfflineFallbackActive] = useState(() =>
    networkMonitor.shouldUseOfflineDataFallback(),
  );

  useEffect(() => {
    setOfflineFallbackActive(networkMonitor.shouldUseOfflineDataFallback());
    return networkMonitor.addListener(() => {
      setOfflineFallbackActive(networkMonitor.shouldUseOfflineDataFallback());
    });
  }, []);
  const [offlineLastSyncAt, setOfflineLastSyncAt] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!selectedTrip?.id) {
      setClearedAlertIds((prev) => (prev.size === 0 ? prev : new Set()));
      setIsOfflineDownloaded((was) => (was ? false : was));
      return () => {
        cancelled = true;
      };
    }

    // Keep cleared-alert state in sync so banner counts match modal contents.
    getClearedAlerts()
      .then((ids) => {
        if (!cancelled) {
          setClearedAlertIds((prev) => {
            if (prev.size === ids.size && [...ids].every((id) => prev.has(id))) {
              return prev;
            }
            return ids;
          });
        }
      })
      .catch(() => {});

    // Offline availability status (same heuristic as PlanningTab banner).
    getCachedTripData(selectedTrip.id)
      .then((cached) => {
        if (!cancelled) setIsOfflineDownloaded(Boolean(cached));
      })
      .catch(() => {
        if (!cancelled) setIsOfflineDownloaded(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedTrip?.id, showTripAlertsModal, offlineCacheBump]);

  useEffect(() => {
    if (!offlineFallbackActive) return;
    let cancelled = false;
    const refreshLastSync = () => {
      void getLastSyncTime()
        .then((value) => {
          if (!cancelled) setOfflineLastSyncAt(value);
        })
        .catch(() => {});
    };
    refreshLastSync();
    const timer = setInterval(refreshLastSync, 60_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [offlineFallbackActive]);

  const offlineStaleDescriptor = useMemo(() => {
    if (!offlineFallbackActive) return null;
    if (!offlineLastSyncAt) return 'Showing cached trip data while you are offline.';
    return `Showing cached trip data from ${new Date(offlineLastSyncAt).toLocaleString()}.`;
  }, [offlineFallbackActive, offlineLastSyncAt]);

  // Get notifications from the store for this trip scope.
  // This uses a referentially-stable selector (avoids React "getSnapshot should be cached" loops).
  const globalTripNotifications = useNotificationsForTrip(selectedTripId ?? undefined);

  // Extract readiness alerts (missing_transport, missing_lodging) that are plan-specific
  // These need booking context that only Plan screen has access to
  const readinessAlerts = useMemo(() => {
    return tripAlertsForPlan.filter(
      (a) => a.type === 'missing_transport' || a.type === 'missing_lodging'
    );
  }, [tripAlertsForPlan]);

  const readinessAlertsSyncKey = useMemo(() => {
    if (!selectedTripId) return '';
    return `${selectedTripId}:${readinessAlerts.map((a) => a.id).sort().join(',')}`;
  }, [selectedTripId, readinessAlerts]);

  const clearedAlertIdsSignature = [...clearedAlertIds].sort().join('|');

  const visaCheck = useMemo(() => {
    return checkVisaRequirement(
      selectedTrip?.destination || selectedTrip?.name,
      profile?.citizenship || profile?.homeCountry,
      selectedTrip?.travelingFrom,
    );
  }, [selectedTrip?.destination, selectedTrip?.name, selectedTrip?.travelingFrom, profile?.citizenship, profile?.homeCountry]);

  const storeItemKeys = useMemo(() => {
    const keys: string[] = [];
    if (selectedTripId) {
      keys.push(`visa-${selectedTripId}`, `offline-${selectedTripId}`);
    }
    for (const a of readinessAlerts) {
      keys.push(a.id);
    }
    return keys;
  }, [selectedTripId, readinessAlertsSyncKey]);

  const selectPlanStoreItems = useCallback(
    (state: ReturnType<typeof useNotificationStore.getState>) => {
      const relevant: Record<string, Notification> = {};
      for (const k of storeItemKeys) {
        if (state.items[k]) relevant[k] = state.items[k];
      }
      const prev = planStoreItemsShallowRef.current;
      if (shallow(prev, relevant)) {
        return prev;
      }
      planStoreItemsShallowRef.current = relevant;
      return relevant;
    },
    [storeItemKeys],
  );

  const storeItems = useNotificationStore(selectPlanStoreItems);

  // Ensure readiness notifications exist in the store so checkbox toggles persist.
  useEffect(() => {
    if (!selectedTripId) return;
    if (readinessAlerts.length === 0) return;
    let cancelled = false;
    const createdAt = 0; // stable (ordering handled elsewhere)
    const raw = tripAlertsToNotifications(readinessAlerts, createdAt);
    hydrateTripAlertNotificationsWithPersistence(raw)
      .then((seed) => {
        if (cancelled) return;
        const signature = buildNotificationSeedSignature(seed);
        if (signature === lastReadinessSeedSignatureRef.current) return;
        lastReadinessSeedSignatureRef.current = signature;
        useNotificationStore.getState().upsertMany(seed);
      })
      .catch((error) => console.error('[Plan] Failed to hydrate readiness notifications', error));
    return () => {
      cancelled = true;
    };
    // Key off alert id set only; omit readinessAlerts from deps to avoid upsertMany loops when array identity churns.
  }, [readinessAlertsSyncKey, selectedTripId]);

  // Ensure visa/offline checklist notifications exist in the store so checkbox toggles work.
  useEffect(() => {
    if (!selectedTrip?.id || isSelectedTripReadOnly) return;

    const visaAlertId = `visa-${selectedTrip.id}`;
    const offlineAlertId = `offline-${selectedTrip.id}`;

    const needsVisa =
      (visaCheck.status === 'required' || visaCheck.status === 'check_required') &&
      !clearedAlertIds.has(visaAlertId);

    const needsOffline =
      planAiPremium && !isOfflineDownloaded && !clearedAlertIds.has(offlineAlertId);

    const raw: Notification[] = [];
    if (needsVisa) {
      raw.push({
        id: visaAlertId,
        title: 'Visa check',
        body: getVisaStatusMessage(
          visaCheck.status,
          selectedTrip.destination || selectedTrip.name,
          profile?.citizenship || profile?.homeCountry,
        ),
        scope: 'trip',
        scopeId: selectedTrip.id,
        kind: 'visa',
        severity: 'warning',
        createdAt: 0,
        source: 'local',
        meta: { tripId: selectedTrip.id, tripName: selectedTrip.name },
        cta: {
          label: 'Check visa requirements',
          action: { type: 'callback', id: 'visa' },
        },
      });
    }

    if (needsOffline) {
      raw.push({
        id: offlineAlertId,
        title: 'Offline download',
        body: 'Download itinerary, bookings, and activities so you can access them without a connection.',
        scope: 'trip',
        scopeId: selectedTrip.id,
        kind: 'offline',
        severity: 'info',
        createdAt: 0,
        source: 'local',
        meta: { tripId: selectedTrip.id, tripName: selectedTrip.name },
        cta: {
          label: 'Open offline settings',
          action: { type: 'callback', id: 'offline' },
        },
      });
    }

    if (raw.length === 0) return;

    let cancelled = false;
    hydrateTripAlertNotificationsWithPersistence(raw)
      .then((seed) => {
        if (cancelled) return;
        const signature = buildNotificationSeedSignature(seed);
        if (signature === lastChecklistSeedSignatureRef.current) return;
        lastChecklistSeedSignatureRef.current = signature;
        useNotificationStore.getState().upsertMany(seed);
      })
      .catch((error) => console.error('[Plan] Failed to hydrate visa/offline notifications', error));
    return () => {
      cancelled = true;
    };
  }, [
    selectedTrip?.id,
    selectedTrip?.name,
    selectedTrip?.destination,
    isSelectedTripReadOnly,
    profile?.citizenship,
    profile?.homeCountry,
    visaCheck.status,
    clearedAlertIdsSignature,
    isOfflineDownloaded,
    planAiPremium,
  ]);

  // Build notifications shown in the "Trip Alerts" modal.
  // Aligns with "needs attention" banner (readiness + visa + offline), without causing store loops.
  const tripNotifications = useMemo<Notification[]>(() => {
    const base = [...globalTripNotifications];
    const baseIds = new Set(base.map((n) => n.id));

    const readinessFromStore: Notification[] = [];
    for (const alert of readinessAlerts) {
      const id = alert.id;
      if (clearedAlertIds.has(id)) {
        continue;
      }
      const stored = storeItems[id];
      if (stored) {
        if (!stored.dismissedAt) {
          readinessFromStore.push(stored);
        }
      } else {
        // Fallback if not yet seeded.
        const [fallback] = tripAlertsToNotifications([alert], 0);
        readinessFromStore.push(fallback);
      }
    }

    const uniqueReadiness = readinessFromStore.filter((n) => !baseIds.has(n.id));
    uniqueReadiness.forEach((n) => baseIds.add(n.id));

    const extras: Notification[] = [];
    if (selectedTrip?.id) {
      const visaAlertId = `visa-${selectedTrip.id}`;
      const offlineAlertId = `offline-${selectedTrip.id}`;

      const needsVisa =
        (visaCheck.status === 'required' || visaCheck.status === 'check_required') &&
        !isSelectedTripReadOnly &&
        !clearedAlertIds.has(visaAlertId);

      const needsOffline =
        !isSelectedTripReadOnly &&
        planAiPremium &&
        !isOfflineDownloaded &&
        !clearedAlertIds.has(offlineAlertId);

      if (needsVisa && !baseIds.has(visaAlertId)) {
        const stored = storeItems[visaAlertId];
        if (!stored?.dismissedAt) {
          extras.push(
            stored ??
              ({
                id: visaAlertId,
                title: 'Visa check',
                body: getVisaStatusMessage(
                  visaCheck.status,
                  selectedTrip.destination || selectedTrip.name,
                  profile?.citizenship || profile?.homeCountry,
                ),
                scope: 'trip',
                scopeId: selectedTrip.id,
                kind: 'visa',
                severity: 'warning',
                createdAt: 0,
                source: 'local',
                meta: {
                  tripId: selectedTrip.id,
                  tripName: selectedTrip.name,
                },
                cta: {
                  label: 'Check visa requirements',
                  action: { type: 'callback', id: 'visa' },
                },
              } as Notification),
          );
          baseIds.add(visaAlertId);
        }
      }

      if (needsOffline && !baseIds.has(offlineAlertId)) {
        const stored = storeItems[offlineAlertId];
        if (!stored?.dismissedAt) {
          extras.push(
            stored ??
              ({
                id: offlineAlertId,
                title: 'Offline download',
                body: 'Download itinerary, bookings, and activities so you can access them without a connection.',
                scope: 'trip',
                scopeId: selectedTrip.id,
                kind: 'offline',
                severity: 'info',
                createdAt: 0,
                source: 'local',
                meta: {
                  tripId: selectedTrip.id,
                  tripName: selectedTrip.name,
                },
                cta: {
                  label: 'Open offline settings',
                  action: { type: 'callback', id: 'offline' },
                },
              } as Notification),
          );
          baseIds.add(offlineAlertId);
        }
      }
    }

    return [...base, ...uniqueReadiness, ...extras];
  }, [
    globalTripNotifications,
    readinessAlerts,
    storeItems,
    selectedTrip?.id,
    selectedTrip?.name,
    selectedTrip?.destination,
    isSelectedTripReadOnly,
    profile?.citizenship,
    profile?.homeCountry,
    visaCheck.status,
    clearedAlertIdsSignature,
    isOfflineDownloaded,
    planAiPremium,
  ]);

  const tripNotificationCount = useMemo(
    () =>
      tripNotifications.filter((n) => {
        if (n.dismissedAt) return false;
        if (
          n.kind === 'collaborators' &&
          selectedTrip &&
          isOwner &&
          !hasCollaborators(selectedTrip)
        ) {
          return true;
        }
        return n.readAt == null || n.readAt <= 0;
      }).length,
    [tripNotifications, selectedTrip, isOwner],
  );

  useEffect(() => {
    useNotificationStore.getState().setActivePlanUnreadCount(tripNotificationCount);
    return () => {
      useNotificationStore.getState().setActivePlanUnreadCount(undefined);
    };
  }, [tripNotificationCount]);

  // Use readiness alerts for scheduling push notification reminders
  useSmartCueNotifications(readinessAlerts, selectedTrip);
  
  const unreadNotificationIds = useMemo(() => {
    const ids = new Set<string>();
    for (const n of tripNotifications) {
      if (!n.readAt) ids.add(n.id);
    }
    return ids;
  }, [tripNotifications]);

  const filteredReadinessAlerts = useMemo(
    () =>
      readinessAlerts.filter(
        (a) =>
          (a.type === 'missing_transport' || a.type === 'missing_lodging') &&
          unreadNotificationIds.has(a.id),
      ),
    [readinessAlerts, unreadNotificationIds],
  );

  const ensureOnlineForNetworkAction = useCallback((): boolean => {
    if (!networkMonitor.shouldUseOfflineDataFallback()) return true;
    showInfo("You're offline. Reconnect to use this feature.");
    return false;
  }, [showInfo]);

  const handleLaunchBudget = useCallback(async () => {
    if (!selectedTripId) {
      showInfo('Select a trip before using the copilot.');
      return;
    }
    if (!ensureOnlineForNetworkAction()) return;

    setCopilotPendingType('budget');
    setCopilotLoading(true);
    try {
      const response = await requestBudgetCopilot({ tripId: selectedTripId });
      setCopilotResult({ type: 'budget', data: response });
    } catch (error) {
      console.error('[Wallet] Budget copilot failed', error);
      showError(error instanceof Error ? error.message : 'Unable to analyze budget right now.');
      setCopilotPendingType(null);
    } finally {
      setCopilotLoading(false);
      setCopilotPendingType(null);
    }
  }, [ensureOnlineForNetworkAction, selectedTripId, showError, showInfo]);

  // Hoist above callers to avoid TDZ issues (web): used by notification action handler config below.
  const openOfflineDownload = useCallback(() => {
    // Offline download is managed under Offline Mode settings (More -> Profile).
    // Use a one-shot storage flag so this works reliably even when navigating within nested tabs.
    AsyncStorage.setItem('@tripvio_open_offline_settings', '1').catch(() => {});
    router.push('/(tabs)/more?tab=profile');
  }, [router]);

  const openPlanOfflineUpgrade = useCallback(() => {
    openPremiumGate({
      feature: 'offline_mode',
      title: 'Offline Mode',
      message:
        'Offline Mode is a Premium feature. Upgrade to download maps, itineraries, and documents for offline use.',
      source: 'feature_gate',
    });
  }, [openPremiumGate]);

  const openVisaFallbackSearch = useCallback(() => {
    showInfo(
      'We couldn’t open an external visa page. Please use the in-app Visa Requirements checklist, or check the destination country’s official immigration website.',
    );
  }, [passportCountryForVisaCall, passportCountryForVisaLabel, selectedTrip?.destination, selectedTrip?.name, showInfo]);

  const closeTripAlertsModal = useCallback(() => {
    setShowTripAlertsModal(false);
  }, []);

  const openCollaborationModal = useCallback(() => {
    setShowCollaborationModal(true);
  }, []);

  const openVisaRequirements = useCallback(async () => {
    const trip = selectedTripRef.current;
    if (!trip) {
      showInfo('Select a trip first.');
      return;
    }
    if (!passportCountryForVisaCall) {
      Alert.alert(
        'Add your citizenship',
        'To check visa requirements, please add your citizenship in your profile.',
      );
      return;
    }

    setVisaModalVisible(true);
    setVisaModalError(null);

    // If destination country isn't known yet, prompt in the modal instead of calling the backend.
    if (computeTripNeedsDestinationCountryConfirm(trip)) {
      setVisaRequirement(null);
      setVisaModalLoading(false);
      setVisaModalError('Confirm the destination country to check visa requirements.');
      return;
    }

    const primaryDestination = trip.destination || trip.destinations?.[0]?.city || trip.name;
    const fetchKey = `${trip.id}:${primaryDestination}:${passportCountryForVisaCall}`;
    if (lastVisaFetchKeyRef.current === fetchKey && visaRequirementRef.current) {
      return;
    }

    setVisaModalLoading(true);
    try {
      const result = await getTravelRequirements(trip, passportCountryForVisaCall);
      if (!result) {
        setVisaRequirement(null);
        setVisaModalError(
          `Unable to fetch visa requirements right now.${formatVisaErrorHint()}`,
        );
        return;
      }

      // In production, never surface provider failure reasons. In dev, show a helpful debug hint.
      if (showStagingDebug && result.source === 'fallback' && result.debug?.failureReason) {
        console.log('[Visa] TravelRequirements failureReason:', result.debug.failureReason);
      }

      const destination = (trip.destination || trip.destinations?.[0]?.city || primaryDestination || '').toString();
      const destinationCountry = destination.split(',').map((p) => p.trim()).pop() || destination;
      const visaReq: VisaRequirement = {
        status: (result.visaStatus || 'unknown') as VisaRequirement['status'],
        visaRequired: !!result.visaRequired,
        stayDuration: result.stayDuration,
        notes: (result.visaNotes || '') + formatVisaProviderDebugHint(result),
        eVisaLink: result.eVisaLink,
        embassyLink: result.embassyLink,
        lastUpdated: result.lastUpdated,
        destination: destinationCountry,
        passportCountry: passportCountryForVisaLabel || passportCountryForVisaCall,
      };

      setVisaRequirement(visaReq);
      lastVisaFetchKeyRef.current = fetchKey;
    } catch (error) {
      console.error('[Plan] Error fetching visa requirements', error);
      setVisaRequirement(null);
      setVisaModalError(`Unable to fetch visa requirements right now.${formatVisaErrorHint(error)}`);
    } finally {
      setVisaModalLoading(false);
    }
  }, [
    formatVisaProviderDebugHint,
    formatVisaErrorHint,
    passportCountryForVisaCall,
    passportCountryForVisaLabel,
    showInfo,
    showStagingDebug,
  ]);

  const handleResumePollFinishedWithoutConnect = useCallback(async (_provider: InboxProviderId) => {
    oauthSyncIntentRef.current = false;
    setInboxSyncing(false);
    setShowEmailSyncStatus(false);
    await clearInboxOauthMarkers().catch(() => {});
  }, []);

  const notificationActionCallbacks = useMemo(
    () => ({
      onClose: closeTripAlertsModal,
      onOpenAddItem: openAddItem,
      onLaunchBudget: handleLaunchBudget,
      onOpenCollaboration: openCollaborationModal,
      onVisaSearch: () => {
        void openVisaRequirements();
      },
      onOpenOfflineDownload: openOfflineDownload,
      showInfo,
    }),
    [
      closeTripAlertsModal,
      openAddItem,
      handleLaunchBudget,
      openCollaborationModal,
      openVisaRequirements,
      openOfflineDownload,
      showInfo,
    ],
  );

  // Use shared notification action handler for consistent behavior
  const {
    handleAction: handleTripNotificationAction,
    handleDismiss: handleNotificationDismiss,
    handleDismissAll: handleNotificationDismissAll,
  } = useNotificationActionHandler({
    context: 'plan',
    tripId: selectedTripId ?? undefined,
    citizenship: profile?.citizenship || profile?.homeCountry,
    destination: selectedTrip?.destination || selectedTrip?.name,
    callbacks: notificationActionCallbacks,
  });

  const rememberClearedAlertIds = useCallback((ids: string[]) => {
    if (!ids.length) return;
    setClearedAlertIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const id of ids) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);

  const handleTripNotificationDismiss = useCallback(
    async (notification: Notification) => {
      await handleNotificationDismiss(notification);
      rememberClearedAlertIds([notification.id]);
    },
    [handleNotificationDismiss, rememberClearedAlertIds],
  );

  // Wrapper to handle notification dismiss by ID (for backwards compatibility)
  const handleClearNotification = useCallback(
    async (notificationId: string) => {
      const notif = tripNotifications.find((n) => n.id === notificationId);
      if (notif) {
        await handleTripNotificationDismiss(notif);
      }
    },
    [tripNotifications, handleTripNotificationDismiss],
  );

  // Wrapper for dismiss all
  const handleClearAllNotifications = useCallback(async () => {
    if (tripNotifications.length === 0) return;
    await handleNotificationDismissAll(tripNotifications);
    rememberClearedAlertIds(tripNotifications.map((notification) => notification.id));
  }, [tripNotifications, handleNotificationDismissAll, rememberClearedAlertIds]);

  // Subscribe to bookings for selected trip
  useEffect(() => {
    if (!selectedTripId) {
      setBookings([]);
      return;
    }

    // Reset when switching trips — otherwise offline hydration keeps `prev` from the last trip
    // (`prev.length > 0 ? prev : fromCache`) and the UI can show the wrong stay / bookings.
    setBookings([]);

    let cancelled = false;
    void getCachedTripData(selectedTripId).then((cached) => {
      if (cancelled || !cached?.bookings?.length) return;
      const fromCache = bookingsFromOfflineCache(cached.bookings as unknown[]);
      if (fromCache.length === 0) return;
      setBookings(fromCache);
    });

    const unsubscribe = subscribeToTripBookings(selectedTripId, (tripBookings) => {
      setBookings((prev) => {
        if (
          networkMonitor.shouldUseOfflineDataFallback() &&
          prev.length > 0 &&
          tripBookings.length === 0 &&
          prev.every((b) => b.tripId === selectedTripId)
        ) {
          return prev;
        }
        return tripBookings;
      });
    });

    return () => {
      cancelled = true;
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (error) {
          // Silently ignore cleanup errors - they're often due to Firestore internal state
        }
      }
    };
  }, [selectedTripId]);

  // Subscribe to expenses for selected trip (Wallet parity: offline pack + export use live data).
  useEffect(() => {
    if (!selectedTripId) {
      setExpenses([]);
      return;
    }

    setExpenses([]);

    let cancelled = false;
    void getCachedTripData(selectedTripId).then((cached) => {
      if (cancelled || !cached?.expenses?.length) return;
      const fromCache = expensesFromOfflineCache(cached.expenses as unknown[]);
      if (fromCache.length === 0) return;
      setExpenses((prev) =>
        prev.length > 0 ? mergeOfflineReceiptUrisIntoExpenses(prev, fromCache) : fromCache,
      );
    });

    const unsubscribe = subscribeToTripExpenses(selectedTripId, (tripExpenses) => {
      setExpenses((prev) => {
        if (
          networkMonitor.shouldUseOfflineDataFallback() &&
          prev.length > 0 &&
          tripExpenses.length === 0 &&
          prev.every((e) => e.tripId === selectedTripId)
        ) {
          return prev;
        }
        return tripExpenses;
      });
      void getCachedTripData(selectedTripId).then((cached) => {
        if (cancelled || !cached?.expenses?.length) return;
        const fromCache = expensesFromOfflineCache(cached.expenses as unknown[]);
        setExpenses((cur) => mergeOfflineReceiptUrisIntoExpenses(cur, fromCache));
      });
    });

    return () => {
      cancelled = true;
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch {
          // ignore
        }
      }
    };
  }, [selectedTripId]);

  // First-use hint: clarify core objects (Trip vs Plan). Deferred until trip
  // selected to avoid adding to the initial mount cascade.
  useEffect(() => {
    if (!firebaseUser?.uid || !selectedTripId) return;
    void (async () => {
      try {
        const seen = await AsyncStorage.getItem(tripPlanDefsKey);
        if (seen === 'true') return;
        await AsyncStorage.setItem(tripPlanDefsKey, 'true');
        showToastWithOptions({
          message: 'Quick definitions',
          type: 'info',
          details: 'Trip = confirmed travel\nPlan = ideas and saved places',
          actions: [
            {
              label: 'Got it',
              onPress: () => {},
              accessibilityLabel: 'Dismiss definitions',
            },
          ],
        });
      } catch {
        // Non-blocking: skip if storage fails
      }
    })();
  }, [firebaseUser?.uid, selectedTripId, showToastWithOptions]);

  const handleOauthConnected = useCallback(
    async (provider: InboxProviderId) => {
      setInboxProvider(provider);
      const shouldStartSync = oauthSyncIntentRef.current;
      oauthSyncIntentRef.current = false;
      if (shouldStartSync) {
        showInfo('Connected, starting sync...');
        setShowEmailSyncStatus(true);
      } else {
        showInfo('Connected.');
      }
    },
    [showInfo],
  );

  const oauthProjectId =
    (Constants.expoConfig?.extra as any)?.firebaseConfig?.projectId ||
    process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID;

  const canResumeOauth = useCallback(() => Boolean(selectedTripIdRef.current), []);
  const checkStatusBypass = useCallback(
    (p: InboxProviderId) => refreshInboxStatus(p, { bypassCache: true }),
    [refreshInboxStatus],
  );

  useInboxOauthResume({
    userId: firebaseUser?.uid,
    enabled: Boolean(selectedTripId),
    canResume: canResumeOauth,
    checkStatus: checkStatusBypass,
    onConnected: handleOauthConnected,
    onResumePollFinishedWithoutConnect: handleResumePollFinishedWithoutConnect,
  });

  useInboxOauthPostMessage({
    userId: firebaseUser?.uid,
    enabled: Boolean(selectedTripId),
    canResume: canResumeOauth,
    projectId: typeof oauthProjectId === 'string' ? oauthProjectId : undefined,
    checkStatus: checkStatusBypass,
    onConnected: handleOauthConnected,
  });

  const resyncPlanItineraryFromServer = useCallback(async () => {
    const tripId = selectedTripIdRef.current;
    if (!tripId) return;
    try {
      const [items, refreshedTrip] = await Promise.all([
        fetchTripItineraryItems(tripId, { fromServer: true }),
        getTripById(tripId, { fromServer: true }),
      ]);
      // Focus / AppState resync can finish after the user switched trips; never apply stale rows.
      if (selectedTripIdRef.current !== tripId) {
        return;
      }
      if (refreshedTrip && firebaseUser?.uid) {
        notifyLiveTripUpdated(firebaseUser.uid, refreshedTrip);
      }
      setItineraryItems((prev) => {
        const rawNext = Array.isArray(items) ? items : [];
        reconcilePendingDeletedItineraryItems(rawNext);
        const next = filterPendingDeletedItineraryItems(rawNext);
        const safePrev = Array.isArray(prev) ? prev : [];
        if (
          networkMonitor.shouldUseOfflineDataFallback() &&
          safePrev.length > 0 &&
          next.length === 0
        ) {
          return filterPendingDeletedItineraryItems(safePrev);
        }
        const prevTripOk = safePrev.every((i) => i.tripId === tripId || !i.tripId);
        if (
          !networkMonitor.shouldUseOfflineDataFallback() &&
          safePrev.length > 0 &&
          next.length === 0 &&
          planItineraryHasSeenNonEmptyRef.current &&
          prevTripOk
        ) {
          return filterPendingDeletedItineraryItems(safePrev);
        }
        return next;
      });
    } catch {
      // Prefer the live listener; authoritative fetch can fail transiently offline.
    }
  }, [filterPendingDeletedItineraryItems, reconcilePendingDeletedItineraryItems]);

  useFocusEffect(
    useCallback(() => {
      void resyncPlanItineraryFromServer();
    }, [resyncPlanItineraryFromServer]),
  );

  useEffect(() => {
    const onAppStateChange = (nextState: AppStateStatus) => {
      if (nextState !== 'active' || !isPlanScreenFocused) return;
      void resyncPlanItineraryFromServer();
    };
    const sub = AppState.addEventListener('change', onAppStateChange);
    return () => sub.remove();
  }, [isPlanScreenFocused, resyncPlanItineraryFromServer]);

  // Subscribe to itinerary items for selected trip
  useEffect(() => {
    if (!selectedTripId) {
      if (__DEV__) console.log('[Wallet] No trip selected, clearing itinerary items');
      planItineraryHasSeenNonEmptyRef.current = false;
      planItineraryEmptySinceRef.current = null;
      planItineraryOnlineVerifyInFlightRef.current = false;
      pendingDeletedItineraryIdsRef.current.clear();
      setItineraryItems([]);
      setItineraryLoading(false);
      return;
    }

    if (__DEV__) console.log('[Wallet] Setting up itinerary subscription for trip:', selectedTripId);
    const tripIdAtStart = selectedTripId;
    planItineraryHasSeenNonEmptyRef.current = false;
    planItineraryEmptySinceRef.current = null;
    planItineraryOnlineVerifyInFlightRef.current = false;
    pendingDeletedItineraryIdsRef.current.clear();
    let subscriptionActive = true;
    const receivedRef = { current: false };
    const startedAt = Date.now();

    // Avoid syncing to [] immediately — wait for offline pack read so cached rows can paint first.
    setItineraryLoading(true);

    void (async () => {
      try {
        const cached = await getCachedTripData(tripIdAtStart);
        if (!subscriptionActive) return;
        const fromCache = itineraryItemsFromOfflineCache(cached?.itinerary ?? [], tripIdAtStart);
        if (fromCache.length > 0) {
          if (__DEV__) {
            console.log('[Plan] Hydrated itinerary from offline pack:', fromCache.length);
          }
          planItineraryHasSeenNonEmptyRef.current = true;
          planItineraryEmptySinceRef.current = null;
          setItineraryItems(filterPendingDeletedItineraryItems(fromCache));
          receivedRef.current = true;
          setItineraryLoading(false);
        } else {
          setItineraryItems([]);
        }
      } catch {
        if (subscriptionActive) {
          setItineraryItems([]);
        }
      }
    })();

    const unsubscribe = subscribeToTripItineraryItems(tripIdAtStart, (items) => {
      if (!subscriptionActive) {
        if (__DEV__) console.warn('[Wallet] Received items after subscription cleanup, ignoring');
        return;
      }
      if (__DEV__) {
        console.log(
          '[Wallet] Received itinerary items:',
          items.length,
          items.map((i) => ({ id: i.id, title: i.title })),
        );
      }
      setItineraryItems((prev) => {
        const next = Array.isArray(items) ? items : [];
        const safePrev = Array.isArray(prev) ? prev : [];
        const offlineFallback = networkMonitor.shouldUseOfflineDataFallback();

        if (next.length > 0) {
          planItineraryHasSeenNonEmptyRef.current = true;
          planItineraryEmptySinceRef.current = null;
          planItineraryOnlineVerifyInFlightRef.current = false;
          reconcilePendingDeletedItineraryItems(next);
          return filterPendingDeletedItineraryItems(next);
        }

        const prevTripOk = safePrev.every((i) => i.tripId === tripIdAtStart || !i.tripId);
        const isOnlineTransientEmpty =
          !offlineFallback &&
          next.length === 0 &&
          safePrev.length > 0 &&
          planItineraryHasSeenNonEmptyRef.current &&
          prevTripOk;

        if (isOnlineTransientEmpty) {
          const now = Date.now();
          if (planItineraryEmptySinceRef.current == null) planItineraryEmptySinceRef.current = now;
          const emptyForMs = now - planItineraryEmptySinceRef.current;
          if (emptyForMs < PLAN_ITINERARY_EMPTY_SNAPSHOT_GRACE_MS) {
            return safePrev;
          }
          if (!planItineraryOnlineVerifyInFlightRef.current) {
            planItineraryOnlineVerifyInFlightRef.current = true;
            void fetchTripItineraryItems(tripIdAtStart)
              .then((verifiedItems) => {
                if (!subscriptionActive || selectedTripIdRef.current !== tripIdAtStart) return;
                if (!Array.isArray(verifiedItems)) return;
                if (verifiedItems.length === 0) {
                  planItineraryHasSeenNonEmptyRef.current = false;
                  planItineraryEmptySinceRef.current = null;
                  setItineraryItems([]);
                  return;
                }
                planItineraryHasSeenNonEmptyRef.current = true;
                planItineraryEmptySinceRef.current = null;
                setItineraryItems((current) => {
                  if (!subscriptionActive || selectedTripIdRef.current !== tripIdAtStart) return current;
                  reconcilePendingDeletedItineraryItems(verifiedItems);
                  const filtered = filterPendingDeletedItineraryItems(verifiedItems);
                  if (current.length !== filtered.length) return filtered;
                  const ck = current.map((i) => `${i.id}:${i.updatedAt ?? ''}`).join(',');
                  const nk = filtered.map((i) => `${i.id}:${i.updatedAt ?? ''}`).join(',');
                  return ck === nk ? current : filtered;
                });
              })
              .finally(() => {
                planItineraryOnlineVerifyInFlightRef.current = false;
              });
          }
          return safePrev;
        }

        if (offlineFallback && safePrev.length > 0 && next.length === 0 && prevTripOk) {
          return safePrev;
        }

        return next;
      });

      // Clear loading state after first data received (whether from cache or server)
      if (!receivedRef.current) {
        receivedRef.current = true;
        setItineraryLoading(false);
      }
    });

    const cacheEscapeTimer = setTimeout(() => {
      if (!subscriptionActive || receivedRef.current) {
        return;
      }
      const cached = itineraryItemsRef.current;
      if (cached.length > 0) {
        receivedRef.current = true;
        setItineraryLoading(false);
        if (__DEV__) {
          const elapsedMs = Date.now() - startedAt;
          console.warn(`[Plan] Itinerary loading unblocked from in-memory cache after ${elapsedMs}ms`);
        }
      }
    }, 4000);

    const watchdogTimer = setTimeout(() => {
      if (!subscriptionActive || receivedRef.current) {
        return;
      }

      receivedRef.current = true;
      setItineraryLoading(false);
      if (__DEV__) {
        const elapsedMs = Date.now() - startedAt;
        console.warn(`[Plan] Itinerary loading watchdog fired after ${elapsedMs}ms`);
      }

      const itineraryStaleFingerprint = (item: ItineraryItem): string =>
        `${item.id}:${item.startDate.getTime()}:${item.endDate?.getTime?.() ?? ''}:${item.updatedAt?.getTime?.() ?? ''}`;

      // Best-effort direct fetch (server-backed when online) to recover stale UI.
      void fetchTripItineraryItems(tripIdAtStart, { fromServer: true })
        .then((items) => {
          if (!subscriptionActive || !Array.isArray(items)) {
            return;
          }
          setItineraryItems((prev) => {
            reconcilePendingDeletedItineraryItems(items);
            const filtered = filterPendingDeletedItineraryItems(items);
            if (prev.length !== filtered.length) {
              return filtered;
            }
            const prevKey = prev.map(itineraryStaleFingerprint).join(',');
            const nextKey = filtered.map(itineraryStaleFingerprint).join(',');
            return prevKey === nextKey ? prev : filtered;
          });
        })
        .catch(() => {
          // Ignore - watchdog already unblocked the UI.
        });
    }, 12000);

    return () => {
      subscriptionActive = false;
      clearTimeout(cacheEscapeTimer);
      clearTimeout(watchdogTimer);
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (error) {
          // Silently ignore cleanup errors - they're often due to Firestore internal state
        }
      }
    };
  }, [selectedTripId]);

  // Refresh the offline trip pack while online (itinerary + bookings + expenses + receipt files).
  // Premium only: free users should not get an implicit full trip pack (same UX as explicit "Download for offline").
  // IMPORTANT: read itineraryItemsRef/bookingsRef/expensesRef inside the delayed callback — not when the
  // effect runs. Otherwise the 5s timer can flush a stale snapshot and overwrite a fresh pack written
  // by realign/shift (user sees a fix, then reopen shows the warning again).
  useEffect(() => {
    const trip = selectedTripRef.current;
    if (!trip?.id || !networkMonitor.isOnline()) return;
    if (itineraryLoading) return;
    if (!planAiPremium) return;
    const timeoutId = setTimeout(() => {
      void (async () => {
        const t = selectedTripRef.current;
        if (!t?.id || !networkMonitor.isOnline()) return;
        await cacheTripPackForOffline(t, {
          itinerary: itineraryItemsRef.current,
          bookings: bookingsRef.current,
          expenses: expensesRef.current,
        });
        const cached = await getCachedTripData(t.id);
        if (!cached?.expenses?.length) return;
        const fromCache = expensesFromOfflineCache(cached.expenses as unknown[]);
        setExpenses((cur) => mergeOfflineReceiptUrisIntoExpenses(cur, fromCache));
      })();
    }, 5000);
    return () => clearTimeout(timeoutId);
  }, [selectedTrip?.id, itineraryLoading, expenses, bookings, itineraryItems, planAiPremium]);

  // Widget / App Intent opens Plan with `day=…` or `focus=today`. Apply once, then drop params so
  // day pills remain interactive (sticky params were snapping selection back on every tap).
  useEffect(() => {
    if (!routeFocusedDateKey) {
      handledWidgetRouteFocusRef.current = null;
      return;
    }
    if (!selectedTripId) return;
    if (paramTripId && selectedTripId !== paramTripId) return;
    if (itineraryLoading) return;

    const signature = `${selectedTripId}:${routeFocusedDateKey}`;
    if (handledWidgetRouteFocusRef.current === signature) return;
    handledWidgetRouteFocusRef.current = signature;

    if (isWidgetRouteFocusParam(params as Record<string, unknown>)) {
      consumeWidgetRouteParams(router, {
        keepTripId: selectedTripId,
        keepItemId: paramItemId,
      });
    }
  }, [
    itineraryLoading,
    paramItemId,
    paramTripId,
    params,
    routeFocusedDateKey,
    router,
    selectedTripId,
  ]);

  // Deep-link from Maps: open a specific itinerary item in the form/modal.
  useEffect(() => {
    if (!paramItemId) return;
    if (!selectedTripId) return;
    if (itineraryLoading) return;
    if (!itineraryItems || itineraryItems.length === 0) return;
    if (openedItemIdRef.current === paramItemId) return;

    const item = itineraryItems.find((i) => i.id === paramItemId) ?? null;
    if (!item) {
      // Avoid spamming; only show once per itemId.
      openedItemIdRef.current = paramItemId;
      showInfo?.('Could not find that itinerary item. It may have been deleted or moved.');
      return;
    }

    openedItemIdRef.current = paramItemId;
    setEditingItineraryItem(item);
    setSelectedDateForForm(item.startDate);
    setShowItineraryForm(true);
  }, [paramItemId, selectedTripId, itineraryItems, itineraryLoading, showInfo]);

  // Open item by id when it appears (e.g. after add from Ideas success "View")
  useEffect(() => {
    if (!pendingOpenItemId || !selectedTripId || !itineraryItems?.length) return;
    const item = itineraryItems.find((i) => i.id === pendingOpenItemId) ?? null;
    if (!item) return;
    openedItemIdRef.current = pendingOpenItemId;
    setEditingItineraryItem(item);
    setSelectedDateForForm(item.startDate);
    setShowItineraryForm(true);
    setPendingOpenItemId(null);
  }, [pendingOpenItemId, selectedTripId, itineraryItems]);

  // Auto-dismiss "Added to itinerary" success modal after 2s
  useEffect(() => {
    if (!addedItemSuccess) return;
    if (addedItemSuccessTimeoutRef.current) {
      clearTimeout(addedItemSuccessTimeoutRef.current);
      addedItemSuccessTimeoutRef.current = null;
    }
    addedItemSuccessTimeoutRef.current = setTimeout(() => {
      addedItemSuccessTimeoutRef.current = null;
      setAddedItemSuccess(null);
    }, 2000);
    return () => {
      if (addedItemSuccessTimeoutRef.current) {
        clearTimeout(addedItemSuccessTimeoutRef.current);
        addedItemSuccessTimeoutRef.current = null;
      }
    };
  }, [addedItemSuccess]);

  // Quick-add template via route params (used by Today hub / quick actions).
  // IMPORTANT: do not depend on `selectedTrip` object identity — Firestore snapshots replace the
  // trips array constantly; re-firing this effect on every snapshot caused setState loops (max update depth).
  useEffect(() => {
    const template = typeof params.template === 'string' ? params.template : undefined;
    if (params.action !== 'add' || !template) {
      quickAddRouteHandledRef.current = null;
      return;
    }
    if (!selectedTripId) return;
    const trip = selectedTripRef.current;
    if (!trip) return;

    const signature = `add|${template}|${selectedTripId}`;
    if (quickAddRouteHandledRef.current === signature) {
      return;
    }
    quickAddRouteHandledRef.current = signature;

    const today = new Date();
    const start = safeTripStartDate ? new Date(safeTripStartDate) : today;
    const end = safeTripEndDate ? new Date(safeTripEndDate) : new Date(start);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    const chosen = today < start ? start : today > end ? start : today;
    const chosenDate = coerceDate(chosen) ?? safeTripStartDate ?? new Date();
    setPendingQuickTemplate(template);
    setSelectedDateForForm(chosenDate);
    setShowItineraryForm(true);
  }, [params.action, params.template, selectedTripId, safeTripEndDate, safeTripStartDate]);

  // Note: AI suggestions are now only generated when user clicks the "AI Suggestions" button
  // The auto-fetch useEffect has been removed to prevent automatic generation

  const handleApplySuggestions = useCallback(async () => {
    if (!firebaseUser?.uid || !aiSuggestions || !selectedTripId) {
      Alert.alert('Error', 'Please sign in and select a trip.');
      return;
    }
    if (!selectedTripRef.current) {
      Alert.alert('Error', 'Please sign in and select a trip.');
      return;
    }

    if (!aiSuggestions.suggestions || aiSuggestions.suggestions.length === 0) {
      Alert.alert('No Suggestions', 'There are no suggestions to add.');
      return;
    }

    setApplyingSuggestions(true);
    try {
      // Filter out suggestions that already exist in the itinerary using fuzzy matching
      const { filterDuplicateSuggestions } = await import('@/utils/suggestionHelpers');
      const newSuggestions = filterDuplicateSuggestions(
        aiSuggestions.suggestions,
        itineraryItems
      );

      if (newSuggestions.length === 0) {
        Alert.alert(
          'Already Added',
          'All suggestions are already in your itinerary.'
        );
        setApplyingSuggestions(false);
        return;
      }

      if (newSuggestions.length < aiSuggestions.suggestions.length) {
        const skippedCount = aiSuggestions.suggestions.length - newSuggestions.length;
        console.log(`[Wallet] Skipping ${skippedCount} duplicate suggestion(s)`);
      }

      console.log(`[Wallet] Applying ${newSuggestions.length} suggestion(s) to itinerary`);
      const result = await applyItinerarySuggestions({
        tripId: selectedTripId,
        itineraryItems: newSuggestions,
      });
      
      console.log('[Wallet] Apply suggestions result:', result);
      
      const message = newSuggestions.length === aiSuggestions.suggestions.length
        ? `${newSuggestions.length} suggestion${newSuggestions.length !== 1 ? 's' : ''} added to itinerary`
        : `${newSuggestions.length} suggestion${newSuggestions.length !== 1 ? 's' : ''} added. ${aiSuggestions.suggestions.length - newSuggestions.length} already in itinerary`;
      
      showSuccess(message);
      setAiSuggestions(null); // Clear suggestions after applying
    } catch (error: any) {
      console.error('[Wallet] Failed to apply itinerary suggestions', error);
      const errorMessage = error?.message || 'Unable to save suggestions. Please try again later.';
      showError(errorMessage);
    } finally {
      setApplyingSuggestions(false);
    }
  }, [aiSuggestions, selectedTripId, firebaseUser?.uid, itineraryItems, showSuccess, showError]);

  // Helper function to infer category from title and description
  const inferItemCategory = useCallback((title: string, description: string): ItineraryItem['category'] => {
    const text = `${title} ${description}`.toLowerCase();

    // Flight
    if (/\b(flight|airline|airport|departure|arrival|boarding|airplane|plane)\b/.test(text)) {
      return 'flight';
    }

    // Hotel
    if (/\b(hotel|accommodation|lodging|check-in|check-out|resort|hostel|airbnb|booking|stay)\b/.test(text)) {
      return 'hotel';
    }

    // Default to activity
    return 'activity';
  }, []);

  const tripOptions = useMemo<SelectOption[]>(() => buildTripSelectOptions(trips), [trips]);

  const handleTripChange = (tripId: string) => {
    setSelectedTripId(tripId);
    router.setParams({ tripId });
  };

  const handleInboxSync = useCallback(async () => {
    if (!firebaseUser?.uid || !selectedTripId) {
      Alert.alert('Select a trip', 'Please select a trip before syncing your inbox.');
      return;
    }

    // Persist intent to use this provider for future screens
    setLastUsedInboxProvider(firebaseUser.uid, inboxProvider).catch(() => {});

    setInboxSyncing(true);
    console.log('[Wallet] Inbox sync start', { provider: inboxProvider });
    let popup: Window | null = null;

    try {
      const status = await refreshInboxStatus(inboxProvider);

      if (inboxProvider === 'icloud' && (!status || !status.connected)) {
        console.log('[Wallet] IMAP credentials required');
        setInboxSyncing(false);
        setImapModalVisible(true);
        return;
      }

      if (status?.requiresAuth) {
        console.log('[Wallet] Inbox requires auth, launching OAuth', { provider: inboxProvider });
        oauthSyncIntentRef.current = true;
        await setInboxOauthPending(inboxProvider).catch(() => {});
        // Open a placeholder popup just before starting auth (web) to avoid popup blocking.
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          try {
            popup = window.open('about:blank', '_blank');
          } catch {
            popup = null;
          }
        }

        const authResponse = await initiateInboxAuth(inboxProvider);
        if (authResponse?.authUrl) {
          if (Platform.OS === 'web') {
            if (popup) {
              try {
                popup.location.href = authResponse.authUrl;
              } catch {
                window.location.href = authResponse.authUrl;
              }
            } else {
              window.location.href = authResponse.authUrl;
            }
          } else {
            const canOpen = await Linking.canOpenURL(authResponse.authUrl);
            if (canOpen) {
              const redirectUrl = Linking.createURL(
                inboxProvider === 'gmail' ? 'gmail-callback' : 'outlook-callback',
              );
              const result = await WebBrowser.openAuthSessionAsync(authResponse.authUrl, redirectUrl);
              completeOAuthWebSession();
              if (result.type !== 'success') {
                Alert.alert('Authentication cancelled', 'Return to Tripvio and try connecting again.');
                setInboxSyncing(false);
                return;
              }
            } else {
              Alert.alert('Connect inbox', 'Unable to open the authorization page automatically.');
              setInboxSyncing(false);
              return;
            }
          }

          Alert.alert(
            'Continue in browser',
            authResponse.message || 'Complete the connection flow and return to Tripvio.'
          );
        } else {
          popup?.close?.();
          Alert.alert('Connect inbox', authResponse?.message || 'Unable to start authorization for this provider.');
        }
        setInboxSyncing(false);
        return;
      }

      // Already connected or no auth needed: show sync status modal
      oauthSyncIntentRef.current = false;
      setShowEmailSyncStatus(true);
      setInboxSyncing(false);
    } catch (error) {
      popup?.close?.();
      oauthSyncIntentRef.current = false;
      console.error('Inbox sync error:', error);
      Alert.alert(
        'Inbox sync failed',
        error instanceof Error ? error.message : 'Failed to sync inbox. Please try again.',
      );
      setInboxSyncing(false);
    }
  }, [firebaseUser?.uid, selectedTripId, inboxProvider, refreshInboxStatus]);

  const handleImapSave = useCallback(
    async (settings: {
      host: string;
      username: string;
      password: string;
      port: number;
      secure: boolean;
      mailbox: string;
    }) => {
      try {
        await saveImapSettings({
          ...settings,
          providerId: 'icloud',
        });
        setImapModalVisible(false);
        await refreshInboxStatus('icloud');
        Alert.alert('Mailbox connected', 'Your iCloud/IMAP credentials were saved.');
        handleInboxSync();
      } catch (error) {
        console.error('[Wallet] Failed to save IMAP settings', error);
        Alert.alert(
          'Unable to save credentials',
          error instanceof Error ? error.message : 'Please double-check your mailbox details and try again.',
        );
      }
    },
    [handleInboxSync, refreshInboxStatus],
  );

  const handleOpenSmartUpload = useCallback(() => {
    if (!firebaseUser?.uid) {
      Alert.alert('Sign in required', 'Please sign in to upload documents.');
      return;
    }
    setSmartUploadVisible(true);
  }, [firebaseUser?.uid]);

  const openDocs = useCallback(() => {
    const docsTarget = getDocumentsTabTarget('merged', { tripId: selectedTripId ?? undefined });
    router.push(docsTarget);
  }, [router, selectedTripId]);

  const openTravelSafety = useCallback(() => {
    router.push({
      pathname: '/(tabs)/travel',
      params: { tripId: selectedTripId ?? undefined, tab: 'safety' },
    });
  }, [router, selectedTripId]);

  const openTravelMaps = useCallback(() => {
    router.push({
      pathname: '/(tabs)/travel',
      params: { tripId: selectedTripId ?? undefined, tab: 'maps' },
    });
  }, [router, selectedTripId]);

  const openBudget = useCallback(() => {
    router.push({
      pathname: '/(tabs)/wallet',
      params: { tripId: selectedTripId ?? undefined },
    });
  }, [router, selectedTripId]);

  const handleQuickAddTemplate = useCallback(
    (template: 'meal' | 'nap' | 'buffer' | 'restroom' | 'play') => {
      if (!selectedTripId || !tripsRef.current.some((t) => t.id === selectedTripId)) return;
      setPendingQuickTemplate(template);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tripStart = safeTripStartDate ? new Date(safeTripStartDate) : today;
      tripStart.setHours(0, 0, 0, 0);
      const tripEnd = safeTripEndDate ? new Date(safeTripEndDate) : new Date(tripStart);
      tripEnd.setHours(23, 59, 59, 999);
      const chosen = today < tripStart ? tripStart : today > tripEnd ? tripStart : today;
      const chosenDate = coerceDate(chosen) ?? safeTripStartDate ?? new Date();
      setSelectedDateForForm(chosenDate);
      setEditingItineraryItem(null);
      setShowItineraryForm(true);
    },
    [safeTripEndDate, safeTripStartDate, selectedTripId],
  );

  const handleOpenEmailProvider = useCallback(() => {
    setShowProviderModal(true);
  }, []);

  const handleChangeProvider = useCallback(() => {
    setShowProviderModal(true);
  }, []);

  const handleConnectProvider = useCallback(async (provider: InboxProviderId) => {
    if (!firebaseUser?.uid || !selectedTripId) {
      showInfo('Select a trip before connecting email.');
      return;
    }

    let popup: Window | null = null;
    try {
      // Check connection status
      const status = await refreshInboxStatus(provider);
      setLastUsedInboxProvider(firebaseUser.uid, provider).catch(() => {});
      
      // Handle iCloud/IMAP - show settings modal if not connected
      if (provider === 'icloud' && (!status || !status.connected)) {
        setImapModalVisible(true);
        return;
      }

      // If requires auth, initiate authentication
      if (status?.requiresAuth || !status?.connected) {
        oauthSyncIntentRef.current = true;
        await setInboxOauthPending(provider).catch(() => {});
        // Open a placeholder popup just before starting auth (web) to avoid popup blocking.
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          try {
            popup = window.open('about:blank', '_blank');
          } catch {
            popup = null;
          }
        }

        const authResponse = await initiateInboxAuth(provider);
        if (authResponse?.authUrl) {
          if (Platform.OS === 'web') {
            if (popup) {
              try {
                popup.location.href = authResponse.authUrl;
              } catch {
                window.location.href = authResponse.authUrl;
              }
            } else {
              window.location.href = authResponse.authUrl;
            }
            showInfo(authResponse.message || 'Continue in browser to finish connecting your inbox.');
          } else {
            popup?.close?.();
            const canOpen = await Linking.canOpenURL(authResponse.authUrl);
            if (canOpen) {
              await Linking.openURL(authResponse.authUrl);
              showInfo(authResponse.message || 'Continue in browser to finish connecting your inbox.');
            } else {
              showError('Unable to open the authorization page automatically.');
            }
          }
        } else {
          oauthSyncIntentRef.current = false;
          popup?.close?.();
          showError(authResponse?.message || 'Unable to start authorization for this provider.');
        }
        return;
      }

      // Already connected: nothing else to do here
    } catch (error) {
      popup?.close?.();
      oauthSyncIntentRef.current = false;
      console.error('[PlanScreen] Error connecting provider:', error);
      showError('Unable to connect email provider. Please try again.');
    }
  }, [firebaseUser?.uid, selectedTripId, refreshInboxStatus, showError, showInfo]);

  const handleDisconnectProvider = useCallback(async (provider: InboxProviderId) => {
    if (!firebaseUser?.uid) {
      return;
    }

    try {
      const ok = await disconnectEmailProvider(provider);
      if (!ok) {
        throw new Error('Disconnect operation returned unsuccessful response');
      }

      await useInboxConnectionsStore.getState().refreshFresh();
      const snap = useInboxConnectionsStore.getState().connections;
      setInboxConnections({
        gmail: snap.gmail,
        outlook: snap.outlook,
        icloud: snap.icloud,
      });

      if (inboxProvider === provider) {
        const fallback =
          (['gmail', 'outlook', 'icloud'] as InboxProviderId[]).find(
            (p) => p !== provider && snap[p],
          ) ?? 'gmail';
        setInboxProvider(fallback);
      }

      showSuccess(`${PROVIDER_LABELS[provider]} disconnected.`);
    } catch (error) {
      console.error('[PlanScreen] Error disconnecting provider:', error);
      showError('Unable to disconnect this email provider. Please try again.');
      throw error;
    }
  }, [firebaseUser?.uid, inboxProvider, refreshInboxStatus, showError, showSuccess]);

  const handleSyncAll = useCallback(async () => {
    if (!firebaseUser?.uid || !selectedTripId) return;

    const providers: InboxProviderId[] = ['gmail', 'outlook', 'icloud'];
    const connectedProviders: InboxProviderId[] = [];

    // Check which providers are connected
    for (const provider of providers) {
      const status = await refreshInboxStatus(provider);
      if (status?.connected) {
        connectedProviders.push(provider);
      }
    }

    if (connectedProviders.length === 0) {
      showInfo('Connect at least one email provider first.');
      return;
    }

    setInboxSyncing(true);
    try {
      let totalBookings = 0;
      for (const provider of connectedProviders) {
        try {
          const result = await syncInboxBookings(selectedTripId, provider);
          if (result.status === 'ok' && result.data) {
            totalBookings += result.data.length;
          }
        } catch (error) {
          console.error(`[PlanScreen] Error syncing ${provider}:`, error);
        }
      }

      showSuccess(
        `Synced ${connectedProviders.length} provider${connectedProviders.length !== 1 ? 's' : ''} and imported ${totalBookings} booking${totalBookings !== 1 ? 's' : ''}`
      );
    } catch (error) {
      console.error('[PlanScreen] Error syncing all providers:', error);
      showError('Failed to sync all providers. Please try again.');
    } finally {
      setInboxSyncing(false);
    }
  }, [firebaseUser?.uid, selectedTripId, refreshInboxStatus, showError, showInfo, showSuccess]);

  const handleSelectProvider = useCallback(async (value: InboxProviderId) => {
    if (!firebaseUser?.uid || !selectedTripId) {
      showInfo('Select a trip before connecting email.');
      setShowProviderModal(false);
      return;
    }

    try {
      setInboxProvider(value);
      setShowProviderModal(false);
      setLastUsedInboxProvider(firebaseUser.uid, value).catch(() => {});
      
      // Check connection status
      const status = await refreshInboxStatus(value);
      
      // Handle iCloud/IMAP - show settings modal if not connected
      if (value === 'icloud' && (!status || !status.connected)) {
        setImapModalVisible(true);
        return;
      }

      // If requires auth, initiate authentication
      if (status?.requiresAuth || !status?.connected) {
        console.log('[PlanScreen] Inbox requires auth, launching OAuth', { provider: value });
        oauthSyncIntentRef.current = true;
        await setInboxOauthPending(value).catch(() => {});

        // Open a placeholder popup just before starting auth (web) to avoid popup blocking.
        let popup: Window | null = null;
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          try {
            popup = window.open('about:blank', '_blank');
          } catch {
            popup = null;
          }
        }

        const authResponse = await initiateInboxAuth(value);
        if (authResponse?.authUrl) {
          if (Platform.OS === 'web') {
            if (popup) {
              try {
                popup.location.href = authResponse.authUrl;
              } catch {
                window.location.href = authResponse.authUrl;
              }
            } else {
              window.location.href = authResponse.authUrl;
            }
            showInfo(authResponse.message || 'Continue in browser to finish connecting your inbox.');
          } else {
            const canOpen = await Linking.canOpenURL(authResponse.authUrl);
            if (canOpen) {
              await Linking.openURL(authResponse.authUrl);
              showInfo(authResponse.message || 'Continue in browser to finish connecting your inbox.');
            } else {
              showError('Unable to open the authorization page automatically.');
            }
          }
        } else {
          oauthSyncIntentRef.current = false;
          showError(authResponse?.message || 'Unable to start authorization for this provider.');
        }
        return;
      }

      // If connected, sync inbox
      if (status?.connected) {
        setInboxSyncing(true);
        try {
          const result = await syncInboxBookings(selectedTripId, value);
          if (result.status === 'error') {
            showError(result.message || 'Unable to sync inbox right now.');
          } else {
            const bookingsImported = result.data?.length ?? 0;
            if (bookingsImported > 0) {
              showSuccess(`Imported ${bookingsImported} booking${bookingsImported === 1 ? '' : 's'} from ${PROVIDER_LABELS[value]}`);
            } else {
              showInfo(`No new bookings found in ${PROVIDER_LABELS[value]}`);
            }
          }
        } catch (error) {
          console.error('Inbox sync error:', error);
          showError(
            error instanceof Error ? error.message : 'Failed to sync inbox. Please try again.'
          );
        } finally {
          setInboxSyncing(false);
        }
      }
    } catch (error) {
      oauthSyncIntentRef.current = false;
      console.error('[PlanScreen] Error selecting provider:', error);
      showError('Unable to connect email provider. Please try again.');
    }
  }, [firebaseUser?.uid, selectedTripId, refreshInboxStatus, showError, showInfo, showSuccess]);

  const handleLaunchRebooking = useCallback(async () => {
    if (!selectedTripId) {
      showInfo('Select a trip before using the copilot.');
      return;
    }
    if (!ensureOnlineForNetworkAction()) return;
    const flightBooking = bookings.find((booking) => booking.type === 'flight');
    if (!flightBooking) {
      showInfo('Add a flight booking before requesting rebooking help.');
      return;
    }

    setCopilotPendingType('rebooking');
    setCopilotLoading(true);
    try {
      const response = await requestRebookingSupport({
        tripId: selectedTripId,
        bookingId: flightBooking.id,
      });
      setCopilotResult({type: 'rebooking', data: response});
    } catch (error) {
      console.error('[Wallet] Rebooking copilot failed', error);
      showError(error instanceof Error ? error.message : 'Unable to suggest rebooking options right now.');
      setCopilotPendingType(null);
    } finally {
      setCopilotLoading(false);
      setCopilotPendingType(null);
    }
  }, [ensureOnlineForNetworkAction, selectedTripId, bookings, showError, showInfo]);

  const handleLaunchDisruption = useCallback(async () => {
    if (!selectedTripId) {
      showInfo('Select a trip before using the copilot.');
      return;
    }
    if (!ensureOnlineForNetworkAction()) return;

    setCopilotPendingType('disruption');
    setCopilotLoading(true);
    try {
      const response = await requestDisruptionInsights({tripId: selectedTripId});
      setCopilotResult({type: 'disruption', data: response});
    } catch (error) {
      console.error('[Wallet] Disruption copilot failed', error);
      showError(error instanceof Error ? error.message : 'Unable to analyze disruptions right now.');
      setCopilotPendingType(null);
    } finally {
      setCopilotLoading(false);
      setCopilotPendingType(null);
    }
  }, [ensureOnlineForNetworkAction, selectedTripId, showError, showInfo]);

  const handleTripCueAlertAction = useCallback(
    (alert: TripAlert) => {
      // Close any open alerts UI first
      setShowTripAlertsModal(false);

      if (alert.type === 'missing_transport') {
        openAddItem();
        return;
      }
      if (alert.type === 'missing_lodging') {
        openAddItem();
        return;
      }
      if (alert.type === 'budget') {
        void handleLaunchBudget();
        return;
      }
      if (alert.type === 'docs') {
        router.push(getDocumentsTabTarget('merged'));
        return;
      }
      if (alert.type === 'collaborators') {
        setShowCollaborationModal(true);
        return;
      }
      if (alert.type === 'visa') {
        void openVisaRequirements();
        return;
      }

      // Fallback: open the add-item flow so the user can resolve it manually.
      openAddItem();
    },
    [handleLaunchBudget, openAddItem, openVisaRequirements, router],
  );

  const handleCloseCopilotModal = useCallback(() => {
    setCopilotResult(null);
    setCopilotPendingType(null);
  }, []);

  // Handle itinerary item operations
  const handleAddItineraryItem = async (item: Omit<ItineraryItem, 'id' | 'tripId' | 'createdAt' | 'updatedAt'>) => {
    console.log('[Wallet] Adding itinerary item');
    if (!selectedTripId || !firebaseUser?.uid) {
      const error = new Error('Trip ID or user ID is missing');
      console.error('[Wallet] Missing required trip context for itinerary add');
      throw error;
    }
    try {
    // If a user schedules an activity outside the stored trip date range (common when multi-city
    // destinations exist but legacy start/end are 1-day), attempt to extend the trip window.
    // Only owners can do this safely under most rule sets.
    const isTransportCategory = ['flight', 'train', 'transportation', 'car', 'ferry'].includes(item.category);
    const canAutoExtendTripDates = !!selectedTrip?.owner && selectedTrip.owner === firebaseUser.uid;
    if (!isTransportCategory && canAutoExtendTripDates && selectedTrip) {
      const tripStart = coerceDate(selectedTrip.startDate);
      const tripEnd = coerceDate(selectedTrip.endDate) ?? tripStart;
      const itemStart = coerceDate(item.startDate);
      if (tripStart && tripEnd && itemStart) {
        const tStart = new Date(tripStart);
        const tEnd = new Date(tripEnd);
        const iStart = new Date(itemStart);
        tStart.setHours(0, 0, 0, 0);
        tEnd.setHours(0, 0, 0, 0);
        iStart.setHours(0, 0, 0, 0);
        if (iStart.getTime() < tStart.getTime() || iStart.getTime() > tEnd.getTime()) {
          const nextStart = new Date(Math.min(tStart.getTime(), iStart.getTime()));
          const nextEnd = new Date(Math.max(tEnd.getTime(), iStart.getTime()));
          console.log('[Wallet] Extending trip window to include scheduled item', {
            tripId: selectedTripId,
            from: { start: tStart.toISOString(), end: tEnd.toISOString() },
            to: { start: nextStart.toISOString(), end: nextEnd.toISOString() },
          });
          try {
            await updateTrip(selectedTripId, firebaseUser.uid, {
              startDate: nextStart,
              endDate: nextEnd,
            });
          } catch (e) {
            console.warn('[Wallet] Failed to extend trip dates; continuing with create attempt', e);
          }
        }
      }
    }

    let bookingId: string | undefined;

    if (item.category === 'flight') {
      try {
        const providerParts: string[] = [];
        if (item.airlineCode) providerParts.push(item.airlineCode.toUpperCase());
        if (item.flightNumber) providerParts.push(item.flightNumber.toUpperCase());

        const provider =
          providerParts.join(' ').trim() ||
          item.title ||
          'Manual Flight';

        const bookingLocation =
          (item.location && item.location.trim().length > 0 && item.location) ||
          (item.address && item.address.trim().length > 0 && item.address) ||
          selectedTrip?.destination ||
          undefined;

        bookingId = await createBooking(selectedTripId, firebaseUser.uid, {
          type: 'flight',
          provider,
          departureDate: item.startDate,
          arrivalDate: item.endDate ?? item.startDate,
          location: bookingLocation,
          rawEmailSubject: item.title,
          airlineCode: item.airlineCode,
          flightNumber: item.flightNumber,
          source: 'manual',
        });
      } catch (bookingError) {
        console.error('[Wallet] Failed to create linked booking for itinerary flight:', bookingError);
      }
    }

    const itineraryPayload =
      bookingId && !item.bookingId ? { ...item, bookingId } : item;

    const itemId = await createItineraryItem(selectedTripId, itineraryPayload);
      console.log('[Wallet] Successfully created itinerary item');

      // Optimistic update so the list updates immediately; subscription will overwrite with canonical data
      const now = new Date();
      const optimisticItem: ItineraryItem = {
        ...itineraryPayload,
        id: itemId,
        tripId: selectedTripId,
        documents: Array.isArray(itineraryPayload.documents) ? itineraryPayload.documents : [],
        createdAt: now,
        updatedAt: now,
      };
      setItineraryItems((prev) => {
        const next = [...prev, optimisticItem];
        next.sort((a, b) => (coerceDate(a.startDate)?.getTime() ?? 0) - (coerceDate(b.startDate)?.getTime() ?? 0));
        return next;
      });

      return itemId;
    } catch (error) {
      console.error('[Wallet] Error adding itinerary item:', error);
      throw error;
    }
  };

  const buildItineraryToastDetails = useCallback((item: ItineraryItem | null) => {
    if (!item) return undefined;
    const date = item.startDate ? toLocalDateKey(item.startDate) : undefined;
    const time = item.startTime ? item.startTime : undefined;
    const place = (item.location && item.location.trim().length > 0 && item.location) || item.address || undefined;
    const parts = [date, time].filter(Boolean).join(' • ');
    const tail = place ? `${parts ? `${parts} • ` : ''}${place}` : parts;
    return tail || undefined;
  }, []);

  const openItemById = useCallback(
    (itemId: string) => {
      const item = itineraryItems.find((i) => i.id === itemId) ?? null;
      if (item) {
        openedItemIdRef.current = itemId;
        setEditingItineraryItem(item);
        setSelectedDateForForm(item.startDate);
        setShowItineraryForm(true);
      } else {
        setPendingOpenItemId(itemId);
      }
    },
    [itineraryItems],
  );

  const showAddedToItineraryToast = useCallback(
    (input: {
      itemId: string;
      title: string;
      startDate: Date;
      startTime?: string;
      location?: string;
    }) => {
      if (!selectedTripId || !selectedTrip) return;
      const start = coerceDate(selectedTrip.startDate) ?? input.startDate;
      const startDay = new Date(start);
      const itemDay = new Date(input.startDate);
      startDay.setHours(0, 0, 0, 0);
      itemDay.setHours(0, 0, 0, 0);
      const dayNum = Math.max(1, Math.floor((itemDay.getTime() - startDay.getTime()) / (1000 * 60 * 60 * 24)) + 1);

      const destinationLabel =
        (selectedTrip ? getTripDestinationLabel(selectedTrip, { maxCities: 1 }) : undefined) ||
        selectedTrip.destination ||
        selectedTrip.name;

      const message = destinationLabel ? `Added to Day ${dayNum} – ${destinationLabel}` : `Added to Day ${dayNum}`;
      const details = [toLocalDateKey(input.startDate), input.startTime, input.location]
        .filter(Boolean)
        .join(' • ');

      setAddedItemSuccess({
        message,
        details: details || undefined,
        itemId: input.itemId,
      });
    },
    [selectedTrip, selectedTripId],
  );

  const handleEditItineraryItem = async (item: ItineraryItem, updates: Partial<ItineraryItem>) => {
    if (!selectedTripId) return;
    await updateItineraryItem(selectedTripId, item.id, updates);
  };

  const handleDeleteItineraryItem = async (item: ItineraryItem) => {
    console.log('[Wallet] handleDeleteItineraryItem called');
    
    if (!selectedTripId) {
      console.error('[Wallet] No trip selected');
      Alert.alert('Error', 'No trip selected. Please select a trip first.');
      return;
    }

    console.log('[Wallet] Showing delete confirmation dialog');
    confirmAction({
      title: 'Delete Item',
      message: `Are you sure you want to delete "${item.title}"?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        console.log('[Wallet] Delete confirmed, attempting to delete item');
        const tripId = selectedTripId;
        const deletedItem = item;
        pendingDeletedItineraryIdsRef.current.add(deletedItem.id);
        setItineraryItems((prev) => prev.filter((i) => i.id !== deletedItem.id));
        try {
          await deleteItineraryItem(tripId, deletedItem.id);
          console.log('[Wallet] Successfully deleted item');
          if (networkMonitor.isOnline()) {
            const fresh = await fetchTripItineraryItems(tripId, { fromServer: true });
            if (selectedTripIdRef.current === tripId) {
              reconcilePendingDeletedItineraryItems(fresh);
              setItineraryItems(filterPendingDeletedItineraryItems(fresh));
            }
          }
        } catch (error) {
          pendingDeletedItineraryIdsRef.current.delete(deletedItem.id);
          setItineraryItems((prev) => {
            if (prev.some((i) => i.id === deletedItem.id)) return prev;
            const next = [...prev, deletedItem];
            next.sort(
              (a, b) =>
                (coerceDate(a.startDate)?.getTime() ?? 0) - (coerceDate(b.startDate)?.getTime() ?? 0),
            );
            return next;
          });
          const message = error instanceof Error ? error.message : String(error);
          console.error('[Wallet] Error deleting itinerary item', { message });
          Alert.alert('Error', 'Failed to delete item. Please try again.');
        }
      },
      onCancel: () => console.log('[Wallet] Delete cancelled'),
    });
  };

  // Handle adding a single suggestion to itinerary
  const handleAddSingleSuggestion = useCallback(async (
    suggestion: ChatPlannerResult['suggestions'][0],
    picked?: { date: Date; startTime?: string; endTime?: string }
  ) => {
    if (!firebaseUser?.uid || !selectedTrip || !selectedTripId) {
      Alert.alert('Error', 'Please sign in and select a trip.');
      return;
    }

    // Create unique ID for this suggestion
    const suggestionId = `${suggestion.day}-${suggestion.title}`;
    
    // Check if already adding
    if (addingSuggestionIds.has(suggestionId)) {
      return; // Already processing
    }

    // Mark as adding
    setAddingSuggestionIds(prev => new Set(prev).add(suggestionId));

    try {
      console.log('[Wallet] Adding suggestion to itinerary:', suggestion.title);
      
      // Calculate startDate based on user pick or day number
      const pickedDate = coerceDate(picked?.date);
      const fallbackStart = safeTripStartDate ?? new Date();
      let itemStartDate = pickedDate ? new Date(pickedDate) : new Date(fallbackStart);
      if (!picked?.date) {
        const dayNumber = suggestion.day ?? 1;
        itemStartDate.setDate(itemStartDate.getDate() + (dayNumber - 1));
      }

      // Infer category
      const category = inferItemCategory(suggestion.title, suggestion.description);

      // Create itinerary item (optimistic - no alert needed, will show in list)
      const createdId = await handleAddItineraryItem({
        title: suggestion.title,
        description: suggestion.description,
        category,
        startDate: itemStartDate,
        startTime: picked?.startTime,
        endTime: picked?.endTime,
        address: suggestion.address,
        location: suggestion.address, // Use address as location if available
        source: 'ai',
        status: 'planned',
        createdBy: firebaseUser.uid,
        documents: [],
      });

      console.log('[Wallet] Successfully added suggestion to itinerary');
      showAddedToItineraryToast({
        itemId: createdId,
        title: suggestion.title,
        startDate: itemStartDate,
        startTime: picked?.startTime,
        location: suggestion.address,
      });
    } catch (error: any) {
      console.error('[Wallet] Failed to add suggestion to itinerary', error);
      const isOffline = error?.code === 'unavailable' || error?.code === 'failed-precondition';
      showError(
        isOffline
          ? "You're offline. Check your connection and try again."
          : error?.message || 'Unable to add suggestion. Please try again later.',
      );
    } finally {
      // Remove from adding set
      setAddingSuggestionIds(prev => {
        const next = new Set(prev);
        next.delete(suggestionId);
        return next;
      });
    }
  }, [
    addingSuggestionIds,
    firebaseUser?.uid,
    handleAddItineraryItem,
    inferItemCategory,
    safeTripStartDate,
    showAddedToItineraryToast,
    selectedTrip,
    selectedTripId,
  ]);




  // Show dropdown if multiple trips, otherwise just show the single trip
  const showDropdown = trips.length > 1;

  // Create dynamic subtitle with trip context (MUST be before early returns)
  const screenSubtitle = 'Your plan and itinerary in one place.';

  const tripCountdown = useMemo(() => {
    if (!selectedTripId) return null;
    if (!safeTripStartDate) return null;
    const start = new Date(safeTripStartDate);
    const today = new Date();
    start.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((start.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  }, [safeTripStartDate, selectedTripId]);

  // Show empty state if no trips (AFTER all hooks)
  if (tripsLoading) {
    return (
      <Screen
        title="Itinerary"
        subtitle="This is your adventure HQ, add tickets, daily plans, and get smart reminders so you never miss a thing."
      >
        <Card>
          <LoadingState 
            variant="inline" 
            message="Loading trips..."
            accessibilityLabel="Loading trips"
            accessibilityLiveRegion="polite"
          />
        </Card>
      </Screen>
    );
  }

  if (trips.length === 0) {
    return (
      <Screen
        title="Itinerary"
        subtitle="This is your adventure HQ, add tickets, daily plans, and get smart reminders so you never miss a thing."
      >
        <Card>
          <View style={{ alignItems: 'center', paddingVertical: theme.spacing(6), gap: theme.spacing(2) }}>
            <View style={{
              width: 80,
              height: 80,
              borderRadius: 40,
              backgroundColor: theme.colors.primary + '10',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: theme.spacing(1),
            }}>
              <Ionicons name="wallet-outline" size={40} color={theme.colors.primary} />
            </View>
            <ThemedText variant="bodyStrong" style={{ fontSize: 20, textAlign: 'center', fontWeight: '700' }}>
              No trips yet
            </ThemedText>
            <Body color={theme.colors.textSecondary} style={{ textAlign: 'center', fontSize: 15, lineHeight: 22, maxWidth: 280 }}>
              Create your first trip to start planning and tracking expenses.
            </Body>
            <Button
              label="Go to Trips"
              variant="primary"
              onPress={() => router.push('/(tabs)')}
              icon={<Ionicons name="arrow-forward" size={18} color={theme.colorScheme === 'dark' ? '#000000' : '#FFFFFF'} />}
              style={{ marginTop: theme.spacing(2), minWidth: 200 }}
              accessibilityLabel="Go to trips tab"
              accessibilityHint="Navigate to the trips tab to create your first trip"
            />
          </View>
        </Card>
      </Screen>
    );
  }

  // Create header actions
  const headerActions = selectedTrip && firebaseUser?.uid && (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
      <NotificationBell
        count={tripNotificationCount}
        onPress={() => setShowTripAlertsModal(true)}
        accessibilityLabel="Open trip notifications"
      />
      <Pressable
        onPress={() => setCollabSuggestToken((n) => n + 1)}
        style={({ pressed }) => ({
          padding: theme.spacing(0.75),
          borderRadius: theme.radii.md,
          opacity: pressed ? 0.7 : 1,
          minHeight: 44,
          minWidth: 44,
          alignItems: 'center',
          justifyContent: 'center',
        })}
        accessibilityRole="button"
        accessibilityLabel="Suggest something"
        accessibilityHint={
          (selectedTrip.collaborators?.length ?? 0) > 0
            ? 'Open collaborative suggestions to add an idea for the trip'
            : 'Open trip ideas and suggestions'
        }
      >
        <Ionicons
          name="sparkles-outline"
          size={22}
          color={theme.colors.primary}
        />
      </Pressable>
      <Pressable
        onPress={scrollPlanToCommentsSection}
        style={({ pressed }) => ({
          padding: theme.spacing(0.75),
          borderRadius: theme.radii.md,
          opacity: pressed ? 0.7 : 1,
          minHeight: 44,
          minWidth: 44,
          alignItems: 'center',
          justifyContent: 'center',
        })}
        accessibilityRole="button"
        accessibilityLabel="Open comments"
        accessibilityHint="Expand the trip comments section"
      >
        <Ionicons
          name="chatbubble-outline"
          size={22}
          color={theme.colors.primary}
        />
      </Pressable>
      <Pressable
        onPress={() => setShowCollaborationModal(true)}
        style={({ pressed }) => ({
          padding: theme.spacing(0.75),
          borderRadius: theme.radii.md,
          opacity: pressed ? 0.7 : 1,
          minHeight: 44, // WCAG minimum touch target
          minWidth: 44,
          alignItems: 'center',
          justifyContent: 'center',
        })}
        accessibilityRole="button"
        accessibilityLabel="Manage collaborators"
        accessibilityHint="Add or remove people who can view or edit this trip"
      >
        <Ionicons
          name="people-outline"
          size={22}
          color={theme.colors.primary}
        />
      </Pressable>
      <Pressable
        onPress={() => setShowExportMenu(true)}
        style={({ pressed }) => ({
          padding: theme.spacing(0.75),
          borderRadius: theme.radii.md,
          opacity: pressed ? 0.7 : 1,
          minHeight: 44, // WCAG minimum touch target
          minWidth: 44,
          alignItems: 'center',
          justifyContent: 'center',
        })}
        accessibilityRole="button"
        accessibilityLabel="Export trip"
        accessibilityHint="Export your trip data, itinerary, or expenses"
      >
        <Ionicons
          name="download-outline"
          size={22}
          color={theme.colors.primary}
        />
      </Pressable>
    </View>
  );

  return (
    <Screen
      title="Plan"
      subtitle={screenSubtitle}
      headerAction={headerActions}
      scrollable={true}
      scrollViewRef={planScrollViewRef}
      floating={
        selectedTripId ? (
          <View pointerEvents="box-none" style={{ gap: theme.spacing(1.5), alignItems: 'flex-end' }}>
            {!ideasModalVisibleForFab && aiSuggestionsLoading && !isSelectedTripReadOnly ? (
              <Pressable
                onPress={() => setIdeasModalReopenToken((t) => t + 1)}
                style={({ pressed }) => ({
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: theme.spacing(1),
                  paddingVertical: theme.spacing(1.25),
                  paddingHorizontal: theme.spacing(1.75),
                  borderRadius: theme.radii.pill,
                  backgroundColor: theme.colors.surface,
                  borderWidth: 1,
                  borderColor: theme.colors.primary + '44',
                  maxWidth: 280,
                  opacity: pressed ? 0.92 : 1,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.14,
                  shadowRadius: 6,
                  elevation: 4,
                })}
                accessibilityRole="button"
                accessibilityLabel="Ideas and AI are still updating. Tap to reopen."
                accessibilityHint="Opens the Ideas and AI window"
              >
                <ActivityIndicator size="small" color={theme.colors.primary} />
                <ThemedText variant="bodyStrong" style={{ fontSize: 14 }} numberOfLines={1}>
                  Ideas updating…
                </ThemedText>
                <Ionicons name="chevron-up" size={18} color={theme.colors.primary} />
              </Pressable>
            ) : null}
            {isTripActiveNow ? (
              <FloatingActionButton
                onPress={() => setCompanionMenuVisible(true)}
                icon="sparkles-outline"
                label={Platform.OS === 'web' ? 'Companion' : undefined}
                accessibilityLabel="Open travel companion menu"
                accessibilityHint="Quick actions for during the trip"
              />
            ) : null}
          </View>
        ) : null
      }
    >
      {offlineStaleDescriptor ? (
        <Card variant="outlined" style={{ marginBottom: theme.spacing(1.5) }}>
          <Caption color={theme.colors.textSecondary}>{offlineStaleDescriptor}</Caption>
        </Card>
      ) : null}
      {selectedTrip ? (
        <>
          {/* Trip Selection and Info Section (moved above hero) */}
          {(showDropdown || isSelectedTripReadOnly) && (
          <Card variant="elevated" elevation={1} style={{ marginBottom: theme.spacing(2) }}>
            <View style={{ gap: theme.spacing(2.5) }}>
              {showDropdown && (
                <>
                  {/* Breadcrumb indicator */}
                  <View style={{ 
                    flexDirection: 'row', 
                    alignItems: 'center', 
                    gap: theme.spacing(0.75),
                    marginBottom: theme.spacing(0.5),
                  }}>
                    <Ionicons name="calendar-outline" size={14} color={theme.colors.textSecondary} />
                    <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                      Viewing itinerary for:
                    </Caption>
                  </View>
                  <SelectField
                    label="Select trip"
                    value={selectedTripId || undefined}
                    options={tripOptions}
                    onValueChange={(value) => {
                      console.log('[Wallet] Trip selection changed:', value);
                      handleTripChange(value);
                    }}
                    placeholder="Choose a trip"
                  />
                </>
              )}
              
              {/* Trip Quick Stats - inside the select trip container */}
              {isSelectedTripReadOnly && (
                <Card variant="outlined" elevation={0} style={{
                  backgroundColor: theme.colors.borderMuted + '20',
                  borderWidth: 1,
                  borderColor: theme.colors.borderMuted,
                }}>
                  <View style={{ 
                    flexDirection: 'row', 
                    alignItems: 'flex-start', 
                    gap: theme.spacing(1.5),
                    padding: theme.spacing(1.5),
                  }}>
                    <View
                      style={{
                        padding: theme.spacing(1),
                        borderRadius: theme.radii.md,
                        backgroundColor: theme.colors.textSecondary + '20',
                      }}
                    >
                      <Ionicons
                        name={selectedTrip?.isArchived ? 'archive' : 'lock-closed'}
                        size={20}
                        color={theme.colors.textSecondary}
                      />
                    </View>
                    <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
                      <ThemedText variant="bodyStrong" style={{ fontSize: 14 }}>
                        {selectedTrip?.isArchived ? 'Trip archived' : 'Trip completed'}
                      </ThemedText>
                      <Body color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                        {selectedTrip?.isArchived
                          ? 'This trip is in read-only mode. Unarchive it from the Trips tab to make edits.'
                          : 'This trip has finished and is locked. Reopen it from the Trips tab if you need to make changes.'}
                      </Body>
                      <Pressable
                        onPress={() => router.push('/(tabs)')}
                        style={({ pressed }) => ({
                          marginTop: theme.spacing(0.5),
                          paddingVertical: theme.spacing(0.75),
                          paddingHorizontal: theme.spacing(1.5),
                          borderRadius: theme.radii.md,
                          backgroundColor: theme.colors.primary + '10',
                          alignSelf: 'flex-start',
                          opacity: pressed ? 0.7 : 1,
                        })}
                      >
                        <Body color={theme.colors.primary} style={{ fontSize: 12, fontWeight: '600' }}>
                          Go to Trips →
                        </Body>
                      </Pressable>
                    </View>
                  </View>
                </Card>
              )}
            </View>
          </Card>
          )}

          {/* Hero Trip Header (restores travel feel) */}
          <Card
            variant="elevated"
            elevation={1}
            padding={0}
            style={{
              marginBottom: theme.spacing(1.5),
              overflow: 'hidden',
            }}
          >
            <View style={{ height: 160 }}>
              <Image
                source={
                  selectedTrip.landmarkImage
                    ? { uri: selectedTrip.landmarkImage }
                    : require('../../assets/travel/travel-1.jpg')
                }
                style={{ width: '100%', height: '100%' }}
                resizeMode="cover"
              />
              <LinearGradient
                colors={['rgba(0,0,0,0.05)', 'rgba(0,0,0,0.75)']}
                start={{ x: 0.5, y: 0 }}
                end={{ x: 0.5, y: 1 }}
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 0,
                  padding: theme.spacing(2),
                  justifyContent: 'flex-end',
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: theme.spacing(1.5) }}>
                  <View style={{ flex: 1 }}>
                    <ThemedText
                      variant="h3"
                      style={{
                        fontSize: 20,
                        fontWeight: '800',
                        color: '#fff',
                        letterSpacing: -0.2,
                      }}
                      numberOfLines={1}
                    >
                      {selectedTrip.name}
                    </ThemedText>

                    <View
                      style={{
                        marginTop: theme.spacing(0.75),
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        gap: theme.spacing(0.75),
                        alignItems: 'center',
                      }}
                    >
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.4) }}>
                        <Ionicons name="calendar-outline" size={13} color="rgba(255,255,255,0.92)" />
                        <Caption style={{ fontSize: 12, fontWeight: '700', color: 'rgba(255,255,255,0.92)' }}>
                          {formatTripDates(selectedTrip.startDate, selectedTrip.endDate)}
                        </Caption>
                      </View>

                      {destinationLabel ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.4) }}>
                          <Caption style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>•</Caption>
                          <Ionicons name="location-outline" size={13} color="rgba(255,255,255,0.92)" />
                          <Caption style={{ fontSize: 12, fontWeight: '700', color: 'rgba(255,255,255,0.92)' }}>
                            {destinationLabel}
                          </Caption>
                        </View>
                      ) : null}

                      {(selectedTrip.collaborators?.length || 0) > 0 ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.4) }}>
                          <Caption style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>•</Caption>
                          <Ionicons name="people-outline" size={13} color="rgba(255,255,255,0.92)" />
                          <Caption style={{ fontSize: 12, fontWeight: '700', color: 'rgba(255,255,255,0.92)' }}>
                            {selectedTrip.collaborators?.length || 0}
                          </Caption>
                        </View>
                      ) : null}
                    </View>
                  </View>

                  {tripCountdown !== null && tripCountdown > 0 ? (
                    <View
                      style={{
                        backgroundColor: 'rgba(255,255,255,0.18)',
                        paddingHorizontal: theme.spacing(1),
                        paddingVertical: theme.spacing(0.5),
                        borderRadius: theme.radii.pill,
                        borderWidth: 1,
                        borderColor: 'rgba(255,255,255,0.22)',
                      }}
                    >
                      <Caption style={{ fontWeight: '900', fontSize: 12, color: '#fff' }}>
                        {tripCountdown === 1 ? '1 day' : `${tripCountdown} days`}
                      </Caption>
                    </View>
                  ) : null}
                </View>
              </LinearGradient>
            </View>
          </Card>

          {/* Quick Actions Bar - minimal strip for essential actions */}
          <View style={{ 
            flexDirection: 'row', 
            flexWrap: 'wrap', 
            gap: theme.spacing(0.75), 
            marginBottom: theme.spacing(1.5) 
          }}>
            <Pressable
              onPress={openDocs}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(0.5),
                paddingHorizontal: theme.spacing(1),
                paddingVertical: theme.spacing(0.6),
                borderRadius: theme.radii.pill,
                backgroundColor: pressed ? theme.colors.primary + '15' : theme.colors.surface,
                borderWidth: 1,
                borderColor: theme.colors.borderMuted,
              })}
            >
              <Ionicons name="document-text-outline" size={14} color={theme.colors.primary} />
              <Caption style={{ fontSize: 11, fontWeight: '600', color: theme.colors.text }}>Docs</Caption>
            </Pressable>
            <Pressable
              onPress={openBudget}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(0.5),
                paddingHorizontal: theme.spacing(1),
                paddingVertical: theme.spacing(0.6),
                borderRadius: theme.radii.pill,
                backgroundColor: pressed ? theme.colors.primary + '15' : theme.colors.surface,
                borderWidth: 1,
                borderColor: theme.colors.borderMuted,
              })}
            >
              <Ionicons name="wallet-outline" size={14} color={theme.colors.primary} />
              <Caption style={{ fontSize: 11, fontWeight: '600', color: theme.colors.text }}>Budget</Caption>
            </Pressable>
            <Pressable
              onPress={openTravelSafety}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(0.5),
                paddingHorizontal: theme.spacing(1),
                paddingVertical: theme.spacing(0.6),
                borderRadius: theme.radii.pill,
                backgroundColor: pressed ? theme.colors.primary + '15' : theme.colors.surface,
                borderWidth: 1,
                borderColor: theme.colors.borderMuted,
              })}
            >
              <Ionicons name="shield-checkmark-outline" size={14} color={theme.colors.primary} />
              <Caption style={{ fontSize: 11, fontWeight: '600', color: theme.colors.text }}>Safety</Caption>
            </Pressable>
            <Pressable
              onPress={openTravelMaps}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(0.5),
                paddingHorizontal: theme.spacing(1),
                paddingVertical: theme.spacing(0.6),
                borderRadius: theme.radii.pill,
                backgroundColor: pressed ? theme.colors.primary + '15' : theme.colors.surface,
                borderWidth: 1,
                borderColor: theme.colors.borderMuted,
              })}
            >
              <Ionicons name="map-outline" size={14} color={theme.colors.primary} />
              <Caption style={{ fontSize: 11, fontWeight: '600', color: theme.colors.text }}>Maps</Caption>
            </Pressable>
            {/* Family quick-add - only shown for family travelers */}
            {!isSelectedTripReadOnly && profile?.travelType === 'family' && (
              <>
                <View style={{ width: 1, height: 16, backgroundColor: theme.colors.borderMuted, marginHorizontal: theme.spacing(0.25) }} />
                {([
                  { label: 'Meal', icon: 'restaurant-outline', template: 'meal' },
                  { label: 'Nap', icon: 'moon-outline', template: 'nap' },
                  { label: 'Play', icon: 'happy-outline', template: 'play' },
                ] as const).map((quick) => (
                  <Pressable
                    key={quick.template}
                    onPress={() => handleQuickAddTemplate(quick.template)}
                    style={({ pressed }) => ({
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: theme.spacing(0.5),
                      paddingHorizontal: theme.spacing(1),
                      paddingVertical: theme.spacing(0.6),
                      borderRadius: theme.radii.pill,
                      backgroundColor: pressed ? theme.colors.primary + '15' : theme.colors.surface,
                      borderWidth: 1,
                      borderColor: theme.colors.borderMuted,
                    })}
                    accessibilityRole="button"
                    accessibilityLabel={`Add ${quick.label.toLowerCase()}`}
                  >
                    <Ionicons name={quick.icon} size={14} color={theme.colors.primary} />
                    <Caption style={{ fontSize: 11, fontWeight: '600', color: theme.colors.text }}>{quick.label}</Caption>
                  </Pressable>
                ))}
              </>
            )}
          </View>

          {selectedTrip && !isSelectedTripReadOnly && (
            <>
              <ItineraryOfflineCard
                trip={selectedTrip}
                itineraryItems={itineraryItems}
                bookings={bookings}
                isPremium={planAiPremium}
                onOpenOfflineUpgrade={openPlanOfflineUpgrade}
                onCacheChanged={() => setOfflineCacheBump((n) => n + 1)}
              />
              <TripOverviewCard
                trip={selectedTrip}
                userId={firebaseUser?.uid}
                visaCheck={visaCheck}
                onVisaLinkPress={() => {
                  void openVisaRequirements();
                }}
                citizenshipLabel={profile?.citizenship || profile?.homeCountry}
                onOpenFullMap={openTravelMaps}
                itineraryItems={itineraryItems}
                bookings={bookings || []}
                readinessAlerts={filteredReadinessAlerts}
                onOpenAlerts={() => setShowTripAlertsModal(true)}
                onAlertAction={handleTripCueAlertAction}
                readinessCollapsed={expandedSections.readiness}
                onToggleReadiness={(collapsed) => updateSectionCollapse('readiness', collapsed)}
                onOpenOfflineDownload={openOfflineDownload}
              />
              <WhereImStayingCard
                trip={selectedTrip}
                bookings={bookings}
                itineraryItems={itineraryItems}
              />
            </>
          )}

          {/* PlanningTab - Primary itinerary content (moved up) */}
          {safeTripStartDate && safeTripEndDate ? (
            <PlanningTab
              trip={selectedTrip}
              itineraryItems={itineraryItems}
              focusedDateKey={routeFocusedDateKey}
              itineraryLoading={itineraryLoading}
              bookings={bookings}
              isArchived={isSelectedTripReadOnly}
              aiPlannerIntentAppendixRef={aiPlannerIntentAppendixRef}
              planIdeaCardsQuota={planAiPremium ? undefined : planIdeaCardsQuota}
              onIdeasModalVisibilityChange={setIdeasModalVisibleForFab}
              ideasModalReopenToken={ideasModalReopenToken}
              refreshPlanItineraryFromServer={resyncPlanItineraryFromServer}
              canEdit={
                !isSelectedTripReadOnly &&
                (isOwner || userPermission === 'edit' || userPermission === 'add_items')
              }
              userId={firebaseUser?.uid || ''}
              userPermission={userPermission}
              isOwner={isOwner}
              onScheduleMaybeItem={async (maybe) => {
                return await openMaybeSchedulePrompt(maybe);
              }}
                onAddItem={(date?: Date) => {
                  const safeDate = coerceDate(date);
                  const dateStr = safeDate ? safeDate.toISOString() : (date ? String(date) : 'none');
                  console.log('[Wallet] Opening itinerary form', safeDate ? `with date: ${dateStr}` : '');
                  openAddItem(safeDate ?? undefined);
                }}
                onEditItem={(item) => {
                  setEditingItineraryItem(item);
                  setShowItineraryForm(true);
                }}
                onDeleteItem={handleDeleteItineraryItem}
                onViewDocument={(url, meta) => setViewingDocument({
                  url,
                  offlinePath: meta?.offlinePath,
                  fileName: meta?.fileName,
                  contentType: meta?.contentType,
                })}
                onOpenEmailProvider={handleOpenEmailProvider}
                inboxSyncing={inboxSyncing}
                inboxConnected={inboxConnections[inboxProvider] ?? null}
                inboxProviderLabel={PROVIDER_LABELS[inboxProvider]}
                onLaunchRebooking={handleLaunchRebooking}
                onLaunchDisruption={handleLaunchDisruption}
                onLaunchBudget={handleLaunchBudget}
                copilotLoading={copilotLoading}
                aiSuggestions={aiSuggestions}
                aiSuggestionsLoading={aiSuggestionsLoading}
                aiSuggestionsError={aiSuggestionsError}
                aiSuggestionsErrorMode={aiSuggestionsErrorMode}
                aiSuggestionsLoadingExtended={aiSuggestionsLoadingExtended}
                aiSuggestionsRequestMode={aiSuggestionsRequestMode}
              onOpenTravelCompanion={() => setTravelCompanionVisible(true)}
                showTripOverviewCard={false}
                readinessCollapsed={expandedSections.readiness}
                onToggleReadiness={(collapsed) => updateSectionCollapse('readiness', collapsed)}
              // Readiness is a checklist: checked (read) items stay visible in the modal,
              // but do not count toward "needs attention".
              readinessAlerts={filteredReadinessAlerts}
              onOpenAlerts={() => setShowTripAlertsModal(true)}
              onAlertAction={handleTripCueAlertAction}
              onOpenOfflineDownload={openOfflineDownload}
              onTripOfflineCacheChanged={() => setOfflineCacheBump((n) => n + 1)}
              openCollaboratorSuggestToken={collabSuggestToken}
                onAddRecommendationToItinerary={async (item) => {
                  const createdId = await handleAddItineraryItem({
                    ...item,
                    source: 'manual', // Amadeus recommendations are manually added
                    status: 'planned',
                    createdBy: firebaseUser?.uid || '',
                    documents: [],
                  });
                  showAddedToItineraryToast({
                    itemId: createdId,
                    title: item.title,
                    startDate: item.startDate,
                    startTime: item.startTime,
                    location: item.location || item.address,
                  });
                }}
                onGenerateSuggestions={async (mode?: 'itinerary' | 'packages') => {
                  if (!firebaseUser?.uid || !selectedTrip || isSelectedTripReadOnly) {
                    return;
                  }
                  if (!ensureOnlineForNetworkAction()) {
                    return;
                  }

                  let requestNonce = 0;
                  try {
                    const AI_LONG_HINT_MS = 5000;
                    // Align with chatPlanner client timeout (280s); stop spinner after 270s if still no response.
                    const AI_WATCHDOG_MS = 270000;
                    const requestMode: 'itinerary' | 'packages' = mode || 'itinerary';
                    const isSameInFlight =
                      aiSuggestionsLoading && aiSuggestionsRequestMode === requestMode;
                    if (isSameInFlight) {
                      showInfo('Still generating suggestions…');
                      return;
                    }

                    const gateStart = safeTripStartDate ?? new Date();
                    const gateEnd = safeTripEndDate ?? gateStart;
                    const gateDaysDiff = Math.ceil(
                      (gateEnd.getTime() - gateStart.getTime()) / (1000 * 60 * 60 * 24),
                    );
                    const projectedBatch = projectedPlanAiIdeaBatchSize(gateDaysDiff);
                    if (
                      !planAiPremium &&
                      planIdeaCardsQuota &&
                      planIdeaCardsQuota.remaining < projectedBatch
                    ) {
                      usePremiumGateStore.getState().open({
                        feature: 'ai_plan_ideas',
                        title: 'Not enough Plan AI cards',
                        message: `This run uses up to ${projectedBatch} idea cards. You have ${planIdeaCardsQuota.remaining} left this month (${FREE_PLAN_LIMITS.aiPlanIdeaCardsPerMonth} included on the free plan). Upgrade to Premium for unlimited Plan AI ideas.`,
                        source: 'feature_gate',
                      });
                      return;
                    }

                    // Single-flight: newer requests supersede older ones.
                    requestNonce = (aiSuggestionsRequestNonceRef.current += 1);
                    setAiSuggestionsRequestMode(requestMode);
                    setAiSuggestionsLoadingExtended(false);
                    setAiSuggestionsError(null);
                    setAiSuggestionsErrorMode(null);
                    setAiSuggestionsLoading(true);
                    const previousMode = (aiSuggestions as any)?.debug?.mode;
                    if (previousMode && previousMode !== requestMode) {
                      setAiSuggestions(null);
                    }

                    const intentAppendixForRequest =
                      requestMode === 'itinerary'
                        ? aiPlannerIntentAppendixRef.current?.trim() || null
                        : null;
                    const plannerFocusCacheKey = buildPlannerFocusCacheKey(intentAppendixForRequest);

                    if (aiSuggestionsLongTimerRef.current) {
                      clearTimeout(aiSuggestionsLongTimerRef.current);
                      aiSuggestionsLongTimerRef.current = null;
                    }
                    aiSuggestionsLongTimerRef.current = setTimeout(() => {
                      if (aiSuggestionsRequestNonceRef.current === requestNonce) {
                        setAiSuggestionsLoadingExtended(true);
                      }
                    }, AI_LONG_HINT_MS);

                    if (aiSuggestionsHardTimerRef.current) {
                      clearTimeout(aiSuggestionsHardTimerRef.current);
                      aiSuggestionsHardTimerRef.current = null;
                    }
                    // Watchdog: never let the UI spin indefinitely.
                    aiSuggestionsHardTimerRef.current = setTimeout(async () => {
                      if (aiSuggestionsRequestNonceRef.current !== requestNonce) return;
                      // Invalidate this request and fall back to cache if available.
                      aiSuggestionsRequestNonceRef.current += 1;
                      setAiSuggestionsLoading(false);
                      setAiSuggestionsLoadingExtended(false);
                      setAiSuggestionsRequestMode(null);
                      try {
                        const cached = await getCachedChatPlannerResult(
                          selectedTrip.id,
                          requestMode,
                          plannerFocusCacheKey,
                        );
                        if (cached?.result) {
                          setAiSuggestions({
                            ...cached.result,
                            debug: {
                              ...(cached.result.debug || {}),
                              cached: true,
                              cachedAt: cached.savedAt,
                              mode: requestMode,
                              plannerFocusCacheKey,
                              watchdog: true,
                            },
                          });
                          showInfo('AI is taking too long — showing cached suggestions.');
                          setAiSuggestionsError(null);
                          setAiSuggestionsErrorMode(null);
                        } else {
                          const msg = 'AI is taking too long right now. Please try again in a moment.';
                          setAiSuggestionsError(msg);
                          setAiSuggestionsErrorMode(requestMode);
                          showError(msg);
                          setAiSuggestions(null);
                        }
                      } catch {
                        const msg = 'AI is taking too long right now. Please try again in a moment.';
                        setAiSuggestionsError(msg);
                        setAiSuggestionsErrorMode(requestMode);
                        showError(msg);
                        setAiSuggestions(null);
                      } finally {
                        if (aiSuggestionsHardTimerRef.current) {
                          clearTimeout(aiSuggestionsHardTimerRef.current);
                          aiSuggestionsHardTimerRef.current = null;
                        }
                        if (aiSuggestionsLongTimerRef.current) {
                          clearTimeout(aiSuggestionsLongTimerRef.current);
                          aiSuggestionsLongTimerRef.current = null;
                        }
                      }
                    }, AI_WATCHDOG_MS);
                    
                    const travelTypeContext: Record<string, string> = {
                      solo: 'I am traveling solo',
                      group: 'I am traveling with a group',
                      business: 'I am on a business trip',
                      couple: 'I am traveling with my partner',
                      family: 'I am traveling with my family/kids',
                    };

                    const travelTypeMessage = profile?.travelType
                      ? travelTypeContext[profile?.travelType] || 'I am planning a trip'
                      : 'I am planning a trip';

                    const safeStartDate = safeTripStartDate ?? new Date();
                    const safeEndDate = safeTripEndDate ?? safeStartDate;

                    const startDateStr = safeStartDate.toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    });
                    const endDateStr = safeEndDate.toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    });

                    const daysDiff = Math.ceil(
                      (safeEndDate.getTime() - safeStartDate.getTime()) / (1000 * 60 * 60 * 24)
                    );

                    const destCities: string[] = Array.isArray(selectedTrip.destinations)
                      ? (selectedTrip.destinations
                          .map((d) => d.city?.trim())
                          .filter(Boolean) as string[])
                      : [];
                    const destinationText =
                      destCities.length > 1
                        ? destCities.join(', ')
                        : destCities.length === 1
                          ? destCities[0]
                          : selectedTrip.destination || selectedTrip.name || 'my destination';
                    const baseTripContext = `I'm planning a trip to ${destinationText} from ${startDateStr} to ${endDateStr} (${daysDiff} days). ${travelTypeMessage}.`;
                    let message = baseTripContext;
                    
                    if (selectedTrip.travelingFrom) {
                      message += ` I'm traveling from ${selectedTrip.travelingFrom}.`;
                    }

                    if (selectedTrip.budget) {
                      message += ` My budget is approximately $${selectedTrip.budget.toLocaleString()}.`;
                    }

                    if (requestMode === 'packages') {
                      message +=
                        `\n\nTASK: Suggest 3–6 official city passes / attraction bundles / museum passes that combine multiple activities in ${destinationText}.` +
                        ` Only return provider-defined bundles or passes (no single-activity items).` +
                        ` For each suggestion, output a short title, a clear 1–2 sentence description, set category="package", and set isPackage=true.` +
                        ` Include a ticketLink ONLY if you are confident it is the official product page (otherwise omit).` +
                        ` Avoid hallucinations: do not invent inclusions or links. If unsure, say so briefly in the description.` +
                        ` These are "idea cards" (not day-by-day), so set day=1 for all items.`;
                    } else if (Array.isArray(itineraryItems) && itineraryItems.length > 0) {
                      const itemCounts = itineraryItems.reduce((acc, item) => {
                        acc[item.category] = (acc[item.category] || 0) + 1;
                        return acc;
                      }, {} as Record<string, number>);
                      
                      const itemSummary = Object.entries(itemCounts)
                        .map(([category, count]) => `${count} ${category}${count > 1 ? 's' : ''}`)
                        .join(', ');
                      
                      // List existing activities to exclude
                      const existingActivities = itineraryItems
                        .map(item => item.title)
                        .filter(Boolean)
                        .slice(0, 20); // Limit to first 20 to avoid message being too long
                      
                      message += ` I already have ${itineraryItems.length} item${itineraryItems.length !== 1 ? 's' : ''} in my itinerary (${itemSummary}).`;
                      
                      if (existingActivities.length > 0) {
                        message += ` IMPORTANT: Do NOT suggest the following activities as they are already in my itinerary: ${existingActivities.join(', ')}.`;
                      }
                      
                      message += ' Please suggest NEW activities and experiences that are different from what I already have, matching my travel style and complementing my existing plans.';
                    } else {
                      message += ' My itinerary is currently empty. Please suggest a complete day-by-day itinerary with activities and experiences that match my travel style, including recommendations for flights, accommodations, and activities.';
                    }

                    console.log('[Wallet] Requesting AI suggestions with message:', message);
                    const normalizedBudget =
                      typeof selectedTrip.budget === 'number' && Number.isFinite(selectedTrip.budget)
                        ? selectedTrip.budget
                        : undefined;
                    const result = await requestItinerarySuggestions({
                      tripId: selectedTrip.id,
                      message,
                      locale: selectedTrip.locale || 'en-US',
                      ...(typeof normalizedBudget === 'number'
                        ? { budget: normalizedBudget }
                        : {}),
                      intentAppendix: intentAppendixForRequest || undefined,
                    });

                    // Ignore stale responses if a newer request started.
                    if (aiSuggestionsRequestNonceRef.current !== requestNonce) {
                      return;
                    }
                    
                    console.log('[Wallet] AI suggestions received:', result);

                    if (result == null || typeof result !== 'object') {
                      const msg = 'We couldn’t read AI suggestions. Please try again.';
                      setAiSuggestionsError(msg);
                      setAiSuggestionsErrorMode(requestMode);
                      showError(msg);
                      setAiSuggestions(null);
                      return;
                    }

                    const incomingSuggestions = Array.isArray(result.suggestions) ? result.suggestions : [];

                    const isFallback =
                      result.debug?.fallback === true ||
                      (incomingSuggestions.length === 1 &&
                        incomingSuggestions[0]?.title?.toLowerCase().includes('check back soon')) ||
                      incomingSuggestions.length === 0;

                    if (isFallback) {
                      // Try cached suggestions first (trust-preserving fallback).
                      const cached = await getCachedChatPlannerResult(
                        selectedTrip.id,
                        requestMode,
                        plannerFocusCacheKey,
                      );
                      if (cached?.result) {
                        setAiSuggestions({
                          ...cached.result,
                          debug: {
                            ...(cached.result.debug || {}),
                            cached: true,
                            cachedAt: cached.savedAt,
                            mode: requestMode,
                            plannerFocusCacheKey,
                          },
                        });
                        showInfo('Showing cached suggestions.');
                        setAiSuggestionsError(null);
                        setAiSuggestionsErrorMode(null);
                      } else {
                        const msg = 'We couldn’t generate ideas right now. Please try again in a moment.';
                        setAiSuggestionsError(msg);
                        setAiSuggestionsErrorMode(requestMode);
                        showError(msg);
                        setAiSuggestions(null);
                      }
                    } else {
                      // Filter out duplicate suggestions that already exist in itinerary
                      const { filterDuplicateSuggestions } = await import('@/utils/suggestionHelpers');
                      const filteredSuggestions = filterDuplicateSuggestions(
                        incomingSuggestions,
                        Array.isArray(itineraryItems) ? itineraryItems : [],
                      );

                      if (filteredSuggestions.length < incomingSuggestions.length) {
                        const removedCount = incomingSuggestions.length - filteredSuggestions.length;
                        console.log(`[Wallet] Filtered out ${removedCount} duplicate suggestion(s)`);
                      }
                      
                      const nextResult: ChatPlannerResult = {
                        ...result,
                        debug: {
                          ...(result.debug || {}),
                          mode: requestMode,
                          plannerFocusCacheKey,
                        },
                        suggestions: filteredSuggestions,
                      };

                      setAiSuggestions(nextResult);
                      // Persist cache so users have a fallback if AI is flaky.
                      await setCachedChatPlannerResult(
                        selectedTrip.id,
                        requestMode,
                        nextResult,
                        plannerFocusCacheKey,
                      );
                    }
                  } catch (error: any) {
                    console.error('[Wallet] Failed to fetch AI suggestions:', error);
                    const errorCode = error?.code;
                    if (errorCode === 'functions/resource-exhausted') {
                      const raw = error?.details;
                      const featureFromDetails =
                        raw &&
                        typeof raw === 'object' &&
                        !Array.isArray(raw) &&
                        'feature' in raw
                          ? String((raw as { feature?: unknown }).feature)
                          : '';
                      if (
                        featureFromDetails === 'ai_plan_ideas' ||
                        /plan ai idea cards/i.test(String(error?.message || ''))
                      ) {
                        usePremiumGateStore.getState().open({
                          feature: 'ai_plan_ideas',
                          title: 'Plan AI idea limit',
                          message:
                            String(error?.message || '').trim() ||
                            'You’ve reached your monthly limit for Plan AI idea cards. Upgrade to Premium for unlimited ideas.',
                          source: 'feature_gate',
                        });
                        return;
                      }
                    }
                    const isTimeout =
                      errorCode === 'timeout' ||
                      /deadline|timed out|timeout/i.test(error?.message || '');

                    // Normalize functions/internal so the UI shows a friendly message instead of "internal"
                    const isInternal = errorCode === 'functions/internal';
                    if (isInternal) {
                      console.error('[Wallet] AI suggestions internal error (full error for debugging):', {
                        code: error?.code,
                        message: error?.message,
                        error,
                      });
                    }

                    const baseErrorMessage = isTimeout
                      ? 'We couldn’t generate ideas right now. Please try again in a moment.'
                      : isInternal
                        ? 'We couldn’t generate ideas right now. Please try again.'
                        : error?.message ||
                          'We couldn’t generate ideas right now. Please try again in a moment.';
                    
                    // Check for specific error codes that indicate configuration issues
                    const isConfigError = errorCode === 'functions/failed-precondition' || 
                                         /not configured|api key|failed-precondition/i.test(baseErrorMessage);
                    const showStagingHint = showStagingDebug && !isConfigError;
                    const errorMessage = showStagingHint
                      ? `${baseErrorMessage}\n\n(Staging hint: ${errorCode || 'unknown'})`
                      : baseErrorMessage;

                    // If another request is already in-flight (single-flight lock), don't scare the user.
                    // Treat it like a no-op; the in-flight request will resolve and update UI.
                    const isInFlight =
                      errorCode === 'functions/aborted' ||
                      /already generating|in[- ]flight|try again in a moment/i.test(errorMessage);
                    if (isInFlight) {
                      return;
                    }

                    const requestMode: 'itinerary' | 'packages' = mode || 'itinerary';
                    const errIntentAppendix =
                      requestMode === 'itinerary'
                        ? aiPlannerIntentAppendixRef.current?.trim() || null
                        : null;
                    const errPlannerFocusCacheKey = buildPlannerFocusCacheKey(errIntentAppendix);
                    // Cached fallback first (match current Plan focus for itinerary).
                    const cached = await getCachedChatPlannerResult(
                      selectedTrip.id,
                      requestMode,
                      errPlannerFocusCacheKey,
                    );
                    if (cached?.result) {
                      setAiSuggestions({
                        ...cached.result,
                        debug: {
                          ...(cached.result.debug || {}),
                          cached: true,
                          cachedAt: cached.savedAt,
                          mode: requestMode,
                          plannerFocusCacheKey: errPlannerFocusCacheKey,
                        },
                      });
                      setAiSuggestionsError(null);
                      setAiSuggestionsErrorMode(null);
                      showInfo('Showing cached suggestions.');
                    } else {
                      setAiSuggestionsError(isConfigError ? 'AI is not configured for this environment.' : baseErrorMessage);
                      setAiSuggestionsErrorMode(requestMode);
                      showError(isConfigError ? 'AI is not configured for this environment.' : baseErrorMessage);
                      setAiSuggestions(null);
                    }
                  } finally {
                    // Only clear loading if we're still the latest request.
                    if (aiSuggestionsRequestNonceRef.current === requestNonce) {
                      setAiSuggestionsLoading(false);
                      setAiSuggestionsLoadingExtended(false);
                      setAiSuggestionsRequestMode(null);
                      if (aiSuggestionsLongTimerRef.current) {
                        clearTimeout(aiSuggestionsLongTimerRef.current);
                        aiSuggestionsLongTimerRef.current = null;
                      }
                      if (aiSuggestionsHardTimerRef.current) {
                        clearTimeout(aiSuggestionsHardTimerRef.current);
                        aiSuggestionsHardTimerRef.current = null;
                      }
                    }
                  }
                }}
                onCancelGenerateSuggestions={cancelAiSuggestions}
                onApplyAllSuggestions={handleApplySuggestions}
                applyingSuggestions={applyingSuggestions}
                onAddSingleSuggestion={handleAddSingleSuggestion}
                addingSuggestionIds={addingSuggestionIds}
                EnhancedSuggestionCard={EnhancedSuggestionCard}
                mapToEnhancedSuggestion={mapToEnhancedSuggestion}
                profile={{
                  travelType: profile?.travelType,
                  citizenship: profile?.citizenship,
                  homeCountry: profile?.homeCountry,
                }}
                onSearchFlights={() => setShowFlightSearch(true)}
                onSearchHotels={() => setShowHotelSearch(true)}
              onSmartUpload={handleOpenSmartUpload}
              smartUploadLoading={smartUploadLoading}
              />
          ) : (
            <Card style={{ marginVertical: theme.spacing(2), padding: theme.spacing(3) }}>
              <ThemedText variant="bodyStrong">Trip dates unavailable</ThemedText>
              <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(1) }}>
                This trip is missing valid start or end dates. Try again after sync finishes, or edit the trip from the Trips tab.
              </Body>
            </Card>
          )}

          {selectedTrip && firebaseUser?.uid ? (
            <View
              style={{ marginTop: theme.spacing(2) }}
              onLayout={(e) => {
                commentsSectionYRef.current = e.nativeEvent.layout.y;
              }}
            >
              <TripErrorBoundary name="TripComments">
                <Suspense fallback={null}>
                  <TripCommentsLazy
                    trip={selectedTrip}
                    currentUserId={firebaseUser.uid}
                    expanded={commentsExpanded}
                    onExpandedChange={setCommentsExpanded}
                  />
                </Suspense>
              </TripErrorBoundary>
            </View>
          ) : null}
        </>
      ) : (
        <Card>
          <View style={{ alignItems: 'center', paddingVertical: theme.spacing(6), gap: theme.spacing(2) }}>
            <View style={{
              width: 80,
              height: 80,
              borderRadius: 40,
              backgroundColor: theme.colors.primary + '10',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: theme.spacing(1),
            }}>
              <Ionicons name="calendar-outline" size={40} color={theme.colors.primary} />
            </View>
            <ThemedText variant="bodyStrong" style={{ fontSize: 20, textAlign: 'center', fontWeight: '700' }}>
              No trip selected
            </ThemedText>
            {tripOptions.length > 0 ? (
              <>
                <Body
                  color={theme.colors.textSecondary}
                  style={{ textAlign: 'center', fontSize: 15, lineHeight: 22, maxWidth: 320 }}
                >
                  Choose a trip to view its itinerary and start planning.
                </Body>
                <View style={{ width: '100%', maxWidth: 420 }}>
                  <SelectField
                    label="Select trip"
                    value={selectedTripId || undefined}
                    options={tripOptions}
                    onValueChange={(value) => handleTripChange(value)}
                    placeholder="Choose a trip"
                  />
                </View>
              </>
            ) : (
              <Body color={theme.colors.textSecondary} style={{ textAlign: 'center', fontSize: 15, lineHeight: 22, maxWidth: 320 }}>
                Create a trip from the Trips tab to start planning.
              </Body>
            )}
          </View>
        </Card>
      )}

      {selectedTrip && firebaseUser?.uid && (
        <>
          <CollaborationModal
            visible={showCollaborationModal}
            onClose={() => setShowCollaborationModal(false)}
            trip={selectedTrip}
            currentUserId={firebaseUser.uid}
            onCollaboratorsUpdated={() => {
              // Trips will automatically update via subscription
            }}
            onLeftTrip={() => setSelectedTripId(null)}
          />

          <Suspense fallback={null}>
            <ImapSettingsModalLazy
              visible={imapModalVisible}
              onClose={() => {
                setImapModalVisible(false);
              }}
              onSave={handleImapSave}
            />
          </Suspense>

          <Suspense fallback={null}>
            <CopilotResultModalLazy
              visible={!!copilotResult || !!copilotPendingType}
              result={copilotResult}
              loading={copilotLoading}
              pendingType={copilotPendingType}
              onClose={handleCloseCopilotModal}
            />
          </Suspense>

          <TripErrorBoundary name="SmartUploadModal">
            <Suspense fallback={null}>
              <SmartUploadModalLazy
                visible={smartUploadVisible}
                onClose={() => setSmartUploadVisible(false)}
                initialTripId={selectedTripId}
                trips={trips}
                userId={firebaseUser?.uid}
                onTripCreated={(tripId) => {
                  showSuccess('Trip created from smart upload.');
                  setSelectedTripId(tripId);
                }}
                onTripSelected={(tripId) => {
                  setSelectedTripId(tripId);
                }}
                onItemsCreated={({ createdIds, failures }) => {
                  if (createdIds.length) {
                    const firstItem = itineraryItems.find((i) => i.id === createdIds[0]) ?? null;
                    const detailsBase = buildItineraryToastDetails(firstItem);
                    const details = detailsBase
                      ? `${detailsBase}\nYou can edit or undo anything at any time.`
                      : 'You can edit or undo anything at any time.';
                    showToastWithOptions({
                      message: `Added ${createdIds.length} item${createdIds.length === 1 ? '' : 's'} to itinerary.`,
                      type: 'success',
                      details,
                      actions: [
                        {
                          label: 'View',
                          onPress: () => {
                            setSmartUploadVisible(false);
                            if (createdIds[0]) router.setParams({ itemId: createdIds[0] });
                          },
                          accessibilityLabel: 'View created itinerary item',
                        },
                        {
                          label: 'Edit',
                          onPress: () => {
                            setSmartUploadVisible(false);
                            if (createdIds[0]) router.setParams({ itemId: createdIds[0] });
                          },
                          accessibilityLabel: 'Edit created itinerary item',
                        },
                      ],
                      onUndo: () => {
                        if (!selectedTripId) return;
                        void (async () => {
                          const results = await Promise.allSettled(
                            createdIds.map((id) => deleteItineraryItem(selectedTripId, id)),
                          );
                          const failed = results.filter((r) => r.status === 'rejected').length;
                          if (failed) {
                            showError(`Undo completed with ${failed} failure${failed === 1 ? '' : 's'}.`);
                          } else {
                            showSuccess('Undone.');
                          }
                        })();
                      },
                    });
                  }
                  if (failures.length) {
                    showError(`Unable to create ${failures.length} item${failures.length === 1 ? '' : 's'}.`);
                  }
                }}
                onProcessingChange={setSmartUploadLoading}
              />
            </Suspense>
          </TripErrorBoundary>

          {/* Visa requirements (in-app; powered by TravelBuddy API via Functions) */}
          <Modal
            visible={visaModalVisible}
            animationType="slide"
            presentationStyle="pageSheet"
            onRequestClose={() => setVisaModalVisible(false)}
          >
            <View
              style={{
                flex: 1,
                backgroundColor: theme.colors.background,
                paddingTop: effectiveTopInset(insets.top) + theme.spacing(2),
                paddingHorizontal: theme.spacing(2),
                paddingBottom: insets.bottom + theme.spacing(2),
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <ThemedText variant="h2" style={{ fontSize: 18, fontWeight: '800' }}>
                  Visa requirements
                </ThemedText>
                <Pressable
                  onPress={() => setVisaModalVisible(false)}
                  style={({ pressed }) => ({
                    padding: theme.spacing(1),
                    borderRadius: theme.radii.md,
                    opacity: pressed ? 0.6 : 1,
                  })}
                >
                  <Ionicons name="close" size={20} color={theme.colors.text} />
                </Pressable>
              </View>

              <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                For your trip to {selectedTrip.destination || selectedTrip.destinations?.[0]?.city || 'your destination'}
              </Caption>

              <ScrollView
                style={{ marginTop: theme.spacing(2) }}
                contentContainerStyle={{ paddingBottom: theme.spacing(3), gap: theme.spacing(2) }}
              >
                {visaModalLoading ? (
                  <Card variant="outlined" style={{ padding: theme.spacing(2), alignItems: 'center' }}>
                    <ActivityIndicator size="small" color={theme.colors.primary} />
                    <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(1) }}>
                      Checking visa requirements…
                    </Caption>
                  </Card>
                ) : visaRequirement ? (
                  <VisaChecklist requirement={visaRequirement} trip={selectedTrip} />
                ) : (
                  <Card variant="outlined" style={{ padding: theme.spacing(2) }}>
                    <Body color={theme.colors.textSecondary}>
                      {visaModalError || 'Visa requirements are temporarily unavailable.'}
                    </Body>
                    {needsDestinationCountryConfirm && (
                      <View style={{ marginTop: theme.spacing(1.5), gap: theme.spacing(1.25) }}>
                        <Body color={theme.colors.text}>
                          To check visa requirements, confirm the destination country for this trip.
                        </Body>
                        <SelectField
                          label="Destination country"
                          placeholder="Select country"
                          value={destinationCountryIso2}
                          options={countryOptions}
                          onValueChange={setDestinationCountryIso2}
                          helperText="This helps us determine visa requirements accurately."
                        />
                        <Button
                          label="Save country & check"
                          onPress={() => void persistDestinationCountryAndRetry()}
                        />
                      </View>
                    )}
                    <View style={{ marginTop: theme.spacing(1.5) }}>
                      <Button
                        label={needsDestinationCountryConfirm ? 'Close' : 'Try again'}
                        variant="secondary"
                        onPress={() => {
                          if (needsDestinationCountryConfirm) {
                            setVisaModalVisible(false);
                          } else {
                            void openVisaRequirements();
                          }
                        }}
                      />
                    </View>
                  </Card>
                )}
              </ScrollView>
            </View>
          </Modal>

          {/* Email Sync Status Modal */}
          {selectedTripId && (
            <TripErrorBoundary name="EmailSyncStatus">
              <Suspense fallback={null}>
                <EmailSyncStatusLazy
                  visible={showEmailSyncStatus}
                  onClose={() => setShowEmailSyncStatus(false)}
                  tripId={selectedTripId}
                  provider={inboxProvider}
                  syncSource="plan"
                  onSyncComplete={(bookings) => {
                    // Keep modal open so the success screen can offer "Sync another email".
                    const n = bookings.length;
                    if (n > 0) {
                      showSuccess(`Imported ${n} booking${n === 1 ? '' : 's'} from ${PROVIDER_LABELS[inboxProvider]}.`);
                    } else {
                      showInfo(`No new bookings found in ${PROVIDER_LABELS[inboxProvider]}.`);
                    }
                  }}
                  onSyncAnotherEmail={() => {
                    setShowEmailSyncStatus(false);
                    setShowProviderModal(true);
                  }}
                  onSyncNow={() => {
                    // Manual sync trigger - component will handle the sync
                    console.log('[PlanScreen] Manual sync triggered');
                  }}
                />
              </Suspense>
            </TripErrorBoundary>
          )}

          {/* Multi-Provider Selector Modal */}
          {selectedTripId && (
            <MultiProviderSelector
              visible={showProviderModal}
              onClose={() => setShowProviderModal(false)}
              tripId={selectedTripId}
              currentProvider={inboxProvider}
              onSelectProvider={handleSelectProvider}
              onConnectProvider={handleConnectProvider}
              onDisconnectProvider={handleDisconnectProvider}
              onSyncAll={handleSyncAll}
            />
          )}

          {/* Itinerary Item Form */}
          <ItineraryItemForm
            visible={showItineraryForm}
            onClose={() => {
              setShowItineraryForm(false);
              InteractionManager.runAfterInteractions(() => {
                setEditingItineraryItem(null);
                setSelectedDateForForm(null);
                setPendingQuickTemplate(null);
              });
            }}
            onSubmit={async (item) => {
              try {
                if (editingItineraryItem) {
                  let updates: Partial<ItineraryItem> = { ...item };
                  // When editing a flight with airline/flight number but no linked booking,
                  // create a booking so the Flight Status Card (Aerodata) can work
                  if (
                    item.category === 'flight' &&
                    (item.airlineCode || item.flightNumber) &&
                    !editingItineraryItem.bookingId &&
                    firebaseUser?.uid &&
                    selectedTrip.id
                  ) {
                    try {
                      const provider =
                        [item.airlineCode, item.flightNumber].filter(Boolean).join(' ') || item.title || 'Flight';
                      const bookingLocation =
                        (item.location?.trim() || item.address?.trim()) ||
                        selectedTrip.destination ||
                        undefined;
                      const bookingId = await createBooking(selectedTrip.id, firebaseUser.uid, {
                        type: 'flight',
                        provider,
                        airlineCode: item.airlineCode,
                        flightNumber: item.flightNumber,
                        departureDate: item.startDate,
                        arrivalDate: item.endDate ?? item.startDate,
                        location: bookingLocation,
                        rawEmailSubject: item.title,
                        status: 'confirmed',
                        source: 'manual',
                      });
                      updates = { ...updates, bookingId };
                    } catch (bookingError) {
                      console.error('[Plan] Failed to create booking for flight edit:', bookingError);
                    }
                  }
                  await updateItineraryItem(selectedTrip.id, editingItineraryItem.id, updates);

                  const linkedBookingId = updates.bookingId || editingItineraryItem.bookingId;
                  if (item.category === 'flight' && linkedBookingId) {
                    const normalizedAirlineCode = item.airlineCode?.trim().toUpperCase() || null;
                    const normalizedFlightNumber = item.flightNumber?.trim().toUpperCase() || null;
                    const provider =
                      [normalizedAirlineCode, normalizedFlightNumber].filter(Boolean).join(' ') ||
                      item.title ||
                      'Manual Flight';
                    const bookingLocation =
                      (item.location?.trim() || item.address?.trim()) ||
                      selectedTrip.destination ||
                      undefined;
                    const previousAirlineCode = editingItineraryItem.airlineCode?.trim().toUpperCase() || null;
                    const previousFlightNumber = editingItineraryItem.flightNumber?.trim().toUpperCase() || null;
                    const identifiersChanged =
                      previousAirlineCode !== normalizedAirlineCode ||
                      previousFlightNumber !== normalizedFlightNumber;

                    await updateBooking(selectedTrip.id, linkedBookingId, {
                      provider,
                      rawEmailSubject: item.title,
                      location: bookingLocation,
                      departureDate: item.startDate,
                      arrivalDate: item.endDate ?? item.startDate,
                      airlineCode: normalizedAirlineCode,
                      flightNumber: normalizedFlightNumber,
                      ...(identifiersChanged
                        ? {
                            // Identifier changes invalidate existing status snapshots.
                            flightStatus: null,
                            lastStatusCheck: null,
                            checkInLink: null,
                            gate: null,
                            terminal: null,
                            checkInAvailable: false,
                          }
                        : {}),
                    });
                  }
                } else {
                  const itemId = await handleAddItineraryItem(item);
                  const created = itineraryItems.find((i) => i.id === itemId) ?? null;
                  const details = buildItineraryToastDetails(created);
                  showToastWithOptions({
                    message: 'Added to itinerary.',
                    type: 'success',
                    details,
                    actions: [
                      {
                        label: 'View',
                        onPress: () => {
                          if (itemId) router.setParams({ itemId });
                        },
                        accessibilityLabel: 'View created itinerary item',
                      },
                      {
                        label: 'Edit',
                        onPress: () => {
                          if (itemId) router.setParams({ itemId });
                        },
                        accessibilityLabel: 'Edit created itinerary item',
                      },
                    ],
                    onUndo: () => {
                      if (!selectedTripId) return;
                      void (async () => {
                        try {
                          await deleteItineraryItem(selectedTripId, itemId);
                          showSuccess('Undone.');
                        } catch {
                          showError('Unable to undo. Please try again.');
                        }
                      })();
                    },
                  });
                }
                // Close form after successful submission
                setShowItineraryForm(false);
                InteractionManager.runAfterInteractions(() => {
                  setEditingItineraryItem(null);
                  setSelectedDateForForm(null);
                  setPendingQuickTemplate(null);
                });
              } catch (error) {
                console.error('Error saving itinerary item:', error);
                // Error is already shown in the form component
                throw error;
              }
            }}
            tripId={selectedTrip.id}
            userId={firebaseUser.uid}
            initialItem={editingItineraryItem || quickAddInitialItem || undefined}
            defaultDate={selectedDateForForm || selectedTrip.startDate}
            tripStartDate={selectedTrip.startDate}
            tripEndDate={selectedTrip.endDate}
            tripDestinations={selectedTrip.destinations}
          />

          {/* Centered "Added to itinerary" success modal (like Template trip creation) */}
          {addedItemSuccess && (
            <Modal
              visible={!!addedItemSuccess}
              transparent
              animationType="fade"
              onRequestClose={() => setAddedItemSuccess(null)}
            >
              <Pressable
                style={{
                  flex: 1,
                  backgroundColor: theme.colors.modalOverlay ?? 'rgba(0,0,0,0.5)',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: theme.spacing(3),
                }}
                onPress={() => setAddedItemSuccess(null)}
              >
                <Pressable
                  onPress={(e) => e.stopPropagation()}
                  style={({ pressed }) => [{ opacity: pressed ? 0.98 : 1 }]}
                >
                  <Card
                    variant="elevated"
                    style={{
                      width: '100%',
                      maxWidth: 320,
                      alignItems: 'center',
                      gap: theme.spacing(2),
                      padding: theme.spacing(4),
                      ...theme.shadows?.level3,
                    }}
                  >
                    <View
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: 36,
                        backgroundColor: theme.colors.success || theme.colors.primary,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Ionicons name="checkmark" size={44} color="#ffffff" />
                    </View>
                    <View style={{ alignItems: 'center', gap: theme.spacing(0.5) }}>
                      <SubHeading style={{ textAlign: 'center', fontWeight: '700', fontSize: 18 }}>
                        {addedItemSuccess.message}
                      </SubHeading>
                      {!!addedItemSuccess.details && (
                        <Caption style={{ textAlign: 'center', color: theme.colors.textSecondary }}>
                          {addedItemSuccess.details}
                        </Caption>
                      )}
                    </View>
                    <View style={{ flexDirection: 'row', gap: theme.spacing(2), marginTop: theme.spacing(1) }}>
                      <Button
                        label="View"
                        variant="secondary"
                        onPress={() => {
                          if (addedItemSuccessTimeoutRef.current) {
                            clearTimeout(addedItemSuccessTimeoutRef.current);
                            addedItemSuccessTimeoutRef.current = null;
                          }
                          openItemById(addedItemSuccess.itemId);
                          setAddedItemSuccess(null);
                        }}
                      />
                      <Button
                        label="Undo"
                        variant="ghost"
                        onPress={() => {
                          if (addedItemSuccessTimeoutRef.current) {
                            clearTimeout(addedItemSuccessTimeoutRef.current);
                            addedItemSuccessTimeoutRef.current = null;
                          }
                          if (selectedTripId) {
                            void deleteItineraryItem(selectedTripId, addedItemSuccess.itemId).then(
                              () => showSuccess('Undone.'),
                              () => showError('Unable to undo. Please try again.')
                            );
                          }
                          setAddedItemSuccess(null);
                        }}
                      />
                    </View>
                  </Card>
                </Pressable>
              </Pressable>
            </Modal>
          )}
          {/* Maybe → Schedule modal (pick date/time, then add directly) */}
          <Modal
            visible={scheduleMaybeVisible}
            animationType="slide"
            presentationStyle="formSheet"
            statusBarTranslucent
            onDismiss={() => {
              setScheduleMaybeVisible(false);
              setScheduleMaybeItem(null);
              setScheduleMaybeError(null);
              setScheduleMaybeDatePickerVisible(false);
              scheduleMaybeResolveRef.current?.(false);
              scheduleMaybeResolveRef.current = null;
            }}
            onRequestClose={() => {
              setScheduleMaybeVisible(false);
              setScheduleMaybeItem(null);
              setScheduleMaybeError(null);
              setScheduleMaybeDatePickerVisible(false);
              scheduleMaybeResolveRef.current?.(false);
              scheduleMaybeResolveRef.current = null;
            }}
          >
            <View
              style={{
                flex: 1,
                backgroundColor: theme.colors.background,
                paddingTop: insets.top + theme.spacing(2),
                paddingHorizontal: theme.spacing(2),
                paddingBottom: insets.bottom + theme.spacing(2),
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <ThemedText variant="h2" style={{ fontSize: 18, fontWeight: '800' }}>
                  Schedule idea
                </ThemedText>
                <Pressable
                  onPress={() => {
                    setScheduleMaybeVisible(false);
                    setScheduleMaybeItem(null);
                    setScheduleMaybeError(null);
                    scheduleMaybeResolveRef.current?.(false);
                    scheduleMaybeResolveRef.current = null;
                  }}
                  style={({ pressed }) => ({
                    padding: theme.spacing(1),
                    borderRadius: theme.radii.md,
                    opacity: pressed ? 0.6 : 1,
                  })}
                  accessibilityRole="button"
                  accessibilityLabel="Close schedule modal"
                >
                  <Ionicons name="close" size={20} color={theme.colors.text} />
                </Pressable>
              </View>

              <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingVertical: theme.spacing(2), gap: theme.spacing(1.25) }}>
                <Card variant="outlined" padding={theme.spacing(2)}>
                  <View style={{ gap: theme.spacing(1) }}>
                    <ThemedText variant="bodyStrong" style={{ fontSize: 15, fontWeight: '800' }}>
                      {scheduleMaybeItem?.title || 'Idea'}
                    </ThemedText>
                    {!!scheduleMaybeItem?.description?.trim() && (
                      <Caption color={theme.colors.textSecondary}>
                        {scheduleMaybeItem.description.trim()}
                      </Caption>
                    )}
                  </View>
                </Card>

                <Card variant="outlined" padding={theme.spacing(2)}>
                  <View style={{ gap: theme.spacing(1.25) }}>
                    <Button
                      label={scheduleMaybeDate ? scheduleMaybeDate.toLocaleDateString() : 'Pick date'}
                      variant="secondary"
                      onPress={() => {
                        if (isUiDebugLoggingEnabled()) {
                          console.log('[Plan] Schedule date picker requested');
                        }
                        setScheduleMaybeDatePickerVisible((prev) => !prev);
                      }}
                      icon={<Ionicons name="calendar-outline" size={18} color={theme.colors.primary} />}
                    />
                    {isScheduleSingleDayTrip && (
                      <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
                        This trip currently has one day set. Update trip dates to schedule on other days.
                      </Caption>
                    )}

                    {scheduleMaybeDatePickerVisible && (
                      <Calendar
                        inline
                        visible={scheduleMaybeDatePickerVisible}
                        onClose={() => {
                          setScheduleMaybeDatePickerVisible(false);
                          if (isUiDebugLoggingEnabled()) {
                            console.log('[Plan] Schedule date picker closed (inline)');
                          }
                        }}
                        onSelectDate={(date) => {
                          setScheduleMaybeDate(date);
                          setScheduleMaybeDatePickerVisible(false);
                          setScheduleMaybeError(null);
                          if (isUiDebugLoggingEnabled()) {
                            console.log('[Plan] Schedule date selected (inline)', { date: date?.toISOString?.() });
                          }
                        }}
                        selectedDate={scheduleMaybeDate || undefined}
                        minDate={scheduleTripStart ?? selectedTrip.startDate}
                        maxDate={scheduleTripEnd ?? selectedTrip.endDate}
                        title="Select date"
                      />
                    )}

                    <Pressable
                      onPress={() => {
                        setScheduleMaybeFlexible((v) => !v);
                        setScheduleMaybeError(null);
                      }}
                      style={({ pressed }) => ({
                        paddingVertical: theme.spacing(1),
                        paddingHorizontal: theme.spacing(1.25),
                        borderRadius: theme.radii.md,
                        backgroundColor: theme.colors.borderMuted + '25',
                        opacity: pressed ? 0.7 : 1,
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      })}
                      accessibilityRole="switch"
                      accessibilityLabel="Flexible time"
                      accessibilityState={{ checked: scheduleMaybeFlexible }}
                    >
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
                        <Ionicons name="time-outline" size={18} color={theme.colors.textSecondary} />
                        <ThemedText variant="body" style={{ fontSize: 13, fontWeight: '700' }}>
                          Flexible time
                        </ThemedText>
                      </View>
                      <Ionicons
                        name={scheduleMaybeFlexible ? 'checkbox' : 'square-outline'}
                        size={20}
                        color={scheduleMaybeFlexible ? theme.colors.primary : theme.colors.textSecondary}
                      />
                    </Pressable>

                    {!scheduleMaybeFlexible && (
                      <TimePickerInput
                        label="Start time"
                        value={scheduleMaybeTime}
                        onValueChange={(v) => {
                          setScheduleMaybeTime(v);
                          setScheduleMaybeEndTime('');
                          setScheduleMaybeError(null);
                        }}
                        helperText="Pick a start time for this activity."
                      />
                    )}

                    {scheduleMaybeSuggestedBlocks.length > 0 && (
                      <View style={{ gap: theme.spacing(0.75) }}>
                        <Caption color={theme.colors.textSecondary} style={{ fontSize: 11, fontWeight: '700' }}>
                          Free blocks
                        </Caption>
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(0.75) }}>
                          {scheduleMaybeSuggestedBlocks.map((b) => {
                            const selected =
                              !scheduleMaybeFlexible && scheduleMaybeTime === b.start && scheduleMaybeEndTime === b.end;
                            return (
                              <Button
                                key={b.label}
                                label={b.label}
                                size="small"
                                variant={selected ? 'secondary' : 'ghost'}
                                onPress={() => {
                                  setScheduleMaybeFlexible(false);
                                  setScheduleMaybeTime(b.start);
                                  setScheduleMaybeEndTime(b.end);
                                  setScheduleMaybeError(null);
                                }}
                                style={{ paddingHorizontal: theme.spacing(1), paddingVertical: theme.spacing(0.5) }}
                              />
                            );
                          })}
                        </View>
                      </View>
                    )}

                    {scheduleMaybeSuggestedTimes.length > 0 && (
                      <View style={{ gap: theme.spacing(0.75) }}>
                        <Caption color={theme.colors.textSecondary} style={{ fontSize: 11, fontWeight: '700' }}>
                          Free time suggestions
                        </Caption>
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(0.75) }}>
                          {scheduleMaybeSuggestedTimes.map((t) => {
                            const selected = !scheduleMaybeFlexible && scheduleMaybeTime === t;
                            return (
                              <Button
                                key={t}
                                label={t}
                                size="small"
                                variant={selected ? 'secondary' : 'ghost'}
                                onPress={() => {
                                  setScheduleMaybeFlexible(false);
                                  setScheduleMaybeTime(t);
                                  setScheduleMaybeEndTime('');
                                  setScheduleMaybeError(null);
                                }}
                                style={{ paddingHorizontal: theme.spacing(1), paddingVertical: theme.spacing(0.5) }}
                              />
                            );
                          })}
                        </View>
                        <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                          Times are based on gaps between scheduled items for that day.
                        </Caption>
                      </View>
                    )}

                    {!scheduleMaybeFlexible && (
                      <TimePickerInput
                        label="End time (optional)"
                        value={scheduleMaybeEndTime}
                        onValueChange={(v) => {
                          setScheduleMaybeEndTime(v);
                          setScheduleMaybeError(null);
                        }}
                        helperText="Optional: set an end time to match a free block."
                      />
                    )}

                    {!!scheduleMaybeError && (
                      <Caption style={{ color: theme.colors.error, fontWeight: '700' }}>
                        {scheduleMaybeError}
                      </Caption>
                    )}

                    <View style={{ flexDirection: 'row', gap: theme.spacing(1) }}>
                      <Button
                        label="Cancel"
                        variant="ghost"
                        onPress={() => {
                          setScheduleMaybeVisible(false);
                          setScheduleMaybeItem(null);
                          setScheduleMaybeError(null);
                          scheduleMaybeResolveRef.current?.(false);
                          scheduleMaybeResolveRef.current = null;
                        }}
                        style={{ flex: 1 }}
                      />
                      <Button
                        label={scheduleMaybeAdding ? 'Adding…' : 'Add to itinerary'}
                        disabled={scheduleMaybeAdding}
                        onPress={() => {
                          void (async () => {
                            setScheduleMaybeError(null);
                            try {
                              if (!scheduleMaybeItem || !selectedTrip || !firebaseUser?.uid) {
                                setScheduleMaybeError('Missing trip or user context.');
                                return;
                              }
                              if (!scheduleMaybeDate) {
                                setScheduleMaybeError('Please pick a date.');
                                return;
                              }
                              if (!scheduleMaybeFlexible && !scheduleMaybeTime) {
                                setScheduleMaybeError('Please pick a start time or enable flexible time.');
                                return;
                              }
                              if (!scheduleMaybeFlexible && scheduleMaybeEndTime) {
                                const parse = (t?: string | null): number | null => {
                                  if (!t || t === 'flexible') return null;
                                  const m = /^(\d{1,2}):(\d{2})$/.exec(t.trim());
                                  if (!m) return null;
                                  const hh = Number(m[1]);
                                  const mm = Number(m[2]);
                                  if (!Number.isFinite(hh) || !Number.isFinite(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
                                  return hh * 60 + mm;
                                };
                                const startMins = parse(scheduleMaybeTime);
                                const endMins = parse(scheduleMaybeEndTime);
                                if (startMins == null || endMins == null) {
                                  setScheduleMaybeError('Please pick a valid end time.');
                                  return;
                                }
                                if (endMins <= startMins) {
                                  setScheduleMaybeError('End time must be after start time.');
                                  return;
                                }
                              }

                              const coercedDate = coerceDate(scheduleMaybeDate);
                              if (!coercedDate) {
                                setScheduleMaybeError('Please pick a valid date.');
                                return;
                              }
                              const startDate = new Date(coercedDate);
                              startDate.setHours(0, 0, 0, 0);

                              setScheduleMaybeAdding(true);
                              const ADD_TIMEOUT_MS = 20_000;
                              try {
                                if (isUiDebugLoggingEnabled()) {
                                  console.log('[Plan] Schedule add to itinerary started', {
                                    tripId: selectedTrip.id,
                                    itemTitle: scheduleMaybeItem.title,
                                    date: startDate.toISOString(),
                                    flexible: scheduleMaybeFlexible,
                                  });
                                }
                              const fallbackAddress =
                                selectedTrip.destination ||
                                selectedTrip.destinations?.[0]?.city ||
                                undefined;
                              const itemId = await Promise.race([
                                handleAddItineraryItem({
                                  title: scheduleMaybeItem.title,
                                  description: scheduleMaybeItem.description,
                                  category: 'activity',
                                  startDate,
                                  startTime: scheduleMaybeFlexible ? 'flexible' : scheduleMaybeTime,
                                  endTime: scheduleMaybeFlexible ? undefined : scheduleMaybeEndTime || undefined,
                                  address: fallbackAddress,
                                  location: fallbackAddress,
                                  source: 'manual',
                                  status: 'planned',
                                  createdBy: firebaseUser.uid,
                                  documents: [],
                                }),
                                  new Promise<string>((_, reject) =>
                                    setTimeout(
                                      () => reject(new Error('Adding took too long. Please try again.')),
                                      ADD_TIMEOUT_MS,
                                    ),
                                  ),
                                ]);

                                setScheduleMaybeVisible(false);
                                setScheduleMaybeItem(null);
                                setScheduleMaybeError(null);
                                scheduleMaybeResolveRef.current?.(true);
                                scheduleMaybeResolveRef.current = null;
                                showAddedToItineraryToast({
                                  itemId,
                                  title: scheduleMaybeItem.title,
                                  startDate,
                                  startTime: scheduleMaybeFlexible ? 'flexible' : scheduleMaybeTime,
                                });
                                if (isUiDebugLoggingEnabled()) {
                                  console.log('[Plan] Schedule add to itinerary success');
                                }
                              } catch (e: any) {
                                if (isUiDebugLoggingEnabled()) {
                                  console.log('[Plan] Schedule add to itinerary failed', { error: e?.message || e });
                                }
                                setScheduleMaybeError(e?.message || 'Unable to add to itinerary. Please try again.');
                              } finally {
                                setScheduleMaybeAdding(false);
                              }
                            } catch (e: any) {
                              setScheduleMaybeError(e?.message || 'Unable to add to itinerary. Please try again.');
                            } finally {
                              setScheduleMaybeAdding(false);
                            }
                          })();
                        }}
                        style={{ flex: 1 }}
                      />
                    </View>
                  </View>
                </Card>
              </ScrollView>
            </View>
          </Modal>

          {/* Document Viewer */}
          {viewingDocument && (
            <TripErrorBoundary name="PlanDocumentViewer">
              <Suspense
                fallback={null}
              >
                <DocumentViewer
                  visible={!!viewingDocument}
                  onClose={() => setViewingDocument(null)}
                  documentUrl={viewingDocument.url}
                  offlinePath={viewingDocument.offlinePath}
                  fileName={viewingDocument.fileName}
                  contentType={viewingDocument.contentType}
                  canDelete={!isSelectedTripReadOnly && firebaseUser.uid === selectedTrip.owner}
                  onDelete={() => {
                    // Document deletion handled by viewer
                    setViewingDocument(null);
                  }}
                />
              </Suspense>
            </TripErrorBoundary>
          )}

          {/* Flight Search Modal */}
          {selectedTrip && firebaseUser?.uid && (
            <Suspense fallback={null}>
              <FlightSearchModalLazy
                visible={showFlightSearch}
                onClose={() => setShowFlightSearch(false)}
                tripId={selectedTrip.id}
                trip={selectedTrip}
                profile={profile}
                onImportFlight={async (booking) => {
                  try {
                    await createBooking(selectedTrip.id, firebaseUser.uid, booking);
                  } catch (error: any) {
                    console.error('Error importing flight:', error);
                    throw error;
                  }
                }}
                onBookingComplete={() => {
                  // Bookings will automatically update via subscription
                }}
              />
            </Suspense>
          )}

          {/* Hotel Search Modal */}
          {selectedTrip && firebaseUser?.uid && (
            <Suspense fallback={null}>
              <HotelSearchModalLazy
                visible={showHotelSearch}
                onClose={() => setShowHotelSearch(false)}
                tripId={selectedTrip.id}
                trip={selectedTrip}
                profile={profile}
                onBookingComplete={() => {
                  // Bookings will automatically update via subscription
                }}
              />
            </Suspense>
          )}

          {/* Export Menu */}
          {selectedTrip && (
            <ExportMenu
              visible={showExportMenu}
              onClose={() => setShowExportMenu(false)}
              trip={selectedTrip}
              expenses={expenses}
              bookings={bookings}
              itineraryItems={itineraryItems}
              budgetSnapshot={budgetSnapshot}
            />
          )}

          {/* Travel Companion (during trip only) */}
          {selectedTripId && isTripActiveNow && (
            <>
              {/* Companion menu */}
              <Modal
                visible={companionMenuVisible}
                transparent
                animationType="slide"
                onRequestClose={() => setCompanionMenuVisible(false)}
              >
                <View style={{ flex: 1, backgroundColor: theme.colors.overlay, justifyContent: 'flex-end' }}>
                  <Pressable style={{ flex: 1 }} onPress={() => setCompanionMenuVisible(false)} />
                  <Card
                    variant="elevated"
                    style={{
                      borderTopLeftRadius: theme.radii.xl,
                      borderTopRightRadius: theme.radii.xl,
                      maxHeight: '80%',
                    }}
                  >
                    <View style={{ padding: theme.spacing(2), gap: theme.spacing(1.25) }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                        <SubHeading style={{ fontSize: 18 }}>Companion</SubHeading>
                        <Pressable onPress={() => setCompanionMenuVisible(false)}>
                          <Ionicons name="close" size={22} color={theme.colors.text} />
                        </Pressable>
                      </View>

                      <Button
                        label="Travel companion"
                        variant="primary"
                        onPress={() => {
                          setCompanionMenuVisible(false);
                          setTravelCompanionVisible(true);
                        }}
                        icon={<Ionicons name="chatbubbles-outline" size={18} color={theme.colors.onPrimary} />}
                      />

                      <Button
                        label="What’s next"
                        variant="secondary"
                        onPress={() => {
                          setCompanionMenuVisible(false);
                          setWhatsNextVisible(true);
                        }}
                        icon={<Ionicons name="time-outline" size={18} color={theme.colors.primary} />}
                      />

                      <Button
                        label="Trip tools"
                        variant="secondary"
                        onPress={() => {
                          setCompanionMenuVisible(false);
                          setTripToolsVisible(true);
                        }}
                        icon={<Ionicons name="grid-outline" size={18} color={theme.colors.primary} />}
                      />
                    </View>
                  </Card>
                </View>
              </Modal>

              {/* What's next modal */}
              <Modal
                visible={whatsNextVisible}
                transparent
                animationType="slide"
                onRequestClose={() => setWhatsNextVisible(false)}
              >
                <View style={{ flex: 1, backgroundColor: theme.colors.overlay, justifyContent: 'flex-end' }}>
                  <Pressable style={{ flex: 1 }} onPress={() => setWhatsNextVisible(false)} />
                  <Card
                    variant="elevated"
                    style={{
                      borderTopLeftRadius: theme.radii.xl,
                      borderTopRightRadius: theme.radii.xl,
                      maxHeight: '80%',
                    }}
                  >
                    <View style={{ padding: theme.spacing(2), gap: theme.spacing(1.25) }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                        <SubHeading style={{ fontSize: 18 }}>What’s next</SubHeading>
                        <Pressable onPress={() => setWhatsNextVisible(false)}>
                          <Ionicons name="close" size={22} color={theme.colors.text} />
                        </Pressable>
                      </View>

                      {nextItineraryItem ? (
                        <Card variant="outlined" style={{ padding: theme.spacing(1.5) }}>
                          <View style={{ gap: theme.spacing(0.75) }}>
                            <ThemedText variant="bodyStrong" style={{ fontSize: 16 }}>
                              {nextItineraryItem.title}
                            </ThemedText>
                            <Caption color={theme.colors.textSecondary}>
                              {(nextItineraryItem.startTime && nextItineraryItem.startTime !== 'flexible')
                                ? `Starts at ${nextItineraryItem.startTime}`
                                : 'Upcoming'}
                              {nextItineraryItem.location ? ` • ${nextItineraryItem.location}` : ''}
                            </Caption>
                          </View>
                        </Card>
                      ) : (
                        <Body color={theme.colors.textSecondary}>No upcoming itinerary items found.</Body>
                      )}

                      <Button
                        label="Open maps"
                        variant="secondary"
                        onPress={() => {
                          setWhatsNextVisible(false);
                          router.push({ pathname: '/(tabs)/travel', params: { tab: 'maps', tripId: selectedTripId } });
                        }}
                        icon={<Ionicons name="map-outline" size={18} color={theme.colors.primary} />}
                      />

                      <Button label="Close" variant="secondary" onPress={() => setWhatsNextVisible(false)} />
                    </View>
                  </Card>
                </View>
              </Modal>

              {/* Trip tools modal */}
              <Modal
                visible={tripToolsVisible}
                transparent
                animationType="slide"
                onRequestClose={() => setTripToolsVisible(false)}
              >
                <View style={{ flex: 1, backgroundColor: theme.colors.overlay, justifyContent: 'flex-end' }}>
                  <Pressable style={{ flex: 1 }} onPress={() => setTripToolsVisible(false)} />
                  <Card
                    variant="elevated"
                    style={{
                      borderTopLeftRadius: theme.radii.xl,
                      borderTopRightRadius: theme.radii.xl,
                      maxHeight: '80%',
                    }}
                  >
                    <View style={{ padding: theme.spacing(2), gap: theme.spacing(1.25) }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                        <SubHeading style={{ fontSize: 18 }}>Trip tools</SubHeading>
                        <Pressable onPress={() => setTripToolsVisible(false)}>
                          <Ionicons name="close" size={22} color={theme.colors.text} />
                        </Pressable>
                      </View>

                      <Button
                        label="Quick add"
                        variant="primary"
                        onPress={() => {
                          setTripToolsVisible(false);
                          setEditingItineraryItem(null);
                          setSelectedDateForForm(safeTripStartDate ?? new Date());
                          setShowItineraryForm(true);
                        }}
                        icon={<Ionicons name="add-outline" size={18} color={theme.colors.onPrimary} />}
                        disabled={isSelectedTripReadOnly}
                      />

                      <Button
                        label="Offline docs"
                        variant="secondary"
                        onPress={() => {
                          setTripToolsVisible(false);
                          router.push(getDocumentsTabTarget('merged', { tripId: selectedTripId }));
                        }}
                        icon={<Ionicons name="document-text-outline" size={18} color={theme.colors.primary} />}
                      />

                      <Button
                        label="Safety"
                        variant="secondary"
                        onPress={() => {
                          setTripToolsVisible(false);
                          router.push({ pathname: '/(tabs)/travel', params: { tab: 'safety', tripId: selectedTripId } });
                        }}
                        icon={<Ionicons name="shield-checkmark-outline" size={18} color={theme.colors.primary} />}
                      />

                      <Button
                        label="Share my coordinates"
                        variant="secondary"
                        onPress={async () => {
                          setTripToolsVisible(false);
                          await handleShareMyLocation();
                        }}
                        icon={<Ionicons name="location-outline" size={18} color={theme.colors.primary} />}
                      />

                      <Button
                        label="Import"
                        variant="secondary"
                        onPress={() => {
                          setTripToolsVisible(false);
                          handleOpenSmartUpload();
                        }}
                        icon={<Ionicons name="cloud-upload-outline" size={18} color={theme.colors.primary} />}
                      />

                      <Button label="Close" variant="secondary" onPress={() => setTripToolsVisible(false)} />
                    </View>
                  </Card>
                </View>
              </Modal>

              {/* Travel Companion full-screen */}
              <TripErrorBoundary name="TravelCompanion">
                <Suspense fallback={null}>
                  <TravelCompanionLazy
                    tripId={selectedTripId}
                    visible={travelCompanionVisible}
                    onClose={() => setTravelCompanionVisible(false)}
                  />
                </Suspense>
              </TripErrorBoundary>
            </>
          )}

        </>
      )}

      {selectedTrip && (
        <NotificationModal
          visible={showTripAlertsModal}
          onClose={() => setShowTripAlertsModal(false)}
          notifications={tripNotifications}
          onAction={handleTripNotificationAction}
          onDismiss={handleTripNotificationDismiss}
          onDismissAll={handleClearAllNotifications}
          title="Trip Alerts"
        />
      )}

    </Screen>
  );
}

export default function PlanScreen() {
  return (
    <TripErrorBoundary name="PlanScreen">
      <PlanScreenInner />
    </TripErrorBoundary>
  );
}
